/* prettier-ignore-file */
import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  TextInput,
  Alert,
  Modal,
  Image,
  Switch,
} from "react-native";
import { useNavigate } from "react-router-dom";
import AuthHeader from "../components/AuthHeader";
import EmailTemplateEditor from "../components/EmailTemplateEditor";
import WaiverTemplateEditor from "../components/WaiverTemplateEditor";
import {
  createDocument,
  updateDocument,
  deleteDocument,
  uploadFile,
} from "../db_calls";
import { db, auth } from "../firebase.config";
import { getCollectionPath, getSettingsPath } from "../utils/dbPaths";
import { getTenantId, getStoreId } from "../services/userStoreCache";
import { useSettings, useProducts } from "../stores";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase.config";
import { formatPhoneNumberProgressive as formatPhoneNumber, centsToDollars, dollarsToCents } from "../utils/formatting";
import {
  REVIEW_REQUEST_DEFAULT_SMS,
  REVIEW_REQUEST_DEFAULT_EMAIL_SUBJECT,
  REVIEW_REQUEST_DEFAULT_EMAIL_BODY,
  REVIEW_REQUEST_OPTOUT_SUFFIX,
} from "../utils/reviewRequest";

const functions = getFunctions(app);

const Settings = () => {
  const navigate = useNavigate();
  const settings = useSettings((state) => state.settings);
  const products1FromStore = useProducts((state) => state.products1);
  const products2FromStore = useProducts((state) => state.products2);
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [bicycles, setBicycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    quantity: "",
    imageUrl: "",
    description: "",
    includedItems: "",
    hasRetailPrice: false,
    retailPriceCents: "",
    physicalItem: true,
  });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const bikeImageInputRef = useRef(null);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);

  // Add-Ons state
  const [addOns, setAddOns] = useState([]);
  const [loadingAddOns, setLoadingAddOns] = useState(true);
  const [editingAddOnId, setEditingAddOnId] = useState(null);
  const [addOnFormData, setAddOnFormData] = useState({
    name: "",
    price: "",
    option: "per-booking",
    inventory: "",
    description: "",
    imageUrl: "",
    hasRetailPrice: false,
    retailPriceCents: "",
    physicalItem: true,
  });
  const [addOnSaving, setAddOnSaving] = useState(false);
  const [deletingAddOnId, setDeletingAddOnId] = useState(null);
  const [uploadingAddOnImage, setUploadingAddOnImage] = useState(false);
  const addOnImageInputRef = useRef(null);

  // Sales Tax state
  const [salesTaxPercent, setSalesTaxPercent] = useState("");
  const [salesTaxLoading, setSalesTaxLoading] = useState(true);
  const [salesTaxSaving, setSalesTaxSaving] = useState(false);
  const [originalSalesTaxPercent, setOriginalSalesTaxPercent] = useState("");

  // Cadence POS connection state (settings/cadenceConnection) — pairing that
  // lets this store's rentals appear on its Cadence/Warpspeed workorder board.
  const [cadenceConn, setCadenceConn] = useState({
    enabled: false,
    tenantID: "",
    storeID: "",
    endpointUrl: "",
    pairingToken: "",
  });
  const [originalCadenceConn, setOriginalCadenceConn] = useState("");
  const [cadenceSaving, setCadenceSaving] = useState(false);
  // Static field labels for the Cadence section — placeholders alone
  // disappear the moment a field has a value.
  const cadenceFieldLabelStyle = {
    fontSize: 13,
    fontWeight: "600",
    color: "#5B6B68",
    marginTop: 10,
    marginBottom: 4,
  };

  // Starting Address state
  const [startingAddress, setStartingAddress] = useState("");
  const [startingAddressInput, setStartingAddressInput] = useState("");
  const [startingAddressSuggestions, setStartingAddressSuggestions] = useState(
    []
  );
  const [showStartingAddressSuggestions, setShowStartingAddressSuggestions] =
    useState(false);
  const [startingAddressLoading, setStartingAddressLoading] = useState(true);
  const [startingAddressSaving, setStartingAddressSaving] = useState(false);
  const [originalStartingAddress, setOriginalStartingAddress] = useState("");
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const hasInteractedWithAddressRef = useRef(false);

  // Mileage Rate and Loading Fee state
  const [mileageRate, setMileageRate] = useState("");
  const [mileageRateLoading, setMileageRateLoading] = useState(true);
  const [mileageRateSaving, setMileageRateSaving] = useState(false);
  const [originalMileageRate, setOriginalMileageRate] = useState("");
  const [loadingFee, setLoadingFee] = useState("");
  const [loadingFeeLoading, setLoadingFeeLoading] = useState(true);
  const [loadingFeeSaving, setLoadingFeeSaving] = useState(false);
  const [originalLoadingFee, setOriginalLoadingFee] = useState("");

  // Delivery and Pickup Info state
  const [deliveryInfo, setDeliveryInfo] = useState("");
  const [deliveryInfoLoading, setDeliveryInfoLoading] = useState(true);
  const [deliveryInfoSaving, setDeliveryInfoSaving] = useState(false);
  const [originalDeliveryInfo, setOriginalDeliveryInfo] = useState("");
  const [pickupInfo, setPickupInfo] = useState("");
  const [pickupInfoLoading, setPickupInfoLoading] = useState(true);
  const [pickupInfoSaving, setPickupInfoSaving] = useState(false);
  const [originalPickupInfo, setOriginalPickupInfo] = useState("");

  // Store contact info state
  const [storeName, setStoreName] = useState("");
  const [storeNameLoading, setStoreNameLoading] = useState(true);
  const [storeNameSaving, setStoreNameSaving] = useState(false);
  const [originalStoreName, setOriginalStoreName] = useState("");
  const [legalStoreName, setLegalStoreName] = useState("");
  const [legalStoreNameLoading, setLegalStoreNameLoading] = useState(true);
  const [legalStoreNameSaving, setLegalStoreNameSaving] = useState(false);
  const [originalLegalStoreName, setOriginalLegalStoreName] = useState("");
  const [legalStoreAddress, setLegalStoreAddress] = useState("");
  const [legalStoreAddressLoading, setLegalStoreAddressLoading] = useState(true);
  const [legalStoreAddressSaving, setLegalStoreAddressSaving] = useState(false);
  const [cancellationPolicy, setCancellationPolicy] = useState("");
  const [cancellationPolicyLoading, setCancellationPolicyLoading] = useState(true);
  const [cancellationPolicySaving, setCancellationPolicySaving] = useState(false);
  const [originalLegalStoreAddress, setOriginalLegalStoreAddress] = useState("");
  const [appHeaderTitle, setAppHeaderTitle] = useState("");
  const [appHeaderTitleLoading, setAppHeaderTitleLoading] = useState(true);
  const [appHeaderTitleSaving, setAppHeaderTitleSaving] = useState(false);
  const [originalAppHeaderTitle, setOriginalAppHeaderTitle] = useState("");
  const [storeLogo, setStoreLogo] = useState("");
  const [storeLogoLoading, setStoreLogoLoading] = useState(true);
  const [uploadingStoreLogo, setUploadingStoreLogo] = useState(false);
  const storeLogoInputRef = useRef(null);
  const [storePhone, setStorePhone] = useState("");
  const [storePhoneLoading, setStorePhoneLoading] = useState(true);
  const [storePhoneSaving, setStorePhoneSaving] = useState(false);
  const [originalStorePhone, setOriginalStorePhone] = useState("");
  const [storeEmail, setStoreEmail] = useState("");
  const [storeEmailLoading, setStoreEmailLoading] = useState(true);
  const [storeEmailSaving, setStoreEmailSaving] = useState(false);
  const [originalStoreEmail, setOriginalStoreEmail] = useState("");
  const [storeWebsite, setStoreWebsite] = useState("");
  const [storeWebsiteLoading, setStoreWebsiteLoading] = useState(true);
  const [storeWebsiteSaving, setStoreWebsiteSaving] = useState(false);
  const [originalStoreWebsite, setOriginalStoreWebsite] = useState("");
  const [cardProcessingFee, setCardProcessingFee] = useState("3");
  const [cardProcessingFeeLoading, setCardProcessingFeeLoading] = useState(true);
  const [cardProcessingFeeSaving, setCardProcessingFeeSaving] = useState(false);
  const [originalCardProcessingFee, setOriginalCardProcessingFee] = useState("3");
  const [cardProcessingFeeForPurchases, setCardProcessingFeeForPurchases] = useState(false);
  const [cardProcessingFeeForRefunds, setCardProcessingFeeForRefunds] = useState(false);
  const [originalCardProcessingFeeForPurchases, setOriginalCardProcessingFeeForPurchases] = useState(false);
  const [originalCardProcessingFeeForRefunds, setOriginalCardProcessingFeeForRefunds] = useState(false);
  
  // Cancellation policy windows and penalties
  const [cancellationWindows, setCancellationWindows] = useState([
    { hours: 48, percentPenalty: 0, daysPenalty: 0 },   // 48+ hours: 0% penalty (100% refund)
    { hours: 24, percentPenalty: 25, daysPenalty: 1 },  // 24-48 hours: 25% penalty (75% refund)
    { hours: 0, percentPenalty: 50, daysPenalty: 2 },   // 0-24 hours: 50% penalty (50% refund)
  ]);
  const [penaltyType, setPenaltyType] = useState("percentage"); // "percentage" or "days"
  const [cancellationWindowsLoading, setCancellationWindowsLoading] = useState(true);
  const [cancellationWindowsSaving, setCancellationWindowsSaving] = useState(false);
  const [deliveryPhone, setDeliveryPhone] = useState("");
  const [deliveryPhoneLoading, setDeliveryPhoneLoading] = useState(true);
  const [deliveryPhoneSaving, setDeliveryPhoneSaving] = useState(false);
  const [originalDeliveryPhone, setOriginalDeliveryPhone] = useState("");
  const [deliveryCallingPhone, setDeliveryCallingPhone] = useState("");
  const [deliveryCallingPhoneLoading, setDeliveryCallingPhoneLoading] = useState(true);
  const [deliveryCallingPhoneSaving, setDeliveryCallingPhoneSaving] = useState(false);
  const [originalDeliveryCallingPhone, setOriginalDeliveryCallingPhone] = useState("");
  const [customerBlurb, setCustomerBlurb] = useState("");
  const [customerBlurbLoading, setCustomerBlurbLoading] = useState(true);
  const [customerBlurbSaving, setCustomerBlurbSaving] = useState(false);
  const [originalCustomerBlurb, setOriginalCustomerBlurb] = useState("");

  // Hours of Operation state
  const [hoursOfOperation, setHoursOfOperation] = useState({
    monday: { open: true, openTime: "09:00", closeTime: "17:00" },
    tuesday: { open: true, openTime: "09:00", closeTime: "17:00" },
    wednesday: { open: true, openTime: "09:00", closeTime: "17:00" },
    thursday: { open: true, openTime: "09:00", closeTime: "17:00" },
    friday: { open: true, openTime: "09:00", closeTime: "17:00" },
    saturday: { open: true, openTime: "09:00", closeTime: "17:00" },
    sunday: { open: true, openTime: "09:00", closeTime: "17:00" },
  });
  const [hoursLoading, setHoursLoading] = useState(true);
  const [hoursSaving, setHoursSaving] = useState(false);
  const [originalHoursOfOperation, setOriginalHoursOfOperation] = useState({});

  // In-Store Pickup Hours state
  const [pickupHoursOfOperation, setPickupHoursOfOperation] = useState({
    monday: { open: true, openTime: "09:00", closeTime: "17:00" },
    tuesday: { open: true, openTime: "09:00", closeTime: "17:00" },
    wednesday: { open: true, openTime: "09:00", closeTime: "17:00" },
    thursday: { open: true, openTime: "09:00", closeTime: "17:00" },
    friday: { open: true, openTime: "09:00", closeTime: "17:00" },
    saturday: { open: true, openTime: "09:00", closeTime: "17:00" },
    sunday: { open: true, openTime: "09:00", closeTime: "17:00" },
  });
  const [pickupHoursLoading, setPickupHoursLoading] = useState(true);
  const [pickupHoursSaving, setPickupHoursSaving] = useState(false);
  const [originalPickupHoursOfOperation, setOriginalPickupHoursOfOperation] = useState({});

  // Product Titles state
  const [product1Title, setProduct1Title] = useState("Bicycles");
  const [product1TitleLoading, setProduct1TitleLoading] = useState(true);
  const [product1TitleSaving, setProduct1TitleSaving] = useState(false);
  const [originalProduct1Title, setOriginalProduct1Title] = useState("Bicycles");
  const [product2Title, setProduct2Title] = useState("Add-Ons");
  const [product2TitleLoading, setProduct2TitleLoading] = useState(true);
  const [product2TitleSaving, setProduct2TitleSaving] = useState(false);
  const [originalProduct2Title, setOriginalProduct2Title] = useState("Add-Ons");

  // Confirmation Heading state
  const [confirmationHeadingTransportation, setConfirmationHeadingTransportation] = useState("");
  const [confirmationHeadingTransportationLoading, setConfirmationHeadingTransportationLoading] = useState(true);
  const [confirmationHeadingTransportationSaving, setConfirmationHeadingTransportationSaving] = useState(false);
  const [originalConfirmationHeadingTransportation, setOriginalConfirmationHeadingTransportation] = useState("");
  const [confirmationHeadingInStorePickup, setConfirmationHeadingInStorePickup] = useState("");
  const [confirmationHeadingInStorePickupLoading, setConfirmationHeadingInStorePickupLoading] = useState(true);
  const [confirmationHeadingInStorePickupSaving, setConfirmationHeadingInStorePickupSaving] = useState(false);
  const [originalConfirmationHeadingInStorePickup, setOriginalConfirmationHeadingInStorePickup] = useState("");

  // Discounts state
  const [discounts, setDiscounts] = useState([]);
  const [loadingDiscounts, setLoadingDiscounts] = useState(true);
  const [editingDiscountId, setEditingDiscountId] = useState(null);
  const [discountFormData, setDiscountFormData] = useState({
    from: "",
    to: "",
    value: "",
  });
  const [discountSaving, setDiscountSaving] = useState(false);
  const [deletingDiscountId, setDeletingDiscountId] = useState(null);
  const [useDeliveryDiscounts, setUseDeliveryDiscounts] = useState(true);
  const [useDeliveryDiscountsLoading, setUseDeliveryDiscountsLoading] = useState(true);
  const [useDeliveryDiscountsSaving, setUseDeliveryDiscountsSaving] = useState(false);
  
  // Duration discounts state
  const [durationDiscounts, setDurationDiscounts] = useState([]);
  const [loadingDurationDiscounts, setLoadingDurationDiscounts] = useState(true);
  const [editingDurationDiscountId, setEditingDurationDiscountId] = useState(null);
  const [durationDiscountFormData, setDurationDiscountFormData] = useState({
    fromDays: "",
    toDays: "",
    value: "",
  });
  const [durationDiscountSaving, setDurationDiscountSaving] = useState(false);
  const [deletingDurationDiscountId, setDeletingDurationDiscountId] = useState(null);
  const [useDurationDiscounts, setUseDurationDiscounts] = useState(false);
  const [useDurationDiscountsLoading, setUseDurationDiscountsLoading] = useState(true);
  const [useDurationDiscountsSaving, setUseDurationDiscountsSaving] = useState(false);

  // Show/Hide state for major components
  const [showBicycles, setShowBicycles] = useState(false);
  const [showAddOns, setShowAddOns] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDiscounts, setShowDiscounts] = useState(false);
  const [showDurationDiscounts, setShowDurationDiscounts] = useState(false);
  const [showEmailTemplates, setShowEmailTemplates] = useState(false);
  const [showSmsTemplates, setShowSmsTemplates] = useState(false);
  const [showWaiverTemplates, setShowWaiverTemplates] = useState(false);
  const [showWaiverTemplate, setShowWaiverTemplate] = useState(false);
  const [waiverTemplate, setWaiverTemplate] = useState(null);
  const [waiverTemplateLoading, setWaiverTemplateLoading] = useState(true);
  const [waiverTemplateSaving, setWaiverTemplateSaving] = useState(false);
  const [showTransferWaiverTemplate, setShowTransferWaiverTemplate] = useState(false);
  const [transferWaiverTemplate, setTransferWaiverTemplate] = useState(null);
  const [transferWaiverTemplateLoading, setTransferWaiverTemplateLoading] = useState(true);
  const [transferWaiverTemplateSaving, setTransferWaiverTemplateSaving] = useState(false);
  const [showHoursOfOperation, setShowHoursOfOperation] = useState(false);
  const [showPickupHoursOfOperation, setShowPickupHoursOfOperation] = useState(false);
  
  // Show/Hide state for form sections
  const [showBicycleForm, setShowBicycleForm] = useState(false);
  const [showAddOnForm, setShowAddOnForm] = useState(false);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [showDurationDiscountForm, setShowDurationDiscountForm] = useState(false);

  // Email template state
  const [emailTemplateLoading, setEmailTemplateLoading] = useState(true);
  const [emailTemplateSaving, setEmailTemplateSaving] = useState(false);
  
  // Universal Booking Confirmation Template
  const [emailUniversalFields, setEmailUniversalFields] = useState({
    mainTitle: "",
    headerSubtitle: "",
    greetingText: "",
    buttonText: "",
    footerMessage: "",
    customInstructions: "",
  });
  const [originalEmailUniversalFields, setOriginalEmailUniversalFields] = useState({});

  // Universal SMS Confirmation Template
  const [smsUniversalTemplate, setSmsUniversalTemplate] = useState("");
  const [originalSmsUniversalTemplate, setOriginalSmsUniversalTemplate] = useState("");
  
  const [emailBookingEditFields, setEmailBookingEditFields] = useState({
    mainTitle: "",
    headerSubtitle: "",
    greetingText: "",
    customInstructions: "",
    buttonText: "",
    footerMessage: "",
    stripeRefundReceiptLink: "",
    refundAmount: "",
  });
  const [originalEmailBookingEditFields, setOriginalEmailBookingEditFields] = useState({});
  
  const [emailCancellationRequestFields, setEmailCancellationRequestFields] = useState({
    headerSubtitle: "",
    greetingText: "",
    warningText: "",
    buttonText: "",
    footerMessage: "",
  });
  const [originalEmailCancellationRequestFields, setOriginalEmailCancellationRequestFields] = useState({});
  
  const [emailCancellationConfirmationFields, setEmailCancellationConfirmationFields] = useState({
    headerSubtitle: "",
    greetingText: "",
    customInstructions: "",
    buttonText: "",
    footerMessage: "",
  });
  const [originalEmailCancellationConfirmationFields, setOriginalEmailCancellationConfirmationFields] = useState({});

  // Refund Only Email Template
  const [emailRefundOnlyFields, setEmailRefundOnlyFields] = useState({
    mainTitle: "",
    headerSubtitle: "",
    greetingText: "",
    customMessage: "",
    buttonText: "",
    footerMessage: "",
    stripeRefundReceiptLink: "",
    refundAmount: "",
  });
  const [originalEmailRefundOnlyFields, setOriginalEmailRefundOnlyFields] = useState({});

  // Refund Only SMS Template
  const [refundOnlySmsTemplate, setRefundOnlySmsTemplate] = useState("");
  const [refundOnlySmsLoading, setRefundOnlySmsLoading] = useState(true);
  const [refundOnlySmsSaving, setRefundOnlySmsSaving] = useState(false);
  const [originalRefundOnlySmsTemplate, setOriginalRefundOnlySmsTemplate] = useState("");

  // Thank You Email Template (sent after booking completion)
  const [emailThankYouFields, setEmailThankYouFields] = useState({
    headerSubtitle: "",
    greetingText: "",
    customInstructions: "",
    buttonText: "",
    footerMessage: "",
  });
  const [originalEmailThankYouFields, setOriginalEmailThankYouFields] = useState({});

  // Thank You SMS Template (sent after booking completion)
  const [thankYouSmsTemplate, setThankYouSmsTemplate] = useState("");
  const [thankYouSmsLoading, setThankYouSmsLoading] = useState(true);
  const [thankYouSmsSaving, setThankYouSmsSaving] = useState(false);
  const [originalThankYouSmsTemplate, setOriginalThankYouSmsTemplate] = useState("");

  // Payment Link SMS Template
  const [paymentLinkSmsTemplate, setPaymentLinkSmsTemplate] = useState("");
  const [paymentLinkSmsLoading, setPaymentLinkSmsLoading] = useState(false);
  const [paymentLinkSmsSaving, setPaymentLinkSmsSaving] = useState(false);
  const [originalPaymentLinkSmsTemplate, setOriginalPaymentLinkSmsTemplate] = useState("");

  // 5-Star Review Request (SMS + email, sent X hours after check-in completion)
  const [reviewRequestEnabled, setReviewRequestEnabled] = useState(false);
  const [reviewRequestUrl, setReviewRequestUrl] = useState("");
  const [reviewRequestUrlError, setReviewRequestUrlError] = useState("");
  const [reviewRequestDelayHours, setReviewRequestDelayHours] = useState("4");
  const [reviewRequestSuppressDays, setReviewRequestSuppressDays] = useState("3");
  const [reviewRequestSmsTemplate, setReviewRequestSmsTemplate] = useState(REVIEW_REQUEST_DEFAULT_SMS);
  const [reviewRequestEmailSubject, setReviewRequestEmailSubject] = useState(REVIEW_REQUEST_DEFAULT_EMAIL_SUBJECT);
  const [reviewRequestEmailBody, setReviewRequestEmailBody] = useState(REVIEW_REQUEST_DEFAULT_EMAIL_BODY);
  const [originalReviewRequest, setOriginalReviewRequest] = useState(null);
  const [reviewRequestLoading, setReviewRequestLoading] = useState(true);
  const [reviewRequestSaving, setReviewRequestSaving] = useState(false);

  // Equipment Penalty Email Template (sent after charging for damaged equipment)
  const [emailPenaltyFields, setEmailPenaltyFields] = useState({
    headerSubtitle: "",
    greetingText: "",
    customInstructions: "",
    buttonText: "",
    footerMessage: "",
  });
  const [originalEmailPenaltyFields, setOriginalEmailPenaltyFields] = useState({});

  // Equipment Penalty SMS Template (sent after charging for damaged equipment)
  const [penaltySmsTemplate, setPenaltySmsTemplate] = useState("");
  const [penaltySmsLoading, setPenaltySmsLoading] = useState(true);
  const [penaltySmsSaving, setPenaltySmsSaving] = useState(false);
  const [originalPenaltySmsTemplate, setOriginalPenaltySmsTemplate] = useState("");


  // SMS confirmation template state

  // SMS reminder template state
  const [smsDayBeforeDeliveryTemplate, setSmsDayBeforeDeliveryTemplate] =
    useState("");
  const [originalSmsDayBeforeDeliveryTemplate, setOriginalSmsDayBeforeDeliveryTemplate] = useState("");
  const [smsDayOfDeliveryTemplate, setSmsDayOfDeliveryTemplate] = useState("");
  const [originalSmsDayOfDeliveryTemplate, setOriginalSmsDayOfDeliveryTemplate] = useState("");
  const [smsDayBeforePickupTemplate, setSmsDayBeforePickupTemplate] =
    useState("");
  const [originalSmsDayBeforePickupTemplate, setOriginalSmsDayBeforePickupTemplate] = useState("");
  const [smsDayOfPickupTemplate, setSmsDayOfPickupTemplate] = useState("");
  const [originalSmsDayOfPickupTemplate, setOriginalSmsDayOfPickupTemplate] = useState("");
  const [smsDayOfInStoreStartTemplate, setSmsDayOfInStoreStartTemplate] =
    useState("");
  const [originalSmsDayOfInStoreStartTemplate, setOriginalSmsDayOfInStoreStartTemplate] = useState("");
  const [smsDayOfInStoreReturnTemplate, setSmsDayOfInStoreReturnTemplate] =
    useState("");
  const [originalSmsDayOfInStoreReturnTemplate, setOriginalSmsDayOfInStoreReturnTemplate] = useState("");

  // Cancellation confirmation templates
  const [cancellationEmailSubject, setCancellationEmailSubject] = useState("");
  const [cancellationEmailBody, setCancellationEmailBody] = useState("");
  const [originalCancellationEmailSubject, setOriginalCancellationEmailSubject] = useState("");
  const [originalCancellationEmailBody, setOriginalCancellationEmailBody] = useState("");
  const [cancellationSmsTemplate, setCancellationSmsTemplate] = useState("");
  const [originalCancellationSmsTemplate, setOriginalCancellationSmsTemplate] = useState("");
  
  // Partial Refund Templates
  const [partialRefundEmailSubject, setPartialRefundEmailSubject] = useState("");
  const [partialRefundEmailBody, setPartialRefundEmailBody] = useState("");
  const [originalPartialRefundEmailSubject, setOriginalPartialRefundEmailSubject] = useState("");
  const [originalPartialRefundEmailBody, setOriginalPartialRefundEmailBody] = useState("");
  const [partialRefundSmsTemplate, setPartialRefundSmsTemplate] = useState("");
  const [originalPartialRefundSmsTemplate, setOriginalPartialRefundSmsTemplate] = useState("");

  const [smsTemplateLoading, setSmsTemplateLoading] = useState(true);
  const [smsTemplateSaving, setSmsTemplateSaving] = useState(false);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Simulate loading progress bar over 4 seconds
  useEffect(() => {
    const isLoading = userLoading || loading || loadingAddOns || salesTaxLoading || 
                      loadingDiscounts || loadingDurationDiscounts ||
                      startingAddressLoading || mileageRateLoading || loadingFeeLoading ||
                      deliveryInfoLoading || pickupInfoLoading || emailTemplateLoading ||
                      smsTemplateLoading || storeNameLoading || legalStoreNameLoading || 
                      legalStoreAddressLoading || cancellationPolicyLoading || appHeaderTitleLoading || storeLogoLoading || 
                      storePhoneLoading || storeEmailLoading || deliveryPhoneLoading || deliveryCallingPhoneLoading || 
                      customerBlurbLoading || hoursLoading || pickupHoursLoading || 
                      product1TitleLoading || product2TitleLoading || 
                      useDeliveryDiscountsLoading || useDurationDiscountsLoading ||
                      confirmationHeadingTransportationLoading ||
                      confirmationHeadingInStorePickupLoading;
    
    if (!isLoading) {
      setLoadingProgress(0);
      return;
    }

    const duration = 4000; // 4 seconds
    const interval = 50; // Update every 50ms
    const increment = 100 / (duration / interval);
    let currentProgress = 0;

    const timer = setInterval(() => {
      currentProgress += increment;
      if (currentProgress >= 100) {
        currentProgress = 100;
        clearInterval(timer);
      }
      setLoadingProgress(currentProgress);
    }, interval);

    return () => clearInterval(timer);
  }, [userLoading, loading, loadingAddOns, salesTaxLoading, startingAddressLoading, 
      mileageRateLoading, loadingFeeLoading, deliveryInfoLoading, pickupInfoLoading,
      emailTemplateLoading, smsTemplateLoading, storeNameLoading, legalStoreNameLoading, 
      appHeaderTitleLoading, storeLogoLoading,
      storePhoneLoading, storeEmailLoading, deliveryPhoneLoading, customerBlurbLoading,
      hoursLoading, pickupHoursLoading, product1TitleLoading, product2TitleLoading, 
      confirmationHeadingTransportationLoading, confirmationHeadingInStorePickupLoading]);

  useEffect(() => {
    // Only load data after authentication completes
    if (!userLoading && user) {
    loadBicycles();
    loadAddOns();
    loadSalesTax();
      loadCardProcessingFee();
      loadCancellationWindows();
    loadDiscounts();
      loadDurationDiscounts();
      loadUseDurationDiscounts();
    loadStartingAddress();
    loadCadenceConnection();
    loadMileageRate();
    loadLoadingFee();
    loadDeliveryInfo();
    loadPickupInfo();
    loadEmailUniversalTemplate();
    loadWaiverTemplate();
    loadTransferWaiverTemplate();
    loadSmsUniversalTemplate();
    loadSmsDayBeforeDeliveryTemplate();
    loadSmsDayOfDeliveryTemplate();
    loadSmsDayBeforePickupTemplate();
    loadSmsDayOfPickupTemplate();
    loadSmsDayOfInStoreStartTemplate();
    loadSmsDayOfInStoreReturnTemplate();
      loadCancellationEmailTemplate();
      loadCancellationSmsTemplate();
      loadPartialRefundEmailTemplate();
      loadPartialRefundSmsTemplate();
      loadCancellationRequestTemplate();
      loadRefundOnlyEmailTemplate();
      loadRefundOnlySmsTemplate();
      loadThankYouEmailTemplate();
      loadThankYouSmsTemplate();
      loadPaymentLinkSmsTemplate();
      loadPenaltyEmailTemplate();
      loadPenaltySmsTemplate();
      loadReviewRequestSettings();
    loadStoreName();
      loadLegalStoreName();
      loadLegalStoreAddress();
      loadCancellationPolicy();
      loadAppHeaderTitle();
    loadStoreLogo();
    loadStorePhone();
    loadStoreEmail();
      loadStoreWebsite();
      loadCardProcessingFee();
    loadDeliveryPhone();
    loadDeliveryCallingPhone();
    loadCustomerBlurb();
    loadHoursOfOperation();
    loadPickupHoursOfOperation();
    loadProductTitles();
    loadConfirmationHeadings();
      loadUseDeliveryDiscounts();
    }
  }, [userLoading, user]);

  // Sync products from Zustand store when they update
  useEffect(() => {
    if (products1FromStore && products1FromStore.length > 0) {
      setBicycles(products1FromStore);
      setLoading(false);
    }
  }, [products1FromStore]);

  useEffect(() => {
    if (products2FromStore && products2FromStore.length > 0) {
      setAddOns(products2FromStore);
      setLoadingAddOns(false);
    }
  }, [products2FromStore]);

  // Load Google Maps Places API
  useEffect(() => {
    const GOOGLE_MAPS_API_KEY = "AIzaSyDnMrIVkx-6V0Brm4pxezeOVcTOELsTmdQ";

    // Check if already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      setMapsLoaded(true);
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService();
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
      return;
    }

    // Check if script is loading
    const existingScript = document.querySelector(
      `script[src*="maps.googleapis.com"]`
    );
    if (existingScript) {
      const checkLoaded = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          setMapsLoaded(true);
          autocompleteServiceRef.current =
            new window.google.maps.places.AutocompleteService();
          placesServiceRef.current =
            new window.google.maps.places.PlacesService(
              document.createElement("div")
            );
          clearInterval(checkLoaded);
        }
      }, 100);
      setTimeout(() => clearInterval(checkLoaded), 10000);
      return;
    }

    // Load script
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setMapsLoaded(true);
        autocompleteServiceRef.current =
          new window.google.maps.places.AutocompleteService();
        placesServiceRef.current = new window.google.maps.places.PlacesService(
          document.createElement("div")
        );
      }
    };
    document.head.appendChild(script);
  }, []);

  // Handle starting address autocomplete
  useEffect(() => {
    // Skip autocomplete during initial load
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      setStartingAddressSuggestions([]);
      setShowStartingAddressSuggestions(false);
      return;
    }

    // Only show suggestions if user has interacted with the field
    if (!hasInteractedWithAddressRef.current) {
      setStartingAddressSuggestions([]);
      setShowStartingAddressSuggestions(false);
      return;
    }

    if (
      !mapsLoaded ||
      !autocompleteServiceRef.current ||
      !startingAddressInput.trim()
    ) {
      setStartingAddressSuggestions([]);
      setShowStartingAddressSuggestions(false);
      return;
    }

    const query = startingAddressInput.trim();
    if (query.length < 3) {
      setStartingAddressSuggestions([]);
      setShowStartingAddressSuggestions(false);
      return;
    }

    // Debounce autocomplete requests
    const timeoutId = setTimeout(() => {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: "us" },
          types: ["address"],
        },
        (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setStartingAddressSuggestions(predictions);
            setShowStartingAddressSuggestions(true);
          } else {
            setStartingAddressSuggestions([]);
            setShowStartingAddressSuggestions(false);
          }
        }
      );
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [startingAddressInput, mapsLoaded]);

  // ==================== AUTHENTICATION ====================

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      console.log(
        "🔐 Auth state in Settings:",
        currentUser ? currentUser.email : "No user"
      );
      setUser(currentUser);
      setUserLoading(false);

      if (!currentUser) {
        console.warn("⚠️ User not authenticated, redirecting to login");
        navigate("/login");
      }
    });

    return () => {
      unsubscribeAuth();
    };
  }, [navigate]);

  // Select starting address suggestion
  const selectStartingAddress = (placeId) => {
    if (!placesServiceRef.current || !placeId) return;

    setShowStartingAddressSuggestions(false);
    setStartingAddressSuggestions([]);

    placesServiceRef.current.getDetails(
      {
        placeId: placeId,
        fields: ["formatted_address", "address_components", "geometry"],
      },
      (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          const fullAddress = place.formatted_address;
          setStartingAddressInput(fullAddress);
          setStartingAddress(fullAddress);
        }
      }
    );
  };

  const loadBicycles = async () => {
    try {
      setLoading(true);
      // Load from Zustand store (already synced by App.js cache initialization)
      // Only update if store has data (don't overwrite with empty array if cache not ready)
      if (products1FromStore && products1FromStore.length > 0) {
        setBicycles(products1FromStore);
      } else if (products1FromStore && products1FromStore.length === 0) {
        // Store is initialized but empty - this is valid
        setBicycles([]);
      }
      // If products1FromStore is undefined/null, cache not ready yet - don't update
    } catch (error) {
      console.error("❌ Error loading bicycles from store:", error);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (bike) => {
    setEditingId(bike.id);
    const retailPriceDollars = bike.retailPriceCents ? centsToDollars(bike.retailPriceCents) : "";
    setFormData({
      name: bike.name || "",
      // price is stored in CENTS; this field is entered and shown in DOLLARS.
      // (retailPriceCells just above already does this — price simply never got it,
      // so editing a $15/day bike used to show "1500".)
      price: bike.price ? centsToDollars(bike.price) : "",
      quantity: bike.quantity?.toString() || "",
      imageUrl: bike.imageUrl || "",
      description: bike.description || "",
      includedItems: bike.includedItems || "",
      hasRetailPrice: bike.hasRetailPrice || false,
      retailPriceCents: retailPriceDollars.toString(),
      physicalItem: bike.physicalItem !== undefined ? bike.physicalItem : true,
    });
    setShowBicycleForm(true);
    // Scroll to form after a brief delay to ensure it's rendered
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setFormData({
      name: "",
      price: "",
      quantity: "",
      imageUrl: "",
      description: "",
      includedItems: "",
      hasRetailPrice: false,
      retailPriceCents: "",
      physicalItem: true,
    });
    setShowBicycleForm(false);
  };

  const handleBikeImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!user) {
      alert("Error: " + "You must be logged in to upload images");
      navigate("/login");
      return;
    }

    if (!file.type.startsWith("image/")) {
      alert("Error: " + "Please select an image file");
      return;
    }

    try {
      setUploadingImage(true);
      const timestamp = Date.now();
      const fileName = `products1/${
        editingId || `new_${timestamp}`
      }/${timestamp}_${file.name}`;

      const downloadURL = await uploadFile(fileName, file, {
        contentType: file.type,
      });

      setFormData({ ...formData, imageUrl: downloadURL });
      alert("Success: " + "Image uploaded successfully");
    } catch (error) {
      console.error("❌ Error uploading image:", error);
      console.error("❌ Error details:", {
        message: error.message,
        code: error.code,
        stack: error.stack,
      });
      alert("Error: " +
        "Failed to upload image: " + (error.message || "Unknown error")
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddOnImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!user) {
      alert("Error: " + "You must be logged in to upload images");
      navigate("/login");
      return;
    }

    if (!file.type.startsWith("image/")) {
      alert("Error: " + "Please select an image file");
      return;
    }

    try {
      setUploadingAddOnImage(true);
      const timestamp = Date.now();
      const fileName = `products2/${
        editingAddOnId || `new_${timestamp}`
      }/${timestamp}_${file.name}`;
      const downloadURL = await uploadFile(fileName, file, {
        contentType: file.type,
      });

      setAddOnFormData({ ...addOnFormData, imageUrl: downloadURL });
      alert("Success: " + "Image uploaded successfully");
    } catch (error) {
      console.error("❌ Error uploading image:", error);
      console.error("❌ Error details:", {
        message: error.message,
        code: error.code,
        stack: error.stack,
      });
      alert("Error: " +
        "Failed to upload image: " + (error.message || "Unknown error")
      );
    } finally {
      setUploadingAddOnImage(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert("Error: " + "Bicycle name is required");
      return;
    }

    // The field is in DOLLARS, storage is in CENTS (.cursorrules #3). Previously this
    // parsed the input straight through, so typing "20" saved 20 CENTS per day.
    const priceDollars = parseFloat(formData.price);
    const quantity = parseInt(formData.quantity, 10);

    if (isNaN(priceDollars) || priceDollars < 0) {
      alert("Error: " + "Please enter a valid price");
      return;
    }
    const price = dollarsToCents(priceDollars);

    if (isNaN(quantity) || quantity < 0) {
      alert("Error: " + "Please enter a valid quantity");
      return;
    }

    // Validate retail price if hasRetailPrice is checked
    let retailPriceCents = null;
    if (formData.hasRetailPrice) {
      const retailPrice = parseFloat(formData.retailPriceCents);
      if (isNaN(retailPrice) || retailPrice < 0) {
        alert("Error: " + "Please enter a valid retail price");
        return;
      }
      retailPriceCents = dollarsToCents(retailPrice);
    }

    try {
      setSaving(true);
      const bikeData = {
        name: formData.name.trim(),
        price: price,
        quantity: quantity,
        imageUrl: formData.imageUrl || "",
        description: formData.description?.trim() || "",
        includedItems: formData.includedItems?.trim() || "",
        hasRetailPrice: formData.hasRetailPrice || false,
        retailPriceCents: retailPriceCents,
        physicalItem: formData.physicalItem !== undefined ? formData.physicalItem : true,
        updatedAt: new Date(),
      };

      if (editingId) {
        // Update existing bicycle
        const products1Path = getCollectionPath("products1");
        await updateDocument(products1Path, editingId, bikeData);
        alert("Success: " + "Bicycle updated successfully");
      } else {
        // Create new bicycle (ID will be auto-generated)
        const products1Path = getCollectionPath("products1");
        const newId = await createDocument(products1Path, {
          ...bikeData,
          createdAt: new Date(),
        });
        alert("Success: " + "Bicycle created successfully");
      }

      cancelEditing();
      loadBicycles();
    } catch (error) {
      console.error("❌ Error saving bicycle:", error);
      alert("Error: " + "Failed to save bicycle: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bikeId, bikeName) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${bikeName}"?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setDeletingId(bikeId);
      const products1Path = getCollectionPath("products1");
      await deleteDocument(products1Path, bikeId);
      alert("Success: " + "Bicycle deleted successfully");
      loadBicycles();
    } catch (error) {
      console.error("❌ Error deleting bicycle:", error);
      alert("Error: " + "Failed to delete bicycle: " + error.message);
    } finally {
      setDeletingId(null);
    }
  };

  const startAdding = () => {
    setEditingId(null);
    setFormData({ name: "", price: "", quantity: "", imageUrl: "", description: "", includedItems: "", hasRetailPrice: false, retailPriceCents: "", physicalItem: true });
    setShowBicycleForm(true);
    // Scroll to form after a brief delay to ensure it's rendered
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  // ==================== ADD-ONS FUNCTIONS ====================

  const loadAddOns = async () => {
    try {
      setLoadingAddOns(true);
      // Load from Zustand store (already synced by App.js cache initialization)
      // Only update if store has data (don't overwrite with empty array if cache not ready)
      if (products2FromStore && products2FromStore.length > 0) {
        setAddOns(products2FromStore);
      } else if (products2FromStore && products2FromStore.length === 0) {
        // Store is initialized but empty - this is valid
        setAddOns([]);
      }
      // If products2FromStore is undefined/null, cache not ready yet - don't update
    } catch (error) {
      console.error("❌ Error loading add-ons from store:", error);
    } finally {
      setLoadingAddOns(false);
    }
  };

  const startEditingAddOn = (addOn) => {
    setEditingAddOnId(addOn.id);
    const retailPriceDollars = addOn.retailPriceCents ? centsToDollars(addOn.retailPriceCents) : "";
    setAddOnFormData({
      name: addOn.name || "",
      price: addOn.price?.toString() || "",
      option: addOn.option || "per-booking",
      inventory:
        addOn.inventory !== undefined && addOn.inventory !== null
          ? addOn.inventory.toString()
          : "",
      description: addOn.description || "",
      imageUrl: addOn.imageUrl || "",
      hasRetailPrice: addOn.hasRetailPrice || false,
      retailPriceCents: retailPriceDollars.toString(),
      physicalItem: addOn.physicalItem !== undefined ? addOn.physicalItem : true,
    });
    setShowAddOnForm(true);
    // Scroll to form after a brief delay to ensure it's rendered
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const cancelEditingAddOn = () => {
    setEditingAddOnId(null);
    setAddOnFormData({
      name: "",
      price: "",
      option: "per-booking",
      inventory: "",
      description: "",
      imageUrl: "",
      hasRetailPrice: false,
      retailPriceCents: "",
      physicalItem: true,
    });
    setShowAddOnForm(false);
  };

  const handleAddOnSave = async () => {
    if (!addOnFormData.name.trim()) {
      alert("Error: " + "Add-on name is required");
      return;
    }

    const price = parseFloat(addOnFormData.price);

    if (isNaN(price) || price < 0) {
      alert("Error: " + "Please enter a valid price");
      return;
    }

    if (
      !addOnFormData.option ||
      (addOnFormData.option !== "per-booking" &&
        addOnFormData.option !== "user-selection" &&
        addOnFormData.option !== "per-bike")
    ) {
      alert("Error: " + "Please select a valid option type");
      return;
    }

    try {
      setAddOnSaving(true);
      const inventory =
        addOnFormData.inventory.trim() !== ""
          ? parseInt(addOnFormData.inventory.trim(), 10)
          : null;

      if (
        addOnFormData.inventory.trim() !== "" &&
        (isNaN(inventory) || inventory < 0)
      ) {
        alert("Error: Please enter a valid inventory quantity or leave it empty");
        return;
      }

      // Validate retail price if hasRetailPrice is checked
      let retailPriceCents = null;
      if (addOnFormData.hasRetailPrice) {
        const retailPrice = parseFloat(addOnFormData.retailPriceCents);
        if (isNaN(retailPrice) || retailPrice < 0) {
          alert("Error: " + "Please enter a valid retail price");
          return;
        }
        retailPriceCents = dollarsToCents(retailPrice);
      }

      const addOnData = {
        name: addOnFormData.name.trim(),
        price: price,
        option: addOnFormData.option,
        description: addOnFormData.description?.trim() || "",
        imageUrl: addOnFormData.imageUrl || "",
        hasRetailPrice: addOnFormData.hasRetailPrice || false,
        retailPriceCents: retailPriceCents,
        physicalItem: addOnFormData.physicalItem !== undefined ? addOnFormData.physicalItem : true,
        updatedAt: new Date(),
      };

      console.log("💾 Saving add-on data:", {
        id: editingAddOnId || "new",
        hasImageUrl: !!addOnData.imageUrl,
        imageUrl: addOnData.imageUrl,
      });

      // Include inventory - if null, it means user cleared the field (set to null)
      // If not null, it means user provided a value
      addOnData.inventory = inventory;

      if (editingAddOnId) {
        // Update existing add-on
        const products2Path = getCollectionPath("products2");
        await updateDocument(products2Path, editingAddOnId, addOnData);
        alert("Success: " + "Add-on updated successfully");
      } else {
        // Create new add-on (ID will be auto-generated)
        // Set order to be at the end of the list
        const maxOrder = addOns.length > 0 
          ? Math.max(...addOns.map(a => a.order ?? 0))
          : -1;
        
        const products2Path = getCollectionPath("products2");
        const newId = await createDocument(products2Path, {
          ...addOnData,
          order: maxOrder + 1,
          createdAt: new Date(),
        });
        alert("Success: " + "Add-on created successfully");
      }

      cancelEditingAddOn();
      loadAddOns();
    } catch (error) {
      console.error("❌ Error saving add-on:", error);
      alert("Error: " + "Failed to save add-on: " + error.message);
    } finally {
      setAddOnSaving(false);
    }
  };

  const handleAddOnDelete = async (addOnId, addOnName) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${addOnName}"?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setDeletingAddOnId(addOnId);
      const products2Path = getCollectionPath("products2");
      await deleteDocument(products2Path, addOnId);
      alert("Success: " + "Add-on deleted successfully");
      loadAddOns();
    } catch (error) {
      console.error("❌ Error deleting add-on:", error);
      alert("Error: " + "Failed to delete add-on: " + error.message);
    } finally {
      setDeletingAddOnId(null);
    }
  };

  const moveBicycleUp = async (index) => {
    if (index === 0) return; // Already at top
    
    try {
      const newBicycles = [...bicycles];
      const currentItem = newBicycles[index];
      const previousItem = newBicycles[index - 1];
      
      // Swap order values
      const currentOrder = currentItem.order ?? index;
      const previousOrder = previousItem.order ?? (index - 1);
      
      // Update in database
      const products1Path = getCollectionPath("products1");
      await setDoc(doc(db, products1Path, currentItem.id), { order: previousOrder }, { merge: true });
      await setDoc(doc(db, products1Path, previousItem.id), { order: currentOrder }, { merge: true });
      
      // Reload to reflect changes
      loadBicycles();
    } catch (error) {
      console.error("❌ Error moving bicycle up:", error);
      alert("Error: " + "Failed to reorder bicycle");
    }
  };

  const moveBicycleDown = async (index) => {
    if (index === bicycles.length - 1) return; // Already at bottom
    
    try {
      const newBicycles = [...bicycles];
      const currentItem = newBicycles[index];
      const nextItem = newBicycles[index + 1];
      
      // Swap order values
      const currentOrder = currentItem.order ?? index;
      const nextOrder = nextItem.order ?? (index + 1);
      
      // Update in database
      const products1Path = getCollectionPath("products1");
      await setDoc(doc(db, products1Path, currentItem.id), { order: nextOrder }, { merge: true });
      await setDoc(doc(db, products1Path, nextItem.id), { order: currentOrder }, { merge: true });
      
      // Reload to reflect changes
      loadBicycles();
    } catch (error) {
      console.error("❌ Error moving bicycle down:", error);
      alert("Error: " + "Failed to reorder bicycle");
    }
  };

  const moveAddOnUp = async (index) => {
    if (index === 0) return; // Already at top
    
    try {
      const newAddOns = [...addOns];
      const currentItem = newAddOns[index];
      const previousItem = newAddOns[index - 1];
      
      // Swap order values
      const currentOrder = currentItem.order ?? index;
      const previousOrder = previousItem.order ?? (index - 1);
      
      // Update in database
      const products2Path = getCollectionPath("products2");
      await setDoc(doc(db, products2Path, currentItem.id), { order: previousOrder }, { merge: true });
      await setDoc(doc(db, products2Path, previousItem.id), { order: currentOrder }, { merge: true });
      
      // Reload to reflect changes
      loadAddOns();
    } catch (error) {
      console.error("❌ Error moving add-on up:", error);
      alert("Error: " + "Failed to reorder add-on");
    }
  };

  const moveAddOnDown = async (index) => {
    if (index === addOns.length - 1) return; // Already at bottom
    
    try {
      const newAddOns = [...addOns];
      const currentItem = newAddOns[index];
      const nextItem = newAddOns[index + 1];
      
      // Swap order values
      const currentOrder = currentItem.order ?? index;
      const nextOrder = nextItem.order ?? (index + 1);
      
      // Update in database
      const products2Path = getCollectionPath("products2");
      await setDoc(doc(db, products2Path, currentItem.id), { order: nextOrder }, { merge: true });
      await setDoc(doc(db, products2Path, nextItem.id), { order: currentOrder }, { merge: true });
      
      // Reload to reflect changes
      loadAddOns();
    } catch (error) {
      console.error("❌ Error moving add-on down:", error);
      alert("Error: " + "Failed to reorder add-on");
    }
  };

  const startAddingAddOn = () => {
    setEditingAddOnId(null);
    setAddOnFormData({
      name: "",
      price: "",
      option: "per-booking",
      inventory: "",
      description: "",
      imageUrl: "",
      hasRetailPrice: false,
      retailPriceCents: "",
      physicalItem: true,
    });
    setShowAddOnForm(true);
    // Scroll to form after a brief delay to ensure it's rendered
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  // ==================== CADENCE POS CONNECTION ====================
  // Load/save of settings/cadenceConnection — the pairing values come from
  // the POS admin's "Rental Link" card (tenant ID, store ID, endpoint URL,
  // pairing token). The morning sync + booking triggers in functions/index.js
  // only act when this doc is complete and enabled.

  const loadCadenceConnection = async () => {
    try {
      const settingsRef = doc(db, getSettingsPath(), "cadenceConnection");
      const settingsDoc = await getDoc(settingsRef);
      const d = settingsDoc.exists() ? settingsDoc.data() : {};
      const conn = {
        enabled: !!d.enabled,
        tenantID: d.tenantID || "",
        storeID: d.storeID || "",
        endpointUrl: d.endpointUrl || "",
        pairingToken: d.pairingToken || "",
      };
      setCadenceConn(conn);
      setOriginalCadenceConn(JSON.stringify(conn));
    } catch (error) {
      console.error("❌ Error loading Cadence connection:", error);
    }
  };

  const handleSaveCadenceConnection = async () => {
    try {
      setCadenceSaving(true);
      const settingsRef = doc(db, getSettingsPath(), "cadenceConnection");
      await setDoc(settingsRef, { ...cadenceConn, updatedAt: Timestamp.now() }, { merge: true });
      setOriginalCadenceConn(JSON.stringify(cadenceConn));
    } catch (error) {
      console.error("❌ Error saving Cadence connection:", error);
      alert("Error: " + "Could not save the Cadence connection");
    } finally {
      setCadenceSaving(false);
    }
  };

  // ==================== STARTING ADDRESS FUNCTIONS ====================

  const loadStartingAddress = async () => {
    try {
      setStartingAddressLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "startingAddress");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const address = data.address || "";
        setStartingAddress(address);
        setStartingAddressInput(address);
        setOriginalStartingAddress(address);
        // Close suggestions when loading existing address
        setShowStartingAddressSuggestions(false);
        setStartingAddressSuggestions([]);
      } else {
        setStartingAddress("");
        setStartingAddressInput("");
        setOriginalStartingAddress("");
        setShowStartingAddressSuggestions(false);
        setStartingAddressSuggestions([]);
      }
    } catch (error) {
      console.error("❌ Error loading starting address:", error);
      setStartingAddress("");
      setStartingAddressInput("");
      setShowStartingAddressSuggestions(false);
      setStartingAddressSuggestions([]);
    } finally {
      setStartingAddressLoading(false);
    }
  };

  const handleSaveStartingAddress = async () => {
    if (!startingAddressInput.trim()) {
      alert("Error: " + "Please enter a starting address");
      return;
    }

    try {
      setStartingAddressSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "startingAddress");

      const dataToSave = {
        address: startingAddressInput.trim(),
        updatedAt: Timestamp.now(),
      };
      await setDoc(settingsRef, dataToSave, { merge: true });
      const savedAddress = startingAddressInput.trim();
      setStartingAddress(savedAddress);
      setOriginalStartingAddress(savedAddress);
      alert("Success: " + "Starting address saved successfully");
    } catch (error) {
      console.error("❌ Error saving starting address:", error);
      alert("Error: " +
        `Failed to save starting address: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setStartingAddressSaving(false);
    }
  };

  // ==================== MILEAGE RATE FUNCTIONS ====================

  const loadMileageRate = async () => {
    try {
      setMileageRateLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "mileageRate");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const rate = data.rate?.toString() || "";
        setMileageRate(rate);
        setOriginalMileageRate(rate);
      } else {
        setMileageRate("");
        setOriginalMileageRate("");
      }
    } catch (error) {
      console.error("❌ Error loading mileage rate:", error);
      setMileageRate("");
      setOriginalMileageRate("");
    } finally {
      setMileageRateLoading(false);
    }
  };

  const handleSaveMileageRate = async () => {
    const rate = parseFloat(mileageRate);

    if (mileageRate.trim() !== "" && (isNaN(rate) || rate < 0)) {
      alert("Error: " + "Please enter a valid mileage rate (0 or greater)");
      return;
    }

    try {
      setMileageRateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "mileageRate");

      if (mileageRate.trim() === "") {
        await setDoc(settingsRef, { rate: null }, { merge: true });
        setOriginalMileageRate("");
        alert("Success: " + "Mileage rate cleared");
      } else {
        const dataToSave = {
          rate: rate,
          updatedAt: Timestamp.now(),
        };
        await setDoc(settingsRef, dataToSave, { merge: true });
        setOriginalMileageRate(mileageRate.trim());
        alert("Success: " + "Mileage rate saved successfully");
      }
    } catch (error) {
      console.error("❌ Error saving mileage rate:", error);
      alert("Error: " +
        `Failed to save mileage rate: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setMileageRateSaving(false);
    }
  };

  // ==================== LOADING FEE FUNCTIONS ====================

  const loadLoadingFee = async () => {
    try {
      setLoadingFeeLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "loadingFee");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const fee = data.fee?.toString() || "";
        setLoadingFee(fee);
        setOriginalLoadingFee(fee);
      } else {
        setLoadingFee("");
        setOriginalLoadingFee("");
      }
    } catch (error) {
      console.error("❌ Error loading loading fee:", error);
      setLoadingFee("");
      setOriginalLoadingFee("");
    } finally {
      setLoadingFeeLoading(false);
    }
  };

  const handleSaveLoadingFee = async () => {
    const fee = parseFloat(loadingFee);

    if (loadingFee.trim() !== "" && (isNaN(fee) || fee < 0)) {
      alert("Error: " + "Please enter a valid loading fee (0 or greater)");
      return;
    }

    try {
      setLoadingFeeSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "loadingFee");

      if (loadingFee.trim() === "") {
        await setDoc(settingsRef, { fee: null }, { merge: true });
        setOriginalLoadingFee("");
        alert("Success: " + "Loading fee cleared");
      } else {
        const dataToSave = {
          fee: fee,
          updatedAt: Timestamp.now(),
        };
        await setDoc(settingsRef, dataToSave, { merge: true });
        setOriginalLoadingFee(loadingFee.trim());
        alert("Success: " + "Loading fee saved successfully");
      }
    } catch (error) {
      console.error("❌ Error saving loading fee:", error);
      alert("Error: " +
        `Failed to save loading fee: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setLoadingFeeSaving(false);
    }
  };

  // ==================== DELIVERY INFO FUNCTIONS ====================

  const loadDeliveryInfo = async () => {
    try {
      setDeliveryInfoLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "deliveryInfo");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const info = data.info || "";
        setDeliveryInfo(info);
        setOriginalDeliveryInfo(info);
      } else {
        setDeliveryInfo("");
        setOriginalDeliveryInfo("");
      }
    } catch (error) {
      console.error("❌ Error loading delivery info:", error);
      setDeliveryInfo("");
      setOriginalDeliveryInfo("");
    } finally {
      setDeliveryInfoLoading(false);
    }
  };

  const handleSaveDeliveryInfo = async () => {
    try {
      setDeliveryInfoSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "deliveryInfo");

      if (deliveryInfo.trim() === "") {
        await setDoc(settingsRef, { info: "" }, { merge: true });
        setOriginalDeliveryInfo("");
        alert("Success: " + "Delivery info cleared");
      } else {
        const dataToSave = {
          info: deliveryInfo.trim(),
          updatedAt: Timestamp.now(),
        };
        await setDoc(settingsRef, dataToSave, { merge: true });
        setOriginalDeliveryInfo(deliveryInfo.trim());
        alert("Success: " + "Delivery info saved successfully");
      }
    } catch (error) {
      console.error("❌ Error saving delivery info:", error);
      alert("Error: " +
        `Failed to save delivery info: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setDeliveryInfoSaving(false);
    }
  };

  // ==================== PICKUP INFO FUNCTIONS ====================

  const loadPickupInfo = async () => {
    try {
      setPickupInfoLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "pickupInfo");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const info = data.info || "";
        setPickupInfo(info);
        setOriginalPickupInfo(info);
      } else {
        setPickupInfo("");
        setOriginalPickupInfo("");
      }
    } catch (error) {
      console.error("❌ Error loading pickup info:", error);
      setPickupInfo("");
    } finally {
      setPickupInfoLoading(false);
    }
  };

  const handleSavePickupInfo = async () => {
    try {
      setPickupInfoSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "pickupInfo");

      if (pickupInfo.trim() === "") {
        await setDoc(settingsRef, { info: "" }, { merge: true });
        setOriginalPickupInfo("");
        alert("Success: " + "Pickup info cleared");
      } else {
        const dataToSave = {
          info: pickupInfo.trim(),
          updatedAt: Timestamp.now(),
        };
        await setDoc(settingsRef, dataToSave, { merge: true });
        setOriginalPickupInfo(pickupInfo.trim());
        alert("Success: " + "Pickup info saved successfully");
      }
    } catch (error) {
      console.error("❌ Error saving pickup info:", error);
      alert("Error: " +
        `Failed to save pickup info: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setPickupInfoSaving(false);
    }
  };

  // ==================== STORE CONTACT INFO FUNCTIONS ====================

  const loadStorePhone = async () => {
    try {
      setStorePhoneLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "storePhone");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const phone = formatPhoneNumber(data.phone || "");
        setStorePhone(phone);
        setOriginalStorePhone(phone);
      } else {
        setStorePhone("");
        setOriginalStorePhone("");
      }
    } catch (error) {
      console.error("❌ Error loading store phone:", error);
      setStorePhone("");
      setOriginalStorePhone("");
    } finally {
      setStorePhoneLoading(false);
    }
  };

  const handleSaveStorePhone = async () => {
    try {
      setStorePhoneSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "storePhone");

      const dataToSave = {
        phone: storePhone.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalStorePhone(storePhone.trim());
      alert("Success: " + "Store phone saved successfully");
    } catch (error) {
      console.error("❌ Error saving store phone:", error);
      alert("Error: " +
        `Failed to save store phone: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setStorePhoneSaving(false);
    }
  };

  const loadStoreEmail = async () => {
    try {
      setStoreEmailLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "storeEmail");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const email = data.email || "";
        setStoreEmail(email);
        setOriginalStoreEmail(email);
      } else {
        setStoreEmail("");
        setOriginalStoreEmail("");
      }
    } catch (error) {
      console.error("❌ Error loading store email:", error);
      setStoreEmail("");
      setOriginalStoreEmail("");
    } finally {
      setStoreEmailLoading(false);
    }
  };

  const handleSaveStoreEmail = async () => {
    try {
      setStoreEmailSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "storeEmail");

      const dataToSave = {
        email: storeEmail.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalStoreEmail(storeEmail.trim());
      alert("Success: " + "Store email saved successfully");
    } catch (error) {
      console.error("❌ Error saving store email:", error);
      alert("Error: " +
        `Failed to save store email: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setStoreEmailSaving(false);
    }
  };

  const loadStoreWebsite = async () => {
    try {
      setStoreWebsiteLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "storeWebsite");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const url = data.url || "";
        setStoreWebsite(url);
        setOriginalStoreWebsite(url);
      } else {
        setStoreWebsite("");
        setOriginalStoreWebsite("");
      }
    } catch (error) {
      console.error("❌ Error loading store website:", error);
      setStoreWebsite("");
      setOriginalStoreWebsite("");
    } finally {
      setStoreWebsiteLoading(false);
    }
  };

  const handleSaveStoreWebsite = async () => {
    try {
      setStoreWebsiteSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "storeWebsite");

      const dataToSave = {
        url: storeWebsite.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalStoreWebsite(storeWebsite.trim());
      alert("Success: " + "Store website saved successfully");
    } catch (error) {
      console.error("❌ Error saving store website:", error);
      alert("Error: " +
        `Failed to save store website: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setStoreWebsiteSaving(false);
    }
  };

  const loadDeliveryPhone = async () => {
    try {
      setDeliveryPhoneLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "deliveryPhone");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const phone = formatPhoneNumber(data.phone || "");
        setDeliveryPhone(phone);
        setOriginalDeliveryPhone(phone);
      } else {
        setDeliveryPhone("");
        setOriginalDeliveryPhone("");
      }
    } catch (error) {
      console.error("❌ Error loading delivery phone:", error);
      setDeliveryPhone("");
      setOriginalDeliveryPhone("");
    } finally {
      setDeliveryPhoneLoading(false);
    }
  };

  const handleSaveDeliveryPhone = async () => {
    try {
      setDeliveryPhoneSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "deliveryPhone");

      const dataToSave = {
        phone: deliveryPhone.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalDeliveryPhone(deliveryPhone.trim());
      alert("Success: " + "Delivery phone saved successfully");
    } catch (error) {
      console.error("❌ Error saving delivery phone:", error);
      alert("Error: " +
        `Failed to save delivery phone: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setDeliveryPhoneSaving(false);
    }
  };

  const loadDeliveryCallingPhone = async () => {
    try {
      setDeliveryCallingPhoneLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "deliveryCallingPhone");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const phone = formatPhoneNumber(data.phone || "");
        setDeliveryCallingPhone(phone);
        setOriginalDeliveryCallingPhone(phone);
      } else {
        setDeliveryCallingPhone("");
        setOriginalDeliveryCallingPhone("");
      }
    } catch (error) {
      console.error("❌ Error loading delivery calling phone:", error);
      setDeliveryCallingPhone("");
      setOriginalDeliveryCallingPhone("");
    } finally {
      setDeliveryCallingPhoneLoading(false);
    }
  };

  const handleSaveDeliveryCallingPhone = async () => {
    try {
      setDeliveryCallingPhoneSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "deliveryCallingPhone");

      const dataToSave = {
        phone: deliveryCallingPhone.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalDeliveryCallingPhone(deliveryCallingPhone.trim());
      alert("Success: " + "Delivery calling phone saved successfully");
    } catch (error) {
      console.error("❌ Error saving delivery calling phone:", error);
      alert("Error: " +
        `Failed to save delivery calling phone: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setDeliveryCallingPhoneSaving(false);
    }
  };

  const loadStoreName = async () => {
    try {
      setStoreNameLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "storeName");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const name = data.name || "";
        setStoreName(name);
        setOriginalStoreName(name);
      } else {
        setStoreName("");
        setOriginalStoreName("");
      }
    } catch (error) {
      console.error("❌ Error loading store name:", error);
      setStoreName("");
      setOriginalStoreName("");
    } finally {
      setStoreNameLoading(false);
    }
  };

  const loadLegalStoreName = async () => {
    try {
      setLegalStoreNameLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "legalStoreName");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const name = data.name || "";
        setLegalStoreName(name);
        setOriginalLegalStoreName(name);
      } else {
        setLegalStoreName("");
        setOriginalLegalStoreName("");
      }
    } catch (error) {
      console.error("❌ Error loading legal store name:", error);
      setLegalStoreName("");
      setOriginalLegalStoreName("");
    } finally {
      setLegalStoreNameLoading(false);
    }
  };

  const handleSaveLegalStoreName = async () => {
    if (!legalStoreName.trim()) {
      alert("Error: " + "Legal store name cannot be empty");
      return;
    }

    try {
      setLegalStoreNameSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "legalStoreName");

      const dataToSave = {
        name: legalStoreName.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalLegalStoreName(legalStoreName.trim());
      alert("Success: " + "Legal store name saved successfully");
    } catch (error) {
      console.error("❌ Error saving legal store name:", error);
      alert("Error: " +
        `Failed to save legal store name: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setLegalStoreNameSaving(false);
    }
  };

  const loadLegalStoreAddress = async () => {
    try {
      setLegalStoreAddressLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "legalStoreAddress");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const address = data.address || "";
        setLegalStoreAddress(address);
        setOriginalLegalStoreAddress(address);
      } else {
        setLegalStoreAddress("");
        setOriginalLegalStoreAddress("");
      }
    } catch (error) {
      console.error("❌ Error loading legal store address:", error);
      setLegalStoreAddress("");
      setOriginalLegalStoreAddress("");
    } finally {
      setLegalStoreAddressLoading(false);
    }
  };

  const handleSaveLegalStoreAddress = async () => {
    if (!legalStoreAddress.trim()) {
      alert("Error: " + "Legal store address cannot be empty");
      return;
    }

    try {
      setLegalStoreAddressSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "legalStoreAddress");

      const dataToSave = {
        address: legalStoreAddress.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalLegalStoreAddress(legalStoreAddress.trim());
      alert("Success: " + "Legal store address saved successfully");
    } catch (error) {
      console.error("❌ Error saving legal store address:", error);
      alert("Error: " +
        `Failed to save legal store address: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setLegalStoreAddressSaving(false);
    }
  };

  const loadCancellationPolicy = async () => {
    try {
      setCancellationPolicyLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "cancellationPolicy");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const policy = data.policy || "Our cancellation policy required 48 hours advance notice for a full refund, and a 24 hour advance notice for a 75% refund. All other cancellations only receive 50% refund.";
        setCancellationPolicy(policy);
      } else {
        setCancellationPolicy("Our cancellation policy required 48 hours advance notice for a full refund, and a 24 hour advance notice for a 75% refund. All other cancellations only receive 50% refund.");
      }
    } catch (error) {
      console.error("❌ Error loading cancellation policy:", error);
      setCancellationPolicy("Our cancellation policy required 48 hours advance notice for a full refund, and a 24 hour advance notice for a 75% refund. All other cancellations only receive 50% refund.");
    } finally {
      setCancellationPolicyLoading(false);
    }
  };

  const handleSaveCancellationPolicy = async () => {
    if (!cancellationPolicy.trim()) {
      alert("Error: " + "Cancellation policy cannot be empty");
      return;
    }

    try {
      setCancellationPolicySaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "cancellationPolicy");

      const dataToSave = {
        policy: cancellationPolicy.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      alert("Success: " + "Cancellation policy saved successfully");
    } catch (error) {
      console.error("❌ Error saving cancellation policy:", error);
      alert("Error: " +
        `Failed to save cancellation policy: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setCancellationPolicySaving(false);
    }
  };

  const loadAppHeaderTitle = async () => {
    try {
      setAppHeaderTitleLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "appHeaderTitle");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const title = data.title || "";
        setAppHeaderTitle(title);
        setOriginalAppHeaderTitle(title);
      } else {
        setAppHeaderTitle("");
        setOriginalAppHeaderTitle("");
      }
    } catch (error) {
      console.error("❌ Error loading app header title:", error);
      setAppHeaderTitle("");
      setOriginalAppHeaderTitle("");
    } finally {
      setAppHeaderTitleLoading(false);
    }
  };

  const handleSaveAppHeaderTitle = async () => {
    try {
      setAppHeaderTitleSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "appHeaderTitle");

      const dataToSave = {
        title: appHeaderTitle.trim() || "Product Rentals",
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalAppHeaderTitle(appHeaderTitle.trim());
      alert("Success: " + "App header title saved successfully");
    } catch (error) {
      console.error("❌ Error saving app header title:", error);
      alert("Error: " +
        `Failed to save app header title: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setAppHeaderTitleSaving(false);
    }
  };

  const handleSaveStoreName = async () => {
    try {
      setStoreNameSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "storeName");

      const dataToSave = {
        name: storeName.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalStoreName(storeName.trim());
      alert("Success: " + "Store name saved successfully");
    } catch (error) {
      console.error("❌ Error saving store name:", error);
      alert("Error: " +
        `Failed to save store name: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setStoreNameSaving(false);
    }
  };

  const loadStoreLogo = async () => {
    try {
      setStoreLogoLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "storeLogo");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setStoreLogo(data.url || "");
      } else {
        setStoreLogo("");
      }
    } catch (error) {
      console.error("❌ Error loading store logo:", error);
      setStoreLogo("");
    } finally {
      setStoreLogoLoading(false);
    }
  };

  const handleStoreLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try{
      setUploadingStoreLogo(true);
      const timestamp = Date.now();
      const fileName = `store_logo/${timestamp}_${file.name}`;
      const downloadURL = await uploadFile(fileName, file, {
        contentType: file.type,
        customMetadata: {
          uploadedAt: new Date().toISOString(),
          originalName: file.name,
        },
      });

      // Save URL to settings
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "storeLogo");
      await setDoc(
        settingsRef,
        {
          url: downloadURL,
          fileName: fileName,
          originalName: file.name,
          updatedAt: Timestamp.now(),
        },
        { merge: true }
      );

      setStoreLogo(downloadURL);
      alert("Success: " + "Store logo uploaded successfully");
    } catch (error) {
      console.error("❌ Error uploading store logo:", error);
      console.error("❌ Error details:", {
        message: error.message,
        code: error.code,
        stack: error.stack,
      });
      alert("Error: " +
        "Failed to upload logo: " + (error.message || "Unknown error")
      );
    } finally {
      setUploadingStoreLogo(false);
    }
  };

  const handleRemoveStoreLogo = async () => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "storeLogo");
      await setDoc(settingsRef, { url: "", updatedAt: Timestamp.now() }, { merge: true });
      setStoreLogo("");
      alert("Success: " + "Store logo removed");
    } catch (error) {
      console.error("❌ Error removing store logo:", error);
      alert("Error: " + "Failed to remove logo");
    }
  };

  const loadCustomerBlurb = async () => {
    try {
      setCustomerBlurbLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "customerBlurb");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const text = data.text || "";
        setCustomerBlurb(text);
        setOriginalCustomerBlurb(text);
      } else {
        setCustomerBlurb("");
        setOriginalCustomerBlurb("");
      }
    } catch (error) {
      console.error("❌ Error loading customer blurb:", error);
      setCustomerBlurb("");
      setOriginalCustomerBlurb("");
    } finally {
      setCustomerBlurbLoading(false);
    }
  };

  const handleSaveCustomerBlurb = async () => {
    try {
      setCustomerBlurbSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "customerBlurb");

      const dataToSave = {
        text: customerBlurb.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalCustomerBlurb(customerBlurb.trim());
      alert("Success: " + "Customer blurb saved successfully");
    } catch (error) {
      console.error("❌ Error saving customer blurb:", error);
      alert("Error: " +
        `Failed to save customer blurb: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setCustomerBlurbSaving(false);
    }
  };

  // ==================== HOURS OF OPERATION FUNCTIONS ====================

  const loadHoursOfOperation = async () => {
    try {
      setHoursLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "hoursOfOperation");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setHoursOfOperation(data.hours || hoursOfOperation);
        setOriginalHoursOfOperation(JSON.parse(JSON.stringify(data.hours || hoursOfOperation)));
      } else {
        setOriginalHoursOfOperation(JSON.parse(JSON.stringify(hoursOfOperation)));
      }
    } catch (error) {
      console.error("❌ Error loading hours of operation:", error);
      setOriginalHoursOfOperation(JSON.parse(JSON.stringify(hoursOfOperation)));
    } finally {
      setHoursLoading(false);
    }
  };

  const handleSaveHoursOfOperation = async () => {
    try {
      setHoursSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "hoursOfOperation");

      const dataToSave = {
        hours: hoursOfOperation,
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalHoursOfOperation(JSON.parse(JSON.stringify(hoursOfOperation)));
      alert("Success: " + "Hours of operation saved successfully");
    } catch (error) {
      console.error("❌ Error saving hours of operation:", error);
      alert("Error: " +
        `Failed to save hours of operation: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setHoursSaving(false);
    }
  };

  const updateDayHours = (day, field, value) => {
    setHoursOfOperation({
      ...hoursOfOperation,
      [day]: {
        ...hoursOfOperation[day],
        [field]: value,
      },
    });
  };

  // ==================== IN-STORE PICKUP HOURS FUNCTIONS ====================

  const loadPickupHoursOfOperation = async () => {
    try {
      setPickupHoursLoading(true);
      const settingsPath = getSettingsPath();
      const hoursDoc = await getDoc(doc(db, settingsPath, "pickupHoursOfOperation"));
      
      if (hoursDoc.exists()) {
        const hours = hoursDoc.data().hours;
        setPickupHoursOfOperation(hours);
        setOriginalPickupHoursOfOperation(hours);
      }
    } catch (error) {
      console.error("❌ Error loading pickup hours of operation:", error);
    } finally {
      setPickupHoursLoading(false);
    }
  };

  const handleSavePickupHoursOfOperation = async () => {
    try {
      setPickupHoursSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "pickupHoursOfOperation");

      const dataToSave = {
        hours: pickupHoursOfOperation,
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalPickupHoursOfOperation(pickupHoursOfOperation);
      alert("Success: " + "Pickup/dropoff hours of operation saved successfully");
    } catch (error) {
      console.error("❌ Error saving pickup hours of operation:", error);
      alert("Error: " + `Failed to save pickup/dropoff hours: ${error.message || "Unknown error"}`);
    } finally {
      setPickupHoursSaving(false);
    }
  };

  const updatePickupDayHours = (day, field, value) => {
    setPickupHoursOfOperation({
      ...pickupHoursOfOperation,
      [day]: {
        ...pickupHoursOfOperation[day],
        [field]: value,
      },
    });
  };

  // ==================== CONFIRMATION HEADING FUNCTIONS ====================

  const loadConfirmationHeadings = async () => {
    try {
      setConfirmationHeadingTransportationLoading(true);
      setConfirmationHeadingInStorePickupLoading(true);

      const settingsPath = getSettingsPath();
      
      // Load Transportation heading
      const transportationDoc = await getDoc(doc(db, settingsPath, "confirmationHeadingTransportation"));
      if (transportationDoc.exists()) {
        const heading = transportationDoc.data().heading || "";
        setConfirmationHeadingTransportation(heading);
        setOriginalConfirmationHeadingTransportation(heading);
      }

      // Load In-Store Pickup heading
      const inStoreDoc = await getDoc(doc(db, settingsPath, "confirmationHeadingInStorePickup"));
      if (inStoreDoc.exists()) {
        const heading = inStoreDoc.data().heading || "";
        setConfirmationHeadingInStorePickup(heading);
        setOriginalConfirmationHeadingInStorePickup(heading);
      }
    } catch (error) {
      console.error("❌ Error loading confirmation headings:", error);
    } finally {
      setConfirmationHeadingTransportationLoading(false);
      setConfirmationHeadingInStorePickupLoading(false);
    }
  };

  const handleSaveConfirmationHeadingTransportation = async () => {
    try {
      setConfirmationHeadingTransportationSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "confirmationHeadingTransportation");

      const dataToSave = {
        heading: confirmationHeadingTransportation.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalConfirmationHeadingTransportation(confirmationHeadingTransportation.trim());
      alert("Success: " + "Confirmation heading for delivery/pickup saved successfully");
    } catch (error) {
      console.error("❌ Error saving confirmation heading:", error);
      alert("Error: " + `Failed to save confirmation heading: ${error.message || "Unknown error"}`);
    } finally {
      setConfirmationHeadingTransportationSaving(false);
    }
  };

  const handleSaveConfirmationHeadingInStorePickup = async () => {
    try {
      setConfirmationHeadingInStorePickupSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "confirmationHeadingInStorePickup");

      const dataToSave = {
        heading: confirmationHeadingInStorePickup.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalConfirmationHeadingInStorePickup(confirmationHeadingInStorePickup.trim());
      alert("Success: " + "Confirmation heading for in-store pickup saved successfully");
    } catch (error) {
      console.error("❌ Error saving confirmation heading:", error);
      alert("Error: " + `Failed to save confirmation heading: ${error.message || "Unknown error"}`);
    } finally {
      setConfirmationHeadingInStorePickupSaving(false);
    }
  };

  // ==================== PRODUCT TITLES FUNCTIONS ====================

  const loadProductTitles = async () => {
    try {
      setProduct1TitleLoading(true);
      setProduct2TitleLoading(true);

      const settingsPath = getSettingsPath();
      
      // Load Product 1 Title
      const product1Doc = await getDoc(doc(db, settingsPath, "product1Title"));
      if (product1Doc.exists()) {
        const title = product1Doc.data().title || "Bicycles";
        setProduct1Title(title);
        setOriginalProduct1Title(title);
      } else {
        setProduct1Title("Bicycles");
        setOriginalProduct1Title("Bicycles");
      }

      // Load Product 2 Title
      const product2Doc = await getDoc(doc(db, settingsPath, "product2Title"));
      if (product2Doc.exists()) {
        const title = product2Doc.data().title || "Add-Ons";
        setProduct2Title(title);
        setOriginalProduct2Title(title);
      } else {
        setProduct2Title("Add-Ons");
        setOriginalProduct2Title("Add-Ons");
      }
    } catch (error) {
      console.error("❌ Error loading product titles:", error);
      setProduct1Title("Bicycles");
      setOriginalProduct1Title("Bicycles");
      setProduct2Title("Add-Ons");
      setOriginalProduct2Title("Add-Ons");
    } finally {
      setProduct1TitleLoading(false);
      setProduct2TitleLoading(false);
    }
  };

  const handleSaveProduct1Title = async () => {
    try {
      setProduct1TitleSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "product1Title");

      const dataToSave = {
        title: product1Title.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalProduct1Title(product1Title.trim());
      alert("Success: " + "Product 1 title saved successfully");
    } catch (error) {
      console.error("❌ Error saving product 1 title:", error);
      alert("Error: " + `Failed to save product 1 title: ${error.message || "Unknown error"}`);
    } finally {
      setProduct1TitleSaving(false);
    }
  };

  const handleSaveProduct2Title = async () => {
    try {
      setProduct2TitleSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "product2Title");

      const dataToSave = {
        title: product2Title.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalProduct2Title(product2Title.trim());
      alert("Success: " + "Product 2 title saved successfully");
    } catch (error) {
      console.error("❌ Error saving product 2 title:", error);
      alert("Error: " + `Failed to save product 2 title: ${error.message || "Unknown error"}`);
    } finally {
      setProduct2TitleSaving(false);
    }
  };

  // ==================== EMAIL TEMPLATE FUNCTIONS ====================







  const loadEmailUniversalTemplate = async () => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "emailUniversalTemplateFields");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        // Replace "Manage Your Booking" with "View Your Booking" if present
        let buttonText = data.buttonText || "✏️ View Your Booking";
        let needsUpdate = false;
        if (buttonText && buttonText.includes("Manage Your Booking")) {
          buttonText = buttonText.replace(/Manage Your Booking/g, "View Your Booking");
          needsUpdate = true;
        }
        const fields = {
          headerSubtitle: data.headerSubtitle || "",
          greetingText: data.greetingText || "Great news! Your bike rental has been confirmed and payment received.",
          buttonText: buttonText,
          footerMessage: data.footerMessage || "",
          customInstructions: data.customInstructions || "",
        };
        setEmailUniversalFields(fields);
        setOriginalEmailUniversalFields(fields);
        
        // Auto-update the database if we fixed the button text
        if (needsUpdate) {
          const dataToSave = {
            ...fields,
            updatedAt: Timestamp.now(),
          };
          await setDoc(settingsRef, dataToSave, { merge: true });
          console.log("✅ Auto-updated email template: replaced 'Manage Your Booking' with 'View Your Booking'");
        }
      } else {
        const defaultFields = {
          headerSubtitle: "",
          greetingText: "Great news! Your bike rental has been confirmed and payment received.",
          buttonText: "✏️ View Your Booking",
          footerMessage: "",
          customInstructions: "",
        };
        setEmailUniversalFields(defaultFields);
        setOriginalEmailUniversalFields(defaultFields);
      }
    } catch (error) {
      console.error("❌ Error loading email universal template:", error);
    }
  };

  const handleSaveEmailUniversalTemplate = async (fields) => {
    try {
      setEmailTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "emailUniversalTemplateFields");

      const dataToSave = {
        ...fields,
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setEmailUniversalFields(fields);
      setOriginalEmailUniversalFields(fields);
      alert("Success: " + "Email universal template saved successfully");
    } catch (error) {
      console.error("❌ Error saving email universal template:", error);
      alert("Error: " +
        `Failed to save email template: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setEmailTemplateSaving(false);
    }
  };

  const handleSendTestEmail = async (templateType) => {
    try {
      setEmailTestSending(true);
      const functions = getFunctions();
      const sendTestEmail = httpsCallable(functions, "sendTestEmail");

      const result = await sendTestEmail({ templateType });

      if (result.data.success) {
        alert(`Test Email Sent! 📧\n\nA test ${templateType} confirmation email has been sent to ${result.data.sentTo}. Check your inbox!`);
      }
    } catch (error) {
      console.error("❌ Error sending test email:", error);
      alert("Error: " +
        `Failed to send test email: ${error.message || "Unknown error"}`
      );
    } finally {
      setEmailTestSending(false);
    }
  };

  // ==================== EMAIL TEMPLATE MIGRATION ====================
  
  const migrateEmailTemplates = async () => {
    try {
      const confirmed = window.confirm(
        "This will update ALL email templates:\n\n" +
        "1. Add 'Main Title' field to all templates\n" +
        "2. Add 'Stripe Refund Receipt Link' and 'Refund Amount' to refund templates\n" +
        "3. Update button text to 'View Your Booking' for refund templates\n\n" +
        "Continue?"
      );
      
      if (!confirmed) return;
      
      const settingsPath = getSettingsPath();
      let updatedCount = 0;
      let errorCount = 0;
      
      // List of all template documents to update
      const templates = [
        {
          docName: "emailUniversalTemplateFields",
          defaults: {
            mainTitle: "🚴 Booking Confirmed!",
            headerSubtitle: "",
            greetingText: "Great news! Your bike rental has been confirmed and payment received.",
            buttonText: "✏️ View Your Booking",
            footerMessage: "",
            customInstructions: "",
          }
        },
        {
          docName: "bookingEditTemplateFields",
          defaults: {
            mainTitle: "✏️ Booking Updated",
            headerSubtitle: "Changes have been made to your reservation",
            greetingText: "Your booking has been successfully updated. Review the changes below and contact us if you have any questions.",
            customInstructions: "",
            buttonText: "✏️ View Your Booking",
            footerMessage: "",
            stripeRefundReceiptLink: "",
            refundAmount: "",
          }
        },
        {
          docName: "refundOnlyEmailFields",
          defaults: {
            mainTitle: "💰 Refund Processed",
            headerSubtitle: "Your refund has been processed",
            greetingText: "We've processed a refund for your booking.",
            customMessage: "",
            buttonText: "✏️ View Your Booking",
            footerMessage: "",
            stripeRefundReceiptLink: "",
            refundAmount: "",
          }
        },
        {
          docName: "emailThankYouTemplateFields",
          defaults: {
            mainTitle: "🙏 Thank You!",
            headerSubtitle: "Thank you for choosing {storeName}!",
            greetingText: "Thank you for your rental! We hope you had a wonderful experience. We'd love to hear your feedback!",
            customInstructions: "",
            buttonText: "📋 View Your Booking",
            footerMessage: "",
          }
        },
        {
          docName: "cancellationRequestTemplateFields",
          defaults: {
            mainTitle: "🚫 Cancellation Request",
            headerSubtitle: "We received your cancellation request",
            greetingText: "We received a request to cancel your booking. To complete the cancellation, please click the button below and confirm.",
            warningText: "Cancelling your booking will remove it from the system and issue a refund according to our cancellation policy. This action cannot be undone.",
            buttonText: "🚫 Confirm Cancellation",
            footerMessage: "If you don't want to cancel, simply ignore this email.",
          }
        },
        {
          docName: "cancellationConfirmationEmailFields",
          defaults: {
            mainTitle: "🚫 Booking Cancelled",
            headerSubtitle: "We're sorry to see you go",
            greetingText: "Your booking has been successfully cancelled and your refund has been processed.",
            customInstructions: "",
            buttonText: "✏️ View Booking",
            footerMessage: "",
          }
        },
        {
          docName: "emailPenaltyTemplateFields",
          defaults: {
            mainTitle: "⚖️ Equipment Charges",
            headerSubtitle: "We hope you had a great rental!",
            greetingText: "Thank you for choosing {storeName}! We've completed your check-in and found it necessary to charge for some equipment-related items.",
            customInstructions: "We've uploaded photos and details to your check-in page so you can see what we found. You can view everything by clicking the button below.",
            buttonText: "📋 View Check-In Details",
            footerMessage: "If you have any questions about these charges, please don't hesitate to contact us. We appreciate your business!",
          }
        }
      ];
      
      for (const template of templates) {
        try {
          const templateRef = doc(db, settingsPath, template.docName);
          const templateDoc = await getDoc(templateRef);
          
          if (templateDoc.exists()) {
            const existingData = templateDoc.data();
            // Merge defaults with existing data, preserving existing values
            // Special handling for cancellation confirmation: always update buttonText from "Contact Us" to "View Booking"
            let buttonTextToUse = existingData.buttonText || template.defaults.buttonText;
            if (template.docName === "cancellationConfirmationEmailFields") {
              // Replace "Contact Us" variants with "View Booking"
              if (existingData.buttonText && (
                existingData.buttonText.includes("Contact Us") ||
                existingData.buttonText.includes("Contact") ||
                existingData.buttonText === "📞 Contact Us"
              )) {
                buttonTextToUse = "✏️ View Booking";
              }
            }

            // Replace {bookingId} with {customerBookingID} in all text fields
            const replaceBookingIdInText = (text) => {
              if (!text || typeof text !== 'string') return text || '';
              return text.replace(/{bookingId}/g, '{customerBookingID}');
            };

            // Helper to safely get field value, ensuring it's never undefined
            const getFieldValue = (existingValue, defaultValue) => {
              const value = existingValue !== undefined && existingValue !== null ? existingValue : (defaultValue !== undefined ? defaultValue : '');
              return replaceBookingIdInText(value || '');
            };

            // Build updated data object, only including fields that exist in defaults or existing data
            const updatedData = {
              updatedAt: Timestamp.now(),
            };
            
            // Always include these fields if they exist in defaults
            if (template.defaults.mainTitle !== undefined) {
              updatedData.mainTitle = getFieldValue(existingData.mainTitle, template.defaults.mainTitle);
            } else if (existingData.mainTitle !== undefined) {
              updatedData.mainTitle = replaceBookingIdInText(existingData.mainTitle);
            }

            if (template.defaults.headerSubtitle !== undefined) {
              updatedData.headerSubtitle = getFieldValue(existingData.headerSubtitle, template.defaults.headerSubtitle);
            } else if (existingData.headerSubtitle !== undefined) {
              updatedData.headerSubtitle = replaceBookingIdInText(existingData.headerSubtitle);
            }

            if (template.defaults.greetingText !== undefined) {
              updatedData.greetingText = getFieldValue(existingData.greetingText, template.defaults.greetingText);
            } else if (existingData.greetingText !== undefined) {
              updatedData.greetingText = replaceBookingIdInText(existingData.greetingText);
            }

            updatedData.buttonText = replaceBookingIdInText(buttonTextToUse);

            if (template.defaults.footerMessage !== undefined) {
              updatedData.footerMessage = getFieldValue(existingData.footerMessage, template.defaults.footerMessage);
            } else if (existingData.footerMessage !== undefined) {
              updatedData.footerMessage = replaceBookingIdInText(existingData.footerMessage);
            }

            // Only add optional fields if they exist in defaults or existing data
            if (template.defaults.customInstructions !== undefined) {
              updatedData.customInstructions = getFieldValue(existingData.customInstructions, template.defaults.customInstructions);
            } else if (existingData.customInstructions !== undefined) {
              updatedData.customInstructions = replaceBookingIdInText(existingData.customInstructions);
            }

            if (template.defaults.customMessage !== undefined) {
              updatedData.customMessage = getFieldValue(existingData.customMessage, template.defaults.customMessage);
            } else if (existingData.customMessage !== undefined) {
              updatedData.customMessage = replaceBookingIdInText(existingData.customMessage);
            }

            if (template.defaults.warningText !== undefined) {
              updatedData.warningText = getFieldValue(existingData.warningText, template.defaults.warningText);
            } else if (existingData.warningText !== undefined) {
              updatedData.warningText = replaceBookingIdInText(existingData.warningText);
            }

            if (template.defaults.stripeRefundReceiptLink !== undefined) {
              updatedData.stripeRefundReceiptLink = existingData.stripeRefundReceiptLink || template.defaults.stripeRefundReceiptLink || '';
            } else if (existingData.stripeRefundReceiptLink !== undefined) {
              updatedData.stripeRefundReceiptLink = existingData.stripeRefundReceiptLink;
            }

            if (template.defaults.refundAmount !== undefined) {
              updatedData.refundAmount = existingData.refundAmount || template.defaults.refundAmount || '';
            } else if (existingData.refundAmount !== undefined) {
              updatedData.refundAmount = existingData.refundAmount;
            }

            // If there's a bookingId field, rename it to customerBookingID
            if (existingData.bookingId || existingData.customerBookingID) {
              updatedData.customerBookingID = existingData.customerBookingID || existingData.bookingId || '';
            }

            // Preserve all other existing fields that aren't being updated
            Object.keys(existingData).forEach(key => {
              if (!updatedData.hasOwnProperty(key) && key !== 'bookingId') {
                updatedData[key] = existingData[key];
              }
            });

            await setDoc(templateRef, updatedData, { merge: true });
            console.log(`✅ Updated ${template.docName}`);
            updatedCount++;
          } else {
            // Create new template with defaults
            await setDoc(templateRef, {
              ...template.defaults,
              updatedAt: Timestamp.now(),
            }, { merge: true });
            console.log(`✅ Created ${template.docName}`);
            updatedCount++;
          }
        } catch (error) {
          console.error(`❌ Error updating ${template.docName}:`, error);
          errorCount++;
        }
      }
      
      alert(
        `Migration Complete!\n\n` +
        `✅ Updated/Created: ${updatedCount} templates\n` +
        `❌ Errors: ${errorCount}\n\n` +
        `All templates now have:\n` +
        `- Main Title field\n` +
        `- Refund templates have Stripe receipt link and refund amount fields\n` +
        `- Refund templates have "View Your Booking" button text\n` +
        `- Cancellation confirmation template has "View Booking" button text\n` +
        `- All {bookingId} references replaced with {customerBookingID}`
      );
      
      // Reload all templates to show updated values
      await loadEmailUniversalTemplate();
      await loadPartialRefundEmailTemplate();
      await loadRefundOnlyEmailTemplate();
      await loadThankYouEmailTemplate();
      await loadCancellationRequestTemplate();
      await loadCancellationEmailTemplate();
      await loadPenaltyEmailTemplate();
      
    } catch (error) {
      console.error("❌ Migration failed:", error);
      alert(`Migration failed: ${error.message}`);
    }
  };

  // ==================== SMS TEMPLATE FUNCTIONS ====================



  // ==================== WAIVER TEMPLATE FUNCTIONS ====================

  const loadWaiverTemplate = async () => {
    try {
      setWaiverTemplateLoading(true);
      const settingsPath = getSettingsPath();
      const templateRef = doc(db, settingsPath, "waiverTemplate");
      const templateDoc = await getDoc(templateRef);

      if (templateDoc.exists()) {
        const data = templateDoc.data();
        setWaiverTemplate(data);
      } else {
        // Create default waiver template if none exists
        const defaultTemplate = {
          sections: [
            {
              id: "assumption-of-risk",
              title: "ASSUMPTION OF RISK",
              content: "I understand that renting and using equipment from <storeName> involves inherent risks including, but not limited to, accidents, injuries, property damage, or equipment malfunction. I voluntarily assume all such risks associated with my use of the rented equipment.",
              requiresInitial: true,
              order: 1,
              visible: true
            },
            {
              id: "liability-waiver",
              title: "RELEASE OF LIABILITY",
              content: "In consideration of being permitted to rent equipment from <storeName>, I hereby release, waive, and discharge <storeName>, its owners, employees, agents, and representatives from any and all liability, claims, demands, or causes of action arising from personal injury, property damage, or any other loss sustained while using the rented equipment, whether caused by negligence or otherwise.",
              requiresInitial: true,
              order: 2,
              visible: true
            },
            {
              id: "equipment-responsibility",
              title: "EQUIPMENT CARE AND RESPONSIBILITY",
              content: "I acknowledge that I am responsible for the rented equipment from the time of pickup until its return to <storeName>. I agree to use the equipment in a safe and responsible manner and will be liable for any damage, loss, or theft of the equipment during the rental period. I will return the equipment in the same condition as received, normal wear and tear excepted.",
              requiresInitial: true,
              order: 3,
              visible: true
            },
            {
              id: "indemnification",
              title: "INDEMNIFICATION",
              content: "I agree to indemnify, defend, and hold harmless <storeName> from and against any and all claims, liabilities, damages, losses, or expenses (including attorney's fees) arising out of or resulting from my use of the rented equipment or any actions taken by me during the rental period.",
              requiresInitial: false,
              order: 4,
              visible: true
            }
          ],
          finalSignatureSection: {
            title: "FULL SIGNATURE",
            content: "By signing below, I acknowledge that I have read, understood, and agree to all terms and conditions contained in this Liability Waiver and Release. I understand that I am giving up substantial rights, including my right to sue. I confirm that I am at least 18 years of age and am legally competent to sign this agreement.",
            requiresTypedName: true,
            requiresHomeAddress: true,
            requiresSignature: true
          },
          lastUpdated: new Date().toISOString()
        };
        
        // Save default template
        await setDoc(templateRef, defaultTemplate, { merge: true });
        setWaiverTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error("❌ Error loading waiver template:", error);
      alert("Error loading waiver template: " + error.message);
    } finally {
      setWaiverTemplateLoading(false);
    }
  };

  const handleSaveWaiverTemplate = async (templateData) => {
    try {
      setWaiverTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const templateRef = doc(db, settingsPath, "waiverTemplate");

      const dataToSave = {
        ...templateData,
        lastUpdated: new Date().toISOString(),
      };

      await setDoc(templateRef, dataToSave, { merge: true });
      setWaiverTemplate(dataToSave);
      alert("Success: Waiver template saved successfully!");
    } catch (error) {
      console.error("❌ Error saving waiver template:", error);
      throw error; // Let the component handle the error
    } finally {
      setWaiverTemplateSaving(false);
    }
  };

  const loadTransferWaiverTemplate = async () => {
    try {
      setTransferWaiverTemplateLoading(true);
      const settingsPath = getSettingsPath();
      const templateRef = doc(db, settingsPath, "transferWaiverTemplate");
      const templateDoc = await getDoc(templateRef);

      if (templateDoc.exists()) {
        setTransferWaiverTemplate(templateDoc.data());
      } else {
        // Create default transfer waiver template
        const defaultTemplate = {
          sections: [
            {
              id: "assumption-of-risk",
              title: "ASSUMPTION OF RISK",
              content: "I understand that renting and using equipment from <storeName> involves inherent risks including, but not limited to, accidents, injuries, property damage, or equipment malfunction. I voluntarily assume all such risks associated with my use of the rented equipment.",
              requiresInitial: true,
              order: 1,
              visible: true
            },
            {
              id: "liability-waiver",
              title: "RELEASE OF LIABILITY",
              content: "In consideration of being permitted to rent equipment from <storeName>, I hereby release, waive, and discharge <storeName>, its owners, employees, agents, and representatives from any and all liability, claims, demands, or causes of action arising from personal injury, property damage, or any other loss sustained while using the rented equipment, whether caused by negligence or otherwise.",
              requiresInitial: true,
              order: 2,
              visible: true
            },
            {
              id: "equipment-responsibility",
              title: "EQUIPMENT CARE AND RESPONSIBILITY",
              content: "I acknowledge that I am responsible for the rented equipment from the time of pickup until its return to <storeName>. I agree to use the equipment in a safe and responsible manner and will be liable for any damage, loss, or theft of the equipment during the rental period. I will return the equipment in the same condition as received, normal wear and tear excepted.",
              requiresInitial: true,
              order: 3,
              visible: true
            },
            {
              id: "indemnification",
              title: "INDEMNIFICATION",
              content: "I agree to indemnify, defend, and hold harmless <storeName> from and against any and all claims, liabilities, damages, losses, or expenses (including attorney's fees) arising out of or resulting from my use of the rented equipment or any actions taken by me during the rental period.",
              requiresInitial: false,
              order: 4,
              visible: true
            }
          ],
          finalSignatureSection: {
            title: "FULL SIGNATURE",
            content: "By signing below, I acknowledge that I have read, understood, and agree to all terms and conditions contained in this Liability Waiver and Release. I understand that I am giving up substantial rights, including my right to sue. I confirm that I am at least 18 years of age and am legally competent to sign this agreement.",
            requiresTypedName: true,
            requiresHomeAddress: true,
            requiresSignature: true
          },
          lastUpdated: new Date().toISOString()
        };
        
        // Save default template
        await setDoc(templateRef, defaultTemplate, { merge: true });
        setTransferWaiverTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error("❌ Error loading transfer waiver template:", error);
      alert("Error loading transfer waiver template: " + error.message);
    } finally {
      setTransferWaiverTemplateLoading(false);
    }
  };

  const handleSaveTransferWaiverTemplate = async (templateData) => {
    try {
      setTransferWaiverTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const templateRef = doc(db, settingsPath, "transferWaiverTemplate");

      const dataToSave = {
        ...templateData,
        lastUpdated: new Date().toISOString(),
      };

      await setDoc(templateRef, dataToSave, { merge: true });
      setTransferWaiverTemplate(dataToSave);
      alert("Success: Transfer waiver template saved successfully!");
    } catch (error) {
      console.error("❌ Error saving transfer waiver template:", error);
      throw error; // Let the component handle the error
    } finally {
      setTransferWaiverTemplateSaving(false);
    }
  };

  // ==================== SMS TEMPLATE FUNCTIONS ====================

  const loadSmsUniversalTemplate = async () => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "smsUniversalTemplate");
      const settingsDoc = await getDoc(settingsRef);

      const defaultTemplate = "🚴 Hi {customerName}! Your booking is confirmed. Your PIN is {bookingPin}. View details: {bookingUrl}";
      
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const template = data.template || defaultTemplate;
        setSmsUniversalTemplate(template);
        setOriginalSmsUniversalTemplate(template);
      } else {
        setSmsUniversalTemplate(defaultTemplate);
        setOriginalSmsUniversalTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error("❌ Error loading SMS universal template:", error);
      const defaultTemplate = "🚴 Hi {customerName}! Your booking is confirmed. Your PIN is {bookingPin}. View details: {bookingUrl}";
      setSmsUniversalTemplate(defaultTemplate);
      setOriginalSmsUniversalTemplate(defaultTemplate);
    }
  };

  const handleSaveSmsUniversalTemplate = async () => {
    try {
      setSmsTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "smsUniversalTemplate");

      const dataToSave = {
        template: smsUniversalTemplate.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      alert("Success: " + "SMS universal template saved successfully");

      await loadSmsUniversalTemplate();
    } catch (error) {
      console.error("❌ Error saving SMS universal template:", error);
      alert("Error: " +
        `Failed to save SMS template: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setSmsTemplateSaving(false);
    }
  };

  const loadSmsDayBeforeDeliveryTemplate = async () => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "smsDayBeforeDeliveryTemplate");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSmsDayBeforeDeliveryTemplate(data.template || "");
        setOriginalSmsDayBeforeDeliveryTemplate(data.template || "");
      } else {
        const defaultTemplate =
          "🚴 Hi {customerName}! Reminder: Your bike delivery is TOMORROW between 11 AM-3 PM. We'll text you when we're on the way! View booking: {bookingUrl}";
        setSmsDayBeforeDeliveryTemplate(defaultTemplate);
        setOriginalSmsDayBeforeDeliveryTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error(
        "❌ Error loading SMS day-before delivery template:",
        error
      );
      setSmsDayBeforeDeliveryTemplate("");
      setOriginalSmsDayBeforeDeliveryTemplate("");
    }
  };

  const loadSmsDayOfDeliveryTemplate = async () => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "smsDayOfDeliveryTemplate");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSmsDayOfDeliveryTemplate(data.template || "");
        setOriginalSmsDayOfDeliveryTemplate(data.template || "");
      } else {
        const defaultTemplate =
          "🚴 Hi {customerName}! Your bike delivery is scheduled for TODAY between 11 AM-3 PM. Questions? Text {deliveryPhone}. View booking: {bookingUrl}";
        setSmsDayOfDeliveryTemplate(defaultTemplate);
        setOriginalSmsDayOfDeliveryTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error("❌ Error loading SMS day-of delivery template:", error);
      setSmsDayOfDeliveryTemplate("");
      setOriginalSmsDayOfDeliveryTemplate("");
    }
  };

  const loadSmsDayBeforePickupTemplate = async () => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "smsDayBeforePickupTemplate");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSmsDayBeforePickupTemplate(data.template || "");
        setOriginalSmsDayBeforePickupTemplate(data.template || "");
      } else {
        const defaultTemplate =
          "🚴 Hi {customerName}! Reminder: We'll pick up your bikes TOMORROW. Please have them ready. Questions? Call {storePhone}. View booking: {bookingUrl}";
        setSmsDayBeforePickupTemplate(defaultTemplate);
        setOriginalSmsDayBeforePickupTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error("❌ Error loading SMS day-before pickup template:", error);
      setSmsDayBeforePickupTemplate("");
      setOriginalSmsDayBeforePickupTemplate("");
    }
  };

  const loadSmsDayOfPickupTemplate = async () => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "smsDayOfPickupTemplate");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSmsDayOfPickupTemplate(data.template || "");
        setOriginalSmsDayOfPickupTemplate(data.template || "");
      } else {
        const defaultTemplate =
          "🚴 Hi {customerName}! We're picking up your bikes TODAY between 11 AM-3 PM. Please have them ready. Questions? Text {deliveryPhone}. Thanks!";
        setSmsDayOfPickupTemplate(defaultTemplate);
        setOriginalSmsDayOfPickupTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error("❌ Error loading SMS day-of pickup template:", error);
      setSmsDayOfPickupTemplate("");
      setOriginalSmsDayOfPickupTemplate("");
    }
  };

  const loadSmsDayOfInStoreStartTemplate = async () => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "smsDayOfInStoreStartTemplate");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSmsDayOfInStoreStartTemplate(data.template || "");
        setOriginalSmsDayOfInStoreStartTemplate(data.template || "");
      } else {
        const defaultTemplate =
          "🚴 Hi {customerName}! Your bike rental starts TODAY! Pick up your bikes at our shop. Hours: M-F 11 AM-6 PM, Sat-Sun 11 AM-4 PM. {storePhone}";
        setSmsDayOfInStoreStartTemplate(defaultTemplate);
        setOriginalSmsDayOfInStoreStartTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error(
        "❌ Error loading SMS day-of in-store start template:",
        error
      );
      setSmsDayOfInStoreStartTemplate("");
      setOriginalSmsDayOfInStoreStartTemplate("");
    }
  };

  const loadSmsDayOfInStoreReturnTemplate = async () => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "smsDayOfInStoreReturnTemplate");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSmsDayOfInStoreReturnTemplate(data.template || "");
        setOriginalSmsDayOfInStoreReturnTemplate(data.template || "");
      } else {
        const defaultTemplate =
          "🚴 Hi {customerName}! Reminder: Today is your LAST DAY for your bike rental. Please return bikes to our shop by closing time. Store hours: M-F 11 AM-6 PM, Sat-Sun 11 AM-4 PM. {storePhone}";
        setSmsDayOfInStoreReturnTemplate(defaultTemplate);
        setOriginalSmsDayOfInStoreReturnTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error(
        "❌ Error loading SMS day-of in-store return template:",
        error
      );
      setSmsDayOfInStoreReturnTemplate("");
      setOriginalSmsDayOfInStoreReturnTemplate("");
    }
  };

  const handleSaveSmsHomePickupTemplate = async () => {
    try {
      setSmsTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "smsHomePickupTemplate");

      const dataToSave = {
        template: smsHomePickupTemplate.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      alert("Success: " + "SMS home pickup template saved successfully");

      await loadSmsHomePickupTemplate();
    } catch (error) {
      console.error("❌ Error saving SMS home pickup template:", error);
      alert("Error: " +
        `Failed to save SMS template: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setSmsTemplateSaving(false);
    }
  };


  const handleSaveSmsDayBeforeDeliveryTemplate = async () => {
    try {
      setSmsTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "smsDayBeforeDeliveryTemplate");

      const dataToSave = {
        template: smsDayBeforeDeliveryTemplate.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      alert("Success: " +
        "SMS day-before delivery template saved successfully"
      );

      await loadSmsDayBeforeDeliveryTemplate();
    } catch (error) {
      console.error("❌ Error saving SMS day-before delivery template:", error);
      alert("Error: " +
        `Failed to save SMS template: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setSmsTemplateSaving(false);
    }
  };

  const handleSaveSmsDayOfDeliveryTemplate = async () => {
    try {
      setSmsTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "smsDayOfDeliveryTemplate");

      const dataToSave = {
        template: smsDayOfDeliveryTemplate.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      alert("Success: " + "SMS day-of delivery template saved successfully");

      await loadSmsDayOfDeliveryTemplate();
    } catch (error) {
      console.error("❌ Error saving SMS day-of delivery template:", error);
      alert("Error: " +
        `Failed to save SMS template: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setSmsTemplateSaving(false);
    }
  };

  const handleSaveSmsDayBeforePickupTemplate = async () => {
    try {
      setSmsTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "smsDayBeforePickupTemplate");

      const dataToSave = {
        template: smsDayBeforePickupTemplate.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      alert("Success: " +
        "SMS day-before pickup template saved successfully"
      );

      await loadSmsDayBeforePickupTemplate();
    } catch (error) {
      console.error("❌ Error saving SMS day-before pickup template:", error);
      alert("Error: " +
        `Failed to save SMS template: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setSmsTemplateSaving(false);
    }
  };

  const handleSaveSmsDayOfPickupTemplate = async () => {
    try {
      setSmsTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "smsDayOfPickupTemplate");

      const dataToSave = {
        template: smsDayOfPickupTemplate.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      alert("Success: " + "SMS day-of pickup template saved successfully");

      await loadSmsDayOfPickupTemplate();
    } catch (error) {
      console.error("❌ Error saving SMS day-of pickup template:", error);
      alert("Error: " +
        `Failed to save SMS template: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setSmsTemplateSaving(false);
    }
  };

  const handleSaveSmsDayOfInStoreStartTemplate = async () => {
    try {
      setSmsTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "smsDayOfInStoreStartTemplate");

      const dataToSave = {
        template: smsDayOfInStoreStartTemplate.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      alert("Success: " +
        "SMS day-of in-store start template saved successfully"
      );

      await loadSmsDayOfInStoreStartTemplate();
    } catch (error) {
      console.error(
        "❌ Error saving SMS day-of in-store start template:",
        error
      );
      alert("Error: " +
        `Failed to save SMS template: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setSmsTemplateSaving(false);
    }
  };

  const handleSaveSmsDayOfInStoreReturnTemplate = async () => {
    try {
      setSmsTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "smsDayOfInStoreReturnTemplate");

      const dataToSave = {
        template: smsDayOfInStoreReturnTemplate.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      alert("Success: " +
        "SMS day-of in-store return template saved successfully"
      );

      await loadSmsDayOfInStoreReturnTemplate();
    } catch (error) {
      console.error(
        "❌ Error saving SMS day-of in-store return template:",
        error
      );
      alert("Error: " +
        `Failed to save SMS template: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setSmsTemplateSaving(false);
    }
  };

  // ==================== CANCELLATION TEMPLATE FUNCTIONS ====================

  const loadCancellationEmailTemplate = async () => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "cancellationConfirmationEmailFields");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setEmailCancellationConfirmationFields(data);
        setOriginalEmailCancellationConfirmationFields(data);
      } else {
        // Set default fields
        const defaultFields = {
          headerSubtitle: "We're sorry to see you go",
          greetingText: "Your booking has been successfully cancelled and your refund has been processed.",
          customInstructions: "Your refund will be returned to your original payment method within 5-10 business days. You'll receive a confirmation email once the refund is completed.",
          buttonText: "✏️ View Booking",
          footerMessage: "We hope to serve you again in the future!",
        };
        setEmailCancellationConfirmationFields(defaultFields);
        setOriginalEmailCancellationConfirmationFields(defaultFields);
      }
      
      // Keep legacy subject/body for backward compatibility (if needed)
      const legacyRef = doc(db, settingsPath, "cancellationConfirmationEmailTemplate");
      const legacyDoc = await getDoc(legacyRef);
      if (legacyDoc.exists()) {
        const data = legacyDoc.data();
        setCancellationEmailSubject(data.subject || "");
        setCancellationEmailBody(data.body || "");
        setOriginalCancellationEmailSubject(data.subject || "");
        setOriginalCancellationEmailBody(data.body || "");
      } else {
        const defaultSubject = "{storeName} - Booking Cancellation Confirmed";
        const defaultBody = ""; // Will use default from cloud function
        setCancellationEmailSubject(defaultSubject);
        setCancellationEmailBody(defaultBody);
        setOriginalCancellationEmailSubject(defaultSubject);
        setOriginalCancellationEmailBody(defaultBody);
      }
    } catch (error) {
      console.error("❌ Error loading cancellation email template:", error);
    }
  };

  const handleSaveCancellationEmailTemplate = async (updatedFields) => {
    try {
      setEmailTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "cancellationConfirmationEmailFields");

      const dataToSave = {
        ...updatedFields,
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setEmailCancellationConfirmationFields(updatedFields);
      setOriginalEmailCancellationConfirmationFields(updatedFields);
      alert("Success: " + "Cancellation confirmation email template saved successfully");
      return true;
    } catch (error) {
      console.error("❌ Error saving cancellation confirmation email template:", error);
      alert("Error: " + `Failed to save template: ${error.message || error.code || "Unknown error"}`);
      return false;
    } finally {
      setEmailTemplateSaving(false);
    }
  };

  const loadCancellationSmsTemplate = async () => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "cancellationConfirmationSmsTemplate");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setCancellationSmsTemplate(data.template || "");
        setOriginalCancellationSmsTemplate(data.template || "");
      } else {
        const defaultTemplate = "🚫 Hi {customerName}! Your {storeName} booking has been cancelled. You'll receive a {refundPercentage}% refund (${refundAmount}) within 5-10 business days.";
        setCancellationSmsTemplate(defaultTemplate);
        setOriginalCancellationSmsTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error("❌ Error loading cancellation SMS template:", error);
    }
  };

  const handleSaveCancellationSmsTemplate = async () => {
    try {
      setSmsTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "cancellationConfirmationSmsTemplate");

      const dataToSave = {
        template: cancellationSmsTemplate.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalCancellationSmsTemplate(cancellationSmsTemplate);
      alert("Success: " + "Cancellation SMS template saved successfully");
    } catch (error) {
      console.error("❌ Error saving cancellation SMS template:", error);
      alert("Error: " + `Failed to save template: ${error.message || error.code || "Unknown error"}`);
    } finally {
      setSmsTemplateSaving(false);
    }
  };

  const handleSendTestSMS = async (templateType) => {
    try {
      setSmsTestSending(true);
      const functions = getFunctions();
      const sendTestSMS = httpsCallable(functions, "sendTestSMS");

      const result = await sendTestSMS({ templateType });

      if (result.data.success) {
        alert(`Test SMS Sent! 📱\n\nA test ${templateType} confirmation SMS has been sent to ${result.data.sentTo}. Check your messages!`);
      }
    } catch (error) {
      console.error("❌ Error sending test SMS:", error);
      alert("Error: " +
        `Failed to send test SMS: ${error.message || "Unknown error"}`
      );
    } finally {
      setSmsTestSending(false);
    }
  };

  // ==================== PARTIAL REFUND TEMPLATE FUNCTIONS ====================

  const loadPartialRefundEmailTemplate = async () => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "bookingEditTemplateFields");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const fields = {
          mainTitle: data.mainTitle || "✏️ Booking Updated",
          headerSubtitle: data.headerSubtitle || "Changes have been made to your reservation",
          greetingText: data.greetingText || "Your booking has been successfully updated. Review the changes below and contact us if you have any questions.",
          customInstructions: data.customInstructions || "",
          buttonText: data.buttonText || "✏️ View Your Booking",
          footerMessage: data.footerMessage || "",
          stripeRefundReceiptLink: data.stripeRefundReceiptLink || "",
          refundAmount: data.refundAmount || "",
        };
        setEmailBookingEditFields(fields);
        setOriginalEmailBookingEditFields(fields);
      } else {
        const defaultFields = {
          mainTitle: "✏️ Booking Updated",
          headerSubtitle: "Changes have been made to your reservation",
          greetingText: "Your booking has been successfully updated. Review the changes below and contact us if you have any questions.",
          customInstructions: "",
          buttonText: "✏️ View Your Booking",
          footerMessage: "",
          stripeRefundReceiptLink: "",
          refundAmount: "",
        };
        setEmailBookingEditFields(defaultFields);
        setOriginalEmailBookingEditFields(defaultFields);
      }
    } catch (error) {
      console.error("❌ Error loading booking edit email template:", error);
    }
  };

  const handleSavePartialRefundEmailTemplate = async (fields) => {
    try {
      setEmailTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "bookingEditTemplateFields");

      const dataToSave = {
        ...fields,
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setEmailBookingEditFields(fields);
      setOriginalEmailBookingEditFields(fields);
      alert("Success: " + "Booking edit email template saved successfully");
    } catch (error) {
      console.error("❌ Error saving booking edit email template:", error);
      alert("Error: " + `Failed to save template: ${error.message || error.code || "Unknown error"}`);
    } finally {
      setEmailTemplateSaving(false);
    }
  };

  const loadPartialRefundSmsTemplate = async () => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "partialRefundSmsTemplate");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setPartialRefundSmsTemplate(data.template || "");
        setOriginalPartialRefundSmsTemplate(data.template || "");
      } else {
        const defaultTemplate = "✏️ Hi {customerName}! Your {storeName} booking has been updated. {itemsAddedRemoved} New balance: ${newBalance}. View details: {bookingUrl}";
        setPartialRefundSmsTemplate(defaultTemplate);
        setOriginalPartialRefundSmsTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error("❌ Error loading partial refund SMS template:", error);
    }
  };

  const handleSavePartialRefundSmsTemplate = async () => {
    try {
      setSmsTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "partialRefundSmsTemplate");

      const dataToSave = {
        template: partialRefundSmsTemplate.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalPartialRefundSmsTemplate(partialRefundSmsTemplate);
      alert("Success: " + "Booking edit SMS template saved successfully");
    } catch (error) {
      console.error("❌ Error saving partial refund SMS template:", error);
      alert("Error: " + `Failed to save template: ${error.message || error.code || "Unknown error"}`);
    } finally {
      setSmsTemplateSaving(false);
    }
  };

  // ==================== EDITED BOOKING TEMPLATES ====================

  // ==================== CANCELLATION REQUEST TEMPLATE ====================

  const loadCancellationRequestTemplate = async () => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "cancellationRequestTemplateFields");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const fields = {
          headerSubtitle: data.headerSubtitle || "We received your cancellation request",
          greetingText: data.greetingText || "We received a request to cancel your booking. To complete the cancellation, please click the button below and confirm.",
          warningText: data.warningText || "Cancelling your booking will remove it from the system and issue a refund according to our cancellation policy. This action cannot be undone.",
          buttonText: data.buttonText || "🚫 Confirm Cancellation",
          footerMessage: data.footerMessage || "If you don't want to cancel, simply ignore this email.",
        };
        setEmailCancellationRequestFields(fields);
        setOriginalEmailCancellationRequestFields(fields);
      } else {
        const defaultFields = {
          headerSubtitle: "We received your cancellation request",
          greetingText: "We received a request to cancel your booking. To complete the cancellation, please click the button below and confirm.",
          warningText: "Cancelling your booking will remove it from the system and issue a refund according to our cancellation policy. This action cannot be undone.",
          buttonText: "🚫 Confirm Cancellation",
          footerMessage: "If you don't want to cancel, simply ignore this email.",
        };
        setEmailCancellationRequestFields(defaultFields);
        setOriginalEmailCancellationRequestFields(defaultFields);
      }
    } catch (error) {
      console.error("❌ Error loading cancellation request template:", error);
    }
  };

  const handleSaveCancellationRequestTemplate = async (fields) => {
    try {
      setEmailTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "cancellationRequestTemplateFields");

      const dataToSave = {
        ...fields,
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setEmailCancellationRequestFields(fields);
      setOriginalEmailCancellationRequestFields(fields);
      alert("Success: " + "Cancellation request email template saved successfully");
    } catch (error) {
      console.error("❌ Error saving cancellation request email template:", error);
      alert("Error: " + `Failed to save template: ${error.message || error.code || "Unknown error"}`);
    } finally {
      setEmailTemplateSaving(false);
    }
  };

  // Load Refund Only Email Template
  const loadRefundOnlyEmailTemplate = async () => {
    try {
      setEmailTemplateLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "refundOnlyEmailFields");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const fields = {
          mainTitle: data.mainTitle || "💰 Refund Processed",
          headerSubtitle: data.headerSubtitle || "Your refund has been processed",
          greetingText: data.greetingText || "We've processed a refund for your booking.",
          customMessage: data.customMessage || "",
          buttonText: data.buttonText || "✏️ View Your Booking",
          footerMessage: data.footerMessage || "",
          stripeRefundReceiptLink: data.stripeRefundReceiptLink || "",
          refundAmount: data.refundAmount || "",
        };
        setEmailRefundOnlyFields(fields);
        setOriginalEmailRefundOnlyFields(fields);
      } else {
        const defaultFields = {
          mainTitle: "💰 Refund Processed",
          headerSubtitle: "Your refund has been processed",
          greetingText: "We've processed a refund for your booking.",
          customMessage: "",
          buttonText: "✏️ View Your Booking",
          footerMessage: "",
          stripeRefundReceiptLink: "",
          refundAmount: "",
        };
        setEmailRefundOnlyFields(defaultFields);
        setOriginalEmailRefundOnlyFields(defaultFields);
      }
    } catch (error) {
      console.error("❌ Error loading refund only email template:", error);
    } finally {
      setEmailTemplateLoading(false);
    }
  };

  const handleSaveRefundOnlyEmailTemplate = async (fields) => {
    try {
      setEmailTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "refundOnlyEmailFields");

      const dataToSave = {
        ...fields,
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setEmailRefundOnlyFields(fields);
      setOriginalEmailRefundOnlyFields(fields);
      alert("Success: " + "Refund only email template saved successfully");
    } catch (error) {
      console.error("❌ Error saving refund only email template:", error);
      alert("Error: " + `Failed to save template: ${error.message || error.code || "Unknown error"}`);
    } finally {
      setEmailTemplateSaving(false);
    }
  };

  // Load Refund Only SMS Template
  const loadRefundOnlySmsTemplate = async () => {
    try {
      setRefundOnlySmsLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "refundOnlySmsTemplate");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setRefundOnlySmsTemplate(data.template || "");
        setOriginalRefundOnlySmsTemplate(data.template || "");
      } else {
        const defaultTemplate = "💰 {storeName}: A refund of ${refundAmount} has been issued for booking {bookingId}. {refundReason}. Questions? Call {storePhone}";
        setRefundOnlySmsTemplate(defaultTemplate);
        setOriginalRefundOnlySmsTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error("❌ Error loading refund only SMS template:", error);
      setRefundOnlySmsTemplate("");
      setOriginalRefundOnlySmsTemplate("");
    } finally {
      setRefundOnlySmsLoading(false);
    }
  };

  const handleSaveRefundOnlySmsTemplate = async () => {
    if (refundOnlySmsTemplate.trim() === originalRefundOnlySmsTemplate.trim()) {
      alert("Info: " + "No changes detected");
      return;
    }

    try {
      setRefundOnlySmsSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "refundOnlySmsTemplate");

      const dataToSave = {
        template: refundOnlySmsTemplate.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalRefundOnlySmsTemplate(refundOnlySmsTemplate.trim());
      alert("Success: " + "Refund only SMS template saved successfully");
    } catch (error) {
      console.error("❌ Error saving refund only SMS template:", error);
      alert("Error: " + `Failed to save SMS template: ${error.message || error.code || "Unknown error"}`);
    } finally {
      setRefundOnlySmsSaving(false);
    }
  };

  // Load Thank You Email Template
  const loadThankYouEmailTemplate = async () => {
    try {
      setEmailTemplateLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "emailThankYouTemplateFields");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const fields = {
          headerSubtitle: data.headerSubtitle || "Thank you for choosing {storeName}!",
          greetingText: data.greetingText || "Thank you for your rental! We hope you had a wonderful experience. We'd love to hear your feedback!",
          customInstructions: data.customInstructions || "",
          buttonText: data.buttonText || "📋 View Your Booking",
          footerMessage: data.footerMessage || "",
        };
        setEmailThankYouFields(fields);
        setOriginalEmailThankYouFields(fields);
      } else {
        const defaultFields = {
          headerSubtitle: "Thank you for choosing {storeName}!",
          greetingText: "Thank you for your rental! We hope you had a wonderful experience. We'd love to hear your feedback!",
          customInstructions: "",
          buttonText: "📋 View Your Booking",
          footerMessage: "",
        };
        setEmailThankYouFields(defaultFields);
        setOriginalEmailThankYouFields(defaultFields);
      }
    } catch (error) {
      console.error("❌ Error loading thank you email template:", error);
    } finally {
      setEmailTemplateLoading(false);
    }
  };

  const handleSaveThankYouEmailTemplate = async (fields) => {
    try {
      setEmailTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "emailThankYouTemplateFields");

      const dataToSave = {
        ...fields,
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setEmailThankYouFields(fields);
      setOriginalEmailThankYouFields(fields);
      alert("Success: " + "Thank you email template saved successfully");
    } catch (error) {
      console.error("❌ Error saving thank you email template:", error);
      alert("Error: " + `Failed to save email template: ${error.message || error.code || "Unknown error"}`);
    } finally {
      setEmailTemplateSaving(false);
    }
  };

  // Load Thank You SMS Template
  const loadThankYouSmsTemplate = async () => {
    try {
      setThankYouSmsLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "thankYouSmsTemplate");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setThankYouSmsTemplate(data.template || "");
        setOriginalThankYouSmsTemplate(data.template || "");
      } else {
        const defaultTemplate = "🙏 Thank you for choosing {storeName}! We hope you had a great experience. View your booking: {bookingUrl}";
        setThankYouSmsTemplate(defaultTemplate);
        setOriginalThankYouSmsTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error("❌ Error loading thank you SMS template:", error);
      setThankYouSmsTemplate("");
      setOriginalThankYouSmsTemplate("");
    } finally {
      setThankYouSmsLoading(false);
      setSmsTemplateLoading(false);
    }
  };

  const handleSaveThankYouSmsTemplate = async () => {
    if (thankYouSmsTemplate.trim() === originalThankYouSmsTemplate.trim()) {
      alert("Info: " + "No changes detected");
      return;
    }

    try {
      setThankYouSmsSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "thankYouSmsTemplate");

      const dataToSave = {
        template: thankYouSmsTemplate.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalThankYouSmsTemplate(thankYouSmsTemplate.trim());
      alert("Success: " + "Thank you SMS template saved successfully");
    } catch (error) {
      console.error("❌ Error saving thank you SMS template:", error);
      alert("Error: " + `Failed to save SMS template: ${error.message || error.code || "Unknown error"}`);
    } finally {
      setThankYouSmsSaving(false);
    }
  };

  // Load Payment Link SMS Template
  const loadPaymentLinkSmsTemplate = async () => {
    try {
      setPaymentLinkSmsLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "paymentLinkSmsTemplate");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setPaymentLinkSmsTemplate(data.template || "");
        setOriginalPaymentLinkSmsTemplate(data.template || "");
      } else {
        // Default template
        const defaultTemplate = "💳 Hi {customerName}! Please complete payment for your {storeName} booking: {paymentUrl}";
        setPaymentLinkSmsTemplate(defaultTemplate);
        setOriginalPaymentLinkSmsTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error("❌ Error loading payment link SMS template:", error);
      setPaymentLinkSmsTemplate("");
      setOriginalPaymentLinkSmsTemplate("");
    } finally {
      setPaymentLinkSmsLoading(false);
    }
  };

  const handleSavePaymentLinkSmsTemplate = async () => {
    if (paymentLinkSmsTemplate.trim() === originalPaymentLinkSmsTemplate.trim()) {
      alert("Info: " + "No changes detected");
      return;
    }

    try {
      setPaymentLinkSmsSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "paymentLinkSmsTemplate");

      const dataToSave = {
        template: paymentLinkSmsTemplate.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalPaymentLinkSmsTemplate(paymentLinkSmsTemplate.trim());
      alert("Success: " + "Payment link SMS template saved successfully");
    } catch (error) {
      console.error("❌ Error saving payment link SMS template:", error);
      alert("Error: " + `Failed to save SMS template: ${error.message || error.code || "Unknown error"}`);
    } finally {
      setPaymentLinkSmsSaving(false);
    }
  };

  // Load 5-Star Review Request settings
  const loadReviewRequestSettings = async () => {
    try {
      setReviewRequestLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "reviewRequest");
      const settingsDoc = await getDoc(settingsRef);

      const data = settingsDoc.exists() ? settingsDoc.data() : {};
      const rawDelayHours = Number(data.delayHours);
      const rawSuppressDays = Number(data.reAskSuppressDays);

      const loaded = {
        enabled: data.enabled === true,
        url: data.url || "",
        delayHours: String(Number.isFinite(rawDelayHours) ? rawDelayHours : 4),
        // 0 is a valid, deliberate value ("ask every time") — must not coerce to the default.
        reAskSuppressDays: String(Number.isFinite(rawSuppressDays) ? rawSuppressDays : 3),
        smsTemplate: data.smsTemplate || REVIEW_REQUEST_DEFAULT_SMS,
        emailSubject: data.emailSubject || REVIEW_REQUEST_DEFAULT_EMAIL_SUBJECT,
        emailBody: data.emailBody || REVIEW_REQUEST_DEFAULT_EMAIL_BODY,
      };

      setReviewRequestEnabled(loaded.enabled);
      setReviewRequestUrl(loaded.url);
      setReviewRequestDelayHours(loaded.delayHours);
      setReviewRequestSuppressDays(loaded.reAskSuppressDays);
      setReviewRequestSmsTemplate(loaded.smsTemplate);
      setReviewRequestEmailSubject(loaded.emailSubject);
      setReviewRequestEmailBody(loaded.emailBody);
      setOriginalReviewRequest(loaded);
    } catch (error) {
      console.error("❌ Error loading review request settings:", error);
    } finally {
      setReviewRequestLoading(false);
    }
  };

  // Trim, default-scheme, and validate the review-page URL. Returns { valid, url, error }.
  const validateReviewRequestUrl = (rawUrl) => {
    const trimmed = (rawUrl || "").trim();
    if (!trimmed) return { valid: true, url: "" }; // empty clears/deactivates
    if (/\s/.test(trimmed)) {
      return { valid: false, error: "URL cannot contain spaces" };
    }

    const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmed);
    const withScheme = hasScheme ? trimmed : `https://${trimmed}`;

    let parsed;
    try {
      parsed = new URL(withScheme);
    } catch (e) {
      return { valid: false, error: "Enter a valid URL" };
    }
    if (parsed.protocol !== "https:") {
      return { valid: false, error: "URL must use https://" };
    }
    if (!parsed.hostname.includes(".")) {
      return { valid: false, error: "Enter a valid domain" };
    }
    return { valid: true, url: withScheme };
  };

  const handleSaveReviewRequestSettings = async () => {
    const { valid, url: validatedUrl, error: urlError } = validateReviewRequestUrl(reviewRequestUrl);
    if (!valid) {
      setReviewRequestUrlError(urlError);
      return;
    }
    setReviewRequestUrlError("");

    try {
      setReviewRequestSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "reviewRequest");

      const delayHours = Math.min(72, Math.max(1, Number(reviewRequestDelayHours) || 4));
      const rawSuppressDays = Number(reviewRequestSuppressDays);
      const reAskSuppressDays = Number.isFinite(rawSuppressDays)
        ? Math.min(365, Math.max(0, rawSuppressDays))
        : 3;

      const dataToSave = {
        enabled: !!reviewRequestEnabled,
        url: validatedUrl,
        delayHours,
        reAskSuppressDays,
        smsTemplate: reviewRequestSmsTemplate.trim(),
        emailSubject: reviewRequestEmailSubject.trim(),
        emailBody: reviewRequestEmailBody.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });

      setReviewRequestUrl(dataToSave.url);
      setReviewRequestDelayHours(String(dataToSave.delayHours));
      setReviewRequestSuppressDays(String(dataToSave.reAskSuppressDays));
      setReviewRequestSmsTemplate(dataToSave.smsTemplate);
      setReviewRequestEmailSubject(dataToSave.emailSubject);
      setReviewRequestEmailBody(dataToSave.emailBody);
      setOriginalReviewRequest({
        enabled: dataToSave.enabled,
        url: dataToSave.url,
        delayHours: String(dataToSave.delayHours),
        reAskSuppressDays: String(dataToSave.reAskSuppressDays),
        smsTemplate: dataToSave.smsTemplate,
        emailSubject: dataToSave.emailSubject,
        emailBody: dataToSave.emailBody,
      });
      alert("Success: " + "Review request settings saved successfully");
    } catch (error) {
      console.error("❌ Error saving review request settings:", error);
      alert("Error: " + `Failed to save review request settings: ${error.message || error.code || "Unknown error"}`);
    } finally {
      setReviewRequestSaving(false);
    }
  };

  // Load Equipment Penalty Email Template
  const loadPenaltyEmailTemplate = async () => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "emailPenaltyTemplateFields");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const fields = {
          headerSubtitle: data.headerSubtitle || "We hope you had a great rental!",
          greetingText: data.greetingText || "Thank you for choosing {storeName}! We've completed your check-in and found it necessary to charge for some equipment-related items.",
          customInstructions: data.customInstructions || "We've uploaded photos and details to your check-in page so you can see what we found. You can view everything by clicking the button below.",
          buttonText: data.buttonText || "📋 View Check-In Details",
          footerMessage: data.footerMessage || "If you have any questions about these charges, please don't hesitate to contact us. We appreciate your business!",
        };
        setEmailPenaltyFields(fields);
        setOriginalEmailPenaltyFields(fields);
      } else {
        const defaultFields = {
          headerSubtitle: "We hope you had a great rental!",
          greetingText: "Thank you for choosing {storeName}! We've completed your check-in and found it necessary to charge for some equipment-related items.",
          customInstructions: "We've uploaded photos and details to your check-in page so you can see what we found. You can view everything by clicking the button below.",
          buttonText: "📋 View Check-In Details",
          footerMessage: "If you have any questions about these charges, please don't hesitate to contact us. We appreciate your business!",
        };
        setEmailPenaltyFields(defaultFields);
        setOriginalEmailPenaltyFields(defaultFields);
      }
    } catch (error) {
      console.error("❌ Error loading penalty email template:", error);
    }
  };

  const handleSavePenaltyEmailTemplate = async (fields) => {
    try {
      setEmailTemplateSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "emailPenaltyTemplateFields");

      const dataToSave = {
        ...fields,
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setEmailPenaltyFields(fields);
      setOriginalEmailPenaltyFields(fields);
      alert("Success: " + "Penalty email template saved successfully");
    } catch (error) {
      console.error("❌ Error saving penalty email template:", error);
      alert("Error: " + `Failed to save email template: ${error.message || error.code || "Unknown error"}`);
    } finally {
      setEmailTemplateSaving(false);
    }
  };

  // ==================== REMOVE STRIPE RECEIPT TEMPLATES ====================

  const removeStripeReceiptTemplates = async () => {
    try {
      const confirmed = window.confirm(
        "This will permanently delete the Stripe Receipt templates from the database:\n\n" +
        "- stripeReceiptEmailFields\n" +
        "- stripeReceiptSmsTemplate\n\n" +
        "These templates are no longer used (hardcoded templates are now used instead).\n\n" +
        "Continue?"
      );

      if (!confirmed) return;

      const settingsPath = getSettingsPath();
      let deletedCount = 0;
      let errorCount = 0;

      // Delete stripeReceiptEmailFields
      try {
        await deleteDocument(settingsPath, "stripeReceiptEmailFields");
        deletedCount++;
        console.log("✅ Deleted stripeReceiptEmailFields");
      } catch (error) {
        console.error("❌ Error deleting stripeReceiptEmailFields:", error);
        errorCount++;
      }

      // Delete stripeReceiptSmsTemplate
      try {
        await deleteDocument(settingsPath, "stripeReceiptSmsTemplate");
        deletedCount++;
        console.log("✅ Deleted stripeReceiptSmsTemplate");
      } catch (error) {
        console.error("❌ Error deleting stripeReceiptSmsTemplate:", error);
        errorCount++;
      }

      alert(
        `Removal Complete!\n\n` +
        `✅ Deleted: ${deletedCount} templates\n` +
        `❌ Errors: ${errorCount}\n\n` +
        `Stripe Receipt templates have been removed from the database.`
      );

    } catch (error) {
      console.error("❌ Removal failed:", error);
      alert(`Removal failed: ${error.message}`);
    }
  };

  // Load Equipment Penalty SMS Template
  const loadPenaltySmsTemplate = async () => {
    try {
      setPenaltySmsLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "penaltySmsTemplate");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setPenaltySmsTemplate(data.template || "");
        setOriginalPenaltySmsTemplate(data.template || "");
      } else {
        const defaultTemplate = "Hi {customerName}! We charged {penaltyAmount} for equipment penalties. Photos & details available here: {checkInUrl} Receipt: {receiptUrl}";
        setPenaltySmsTemplate(defaultTemplate);
        setOriginalPenaltySmsTemplate(defaultTemplate);
      }
    } catch (error) {
      console.error("❌ Error loading penalty SMS template:", error);
      setPenaltySmsTemplate("");
      setOriginalPenaltySmsTemplate("");
    } finally {
      setPenaltySmsLoading(false);
    }
  };

  const handleSavePenaltySmsTemplate = async () => {
    if (penaltySmsTemplate.trim() === originalPenaltySmsTemplate.trim()) {
      alert("Info: " + "No changes detected");
      return;
    }

    try {
      setPenaltySmsSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "penaltySmsTemplate");

      const dataToSave = {
        template: penaltySmsTemplate.trim(),
        updatedAt: Timestamp.now(),
      };

      await setDoc(settingsRef, dataToSave, { merge: true });
      setOriginalPenaltySmsTemplate(penaltySmsTemplate.trim());
      alert("Success: " + "Penalty SMS template saved successfully");
    } catch (error) {
      console.error("❌ Error saving penalty SMS template:", error);
      alert("Error: " + `Failed to save SMS template: ${error.message || error.code || "Unknown error"}`);
    } finally {
      setPenaltySmsSaving(false);
    }
  };

  // ==================== EMAIL TEMPLATE DEFAULTS ====================

  const getDefaultDeliveryEmailTemplate = () => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Booking Confirmation</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 10px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
          
          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 30px; text-align: center;">
              <!-- Logo (if available) -->
              <img src="{storeLogo}" alt="{storeName}" style="max-width: 150px; max-height: 150px; margin-bottom: 15px; display: block; margin-left: auto; margin-right: auto;" />
              <h1 style="margin: 0; color: #ffffff; font-size: 28px; font-weight: bold;">🚴 Booking Confirmed!</h1>
              <p style="margin: 10px 0 0 0; color: #ffffff; font-size: 16px;">Get ready for your adventure with {storeName}</p>
            </td>
          </tr>

          <!-- Greeting -->
          <tr>
            <td style="padding: 40px 30px 20px 30px;">
              <p style="margin: 0; font-size: 16px; color: #333333; line-height: 1.6;">
                Hi <strong>{customerName}</strong>,
              </p>
              <p style="margin: 15px 0 0 0; font-size: 16px; color: #333333; line-height: 1.6;">
                Great news! Your bike rental has been confirmed and payment received. We can't wait to get you out on the trails! 🎉
              </p>
            </td>
          </tr>

          <!-- Booking Details Box -->
          <tr>
            <td style="padding: 0 30px 20px 30px;">
              <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f8f9fa; border-radius: 8px; border-left: 4px solid #667eea;">
                <tr>
                  <td style="padding: 20px;">
                    <h2 style="margin: 0 0 15px 0; color: #667eea; font-size: 18px;">📋 Booking Details</h2>
                    <table width="100%" cellpadding="8" cellspacing="0">
                      <tr>
                        <td style="color: #666; font-size: 14px; padding: 5px 0;">🆔 Booking ID:</td>
                        <td style="color: #333; font-size: 14px; font-weight: bold; padding: 5px 0;">{bookingId}</td>
                      </tr>
                      <tr>
                        <td style="color: #666; font-size: 14px; padding: 5px 0;">📅 Start Date:</td>
                        <td style="color: #333; font-size: 14px; font-weight: bold; padding: 5px 0;">{startDate}</td>
                      </tr>
                      <tr>
                        <td style="color: #666; font-size: 14px; padding: 5px 0;">📅 End Date:</td>
                        <td style="color: #333; font-size: 14px; font-weight: bold; padding: 5px 0;">{endDate}</td>
                      </tr>
                      <tr>
                        <td style="color: #666; font-size: 14px; padding: 5px 0;">⏱️ Duration:</td>
                        <td style="color: #333; font-size: 14px; font-weight: bold; padding: 5px 0;">{days} day(s)</td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Bikes & Add-ons -->
          <tr>
            <td style="padding: 0 30px 20px 30px;">
              <h2 style="margin: 0 0 15px 0; color: #333; font-size: 18px;">🚴‍♂️ Your Bikes & Add-ons</h2>
              <div style="background-color: #f8f9fa; border-radius: 8px; padding: 15px;">
                <p style="margin: 0; font-size: 14px; color: #333; line-height: 1.6; white-space: pre-line;">{bicyclesAndAddOns}</p>
              </div>
            </td>
          </tr>

          <!-- Delivery/Pickup Info (conditional) -->
          <!-- This section is dynamically generated and includes:
               - Delivery/Pickup status
               - Customer address
               - Schedule: 11 AM - 3 PM, 7 days/week, 4-hour window
               - Store phone for arrangements
               - Delivery phone (text only) -->
          {deliveryPickupSection}

          <!-- Security PIN Box -->
          <tr>
            <td style="padding: 0 30px 20px 30px;">
              <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f0f7ff; border-radius: 8px; border: 3px solid #007AFF;">
                <tr>
                  <td style="padding: 20px; text-align: center;">
                    <p style="margin: 0 0 10px 0; font-size: 14px; color: #666;">🔐 Your Booking PIN:</p>
                    <p style="margin: 0; font-size: 36px; font-weight: bold; color: #007AFF; letter-spacing: 8px;">
                      {bookingPin}
                    </p>
                    <p style="margin: 10px 0 0 0; font-size: 12px; color: #666;">
                      Keep this PIN secure - you'll need it to manage your booking online
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Manage Booking Button -->
          <tr>
            <td style="padding: 0 30px 20px 30px;" align="center">
              <a href="{bookingUrl}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; text-decoration: none; padding: 15px 40px; border-radius: 25px; font-size: 16px; font-weight: bold; box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);">
                ✏️ View Your Booking
              </a>
            </td>
          </tr>

          <!-- Contact Info -->
          <tr>
            <td style="padding: 20px 30px 30px 30px; border-top: 1px solid #e0e0e0;">
              <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f8f9fa; border-radius: 8px; padding: 20px;">
                <tr>
                  <td align="center">
                    <h3 style="margin: 0 0 15px 0; color: #333; font-size: 16px;">📞 Questions or Need Help?</h3>
                    <p style="margin: 5px 0; font-size: 14px; color: #666; line-height: 1.8;">
                      <strong>📱 Phone:</strong> {storePhone}<br>
                      <strong>📧 Email:</strong> {storeEmail}
                    </p>
                    <p style="margin: 15px 0 0 0; font-size: 14px;">
                      <a href="https://bonitabikes.com" style="display: inline-block; color: #667eea; text-decoration: none; font-weight: 600; padding: 10px 20px; border: 2px solid #667eea; border-radius: 8px;">
                        🌐 Visit BonitaBikes.com
                      </a>
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color: #f8f9fa; padding: 20px 30px; text-align: center;">
              <p style="margin: 0; font-size: 12px; color: #999;">
                © 2024 Bonita Bikes. All rights reserved.<br>
                This booking confirmation was sent to {customerEmail}
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };

  const getDefaultPickupEmailTemplate = () => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Booking Confirmation</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 10px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
          
          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 30px; text-align: center;">
              <!-- Logo (if available) -->
              <img src="{storeLogo}" alt="{storeName}" style="max-width: 150px; max-height: 150px; margin-bottom: 15px; display: block; margin-left: auto; margin-right: auto;" />
              <h1 style="margin: 0; color: #ffffff; font-size: 28px; font-weight: bold;">🚴 Booking Confirmed!</h1>
              <p style="margin: 10px 0 0 0; color: #ffffff; font-size: 16px;">Get ready for your adventure with {storeName}</p>
            </td>
          </tr>

          <!-- Greeting -->
          <tr>
            <td style="padding: 40px 30px 20px 30px;">
              <p style="margin: 0; font-size: 16px; color: #333333; line-height: 1.6;">
                Hi <strong>{customerName}</strong>,
              </p>
              <p style="margin: 15px 0 0 0; font-size: 16px; color: #333333; line-height: 1.6;">
                Great news! Your bike rental has been confirmed and payment received. We can't wait to get you out on the trails! 🎉
              </p>
            </td>
          </tr>

          <!-- Booking Details Box -->
          <tr>
            <td style="padding: 0 30px 20px 30px;">
              <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f8f9fa; border-radius: 8px; border-left: 4px solid #667eea;">
                <tr>
                  <td style="padding: 20px;">
                    <h2 style="margin: 0 0 15px 0; color: #667eea; font-size: 18px;">📋 Booking Details</h2>
                    <table width="100%" cellpadding="8" cellspacing="0">
                      <tr>
                        <td style="color: #666; font-size: 14px; padding: 5px 0;">🆔 Booking ID:</td>
                        <td style="color: #333; font-size: 14px; font-weight: bold; padding: 5px 0;">{bookingId}</td>
                      </tr>
                      <tr>
                        <td style="color: #666; font-size: 14px; padding: 5px 0;">📅 Start Date:</td>
                        <td style="color: #333; font-size: 14px; font-weight: bold; padding: 5px 0;">{startDate}</td>
                      </tr>
                      <tr>
                        <td style="color: #666; font-size: 14px; padding: 5px 0;">📅 End Date:</td>
                        <td style="color: #333; font-size: 14px; font-weight: bold; padding: 5px 0;">{endDate}</td>
                      </tr>
                      <tr>
                        <td style="color: #666; font-size: 14px; padding: 5px 0;">⏱️ Duration:</td>
                        <td style="color: #333; font-size: 14px; font-weight: bold; padding: 5px 0;">{days} day(s)</td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Bikes & Add-ons -->
          <tr>
            <td style="padding: 0 30px 20px 30px;">
              <h2 style="margin: 0 0 15px 0; color: #333; font-size: 18px;">🚴‍♂️ Your Bikes & Add-ons</h2>
              <div style="background-color: #f8f9fa; border-radius: 8px; padding: 15px;">
                <p style="margin: 0; font-size: 14px; color: #333; line-height: 1.6; white-space: pre-line;">{bicyclesAndAddOns}</p>
              </div>
            </td>
          </tr>

          <!-- In-Store Pickup Information -->
          <tr>
            <td style="padding: 0 30px 20px 30px;">
              <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #e8f5e9; border-radius: 8px; border-left: 4px solid #4caf50;">
                <tr>
                  <td style="padding: 20px;">
                    <h2 style="margin: 0 0 15px 0; color: #2e7d32; font-size: 18px;">🏪 In-Store Pickup</h2>
                    <p style="margin: 0 0 15px 0; font-size: 14px; color: #333; line-height: 1.6;">
                      Your rental is ready for pickup at our store. Please arrive during your scheduled pickup time on your <strong>start date</strong> and return on your <strong>end date</strong>.
                    </p>
                    <p style="margin: 15px 0 0 0; font-size: 13px; color: #666;">
                      Need to arrange different times? Give us a call at <strong>{storePhone}</strong> or visit <a href="https://bonitabikes.com" style="color: #667eea; text-decoration: none; font-weight: 600;">bonitabikes.com</a>
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Security PIN Box -->
          <tr>
            <td style="padding: 0 30px 20px 30px;">
              <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f0f7ff; border-radius: 8px; border: 3px solid #007AFF;">
                <tr>
                  <td style="padding: 20px; text-align: center;">
                    <p style="margin: 0 0 10px 0; font-size: 14px; color: #666;">🔐 Your Booking PIN:</p>
                    <p style="margin: 0; font-size: 36px; font-weight: bold; color: #007AFF; letter-spacing: 8px;">
                      {bookingPin}
                    </p>
                    <p style="margin: 10px 0 0 0; font-size: 12px; color: #666;">
                      Keep this PIN secure - you'll need it to manage your booking online
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Manage Booking Button -->
          <tr>
            <td style="padding: 0 30px 20px 30px;" align="center">
              <a href="{bookingUrl}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; text-decoration: none; padding: 15px 40px; border-radius: 25px; font-size: 16px; font-weight: bold; box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);">
                ✏️ View Your Booking
              </a>
            </td>
          </tr>

          <!-- Contact Info -->
          <tr>
            <td style="padding: 20px 30px 30px 30px; border-top: 1px solid #e0e0e0;">
              <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f8f9fa; border-radius: 8px; padding: 20px;">
                <tr>
                  <td align="center">
                    <h3 style="margin: 0 0 15px 0; color: #333; font-size: 16px;">📞 Questions or Need Help?</h3>
                    <p style="margin: 5px 0; font-size: 14px; color: #666; line-height: 1.8;">
                      <strong>📱 Phone:</strong> {storePhone}<br>
                      <strong>📧 Email:</strong> {storeEmail}
                    </p>
                    <p style="margin: 15px 0 0 0; font-size: 14px;">
                      <a href="https://bonitabikes.com" style="display: inline-block; color: #667eea; text-decoration: none; font-weight: 600; padding: 10px 20px; border: 2px solid #667eea; border-radius: 8px;">
                        🌐 Visit BonitaBikes.com
                      </a>
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color: #f8f9fa; padding: 20px 30px; text-align: center;">
              <p style="margin: 0; font-size: 12px; color: #999;">
                © 2024 Bonita Bikes. All rights reserved.<br>
                This booking confirmation was sent to {customerEmail}
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };

  const getDefaultBookingEditEmailTemplate = () => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Booking Updated</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 10px; overflow: hidden; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
          
          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 30px; text-align: center;">
              <!-- Logo (if available) -->
              <img src="{storeLogo}" alt="{storeName}" style="max-width: 150px; max-height: 150px; margin-bottom: 15px; display: block; margin-left: auto; margin-right: auto;" />
              <h1 style="margin: 0; color: #ffffff; font-size: 28px; font-weight: bold;">✏️ Booking Updated!</h1>
              <p style="margin: 10px 0 0 0; color: #ffffff; font-size: 16px;">Changes have been made to your reservation</p>
            </td>
          </tr>

          <!-- Greeting -->
          <tr>
            <td style="padding: 40px 30px 20px 30px;">
              <p style="margin: 0; font-size: 16px; color: #333333; line-height: 1.6;">
                Hi <strong>{customerName}</strong>,
              </p>
              <p style="margin: 15px 0 0 0; font-size: 16px; color: #333333; line-height: 1.6;">
                Your booking has been successfully updated. Here's a summary of the changes made:
              </p>
            </td>
          </tr>

          <!-- Booking Details Box -->
          <tr>
            <td style="padding: 0 30px 20px 30px;">
              <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f8f9fa; border-radius: 8px; border-left: 4px solid #667eea;">
                <tr>
                  <td style="padding: 20px;">
                    <h2 style="margin: 0 0 15px 0; color: #667eea; font-size: 18px;">📋 Booking Details</h2>
                    <table width="100%" cellpadding="8" cellspacing="0">
                      <tr>
                        <td style="color: #666; font-size: 14px; padding: 5px 0;">🆔 Booking ID:</td>
                        <td style="color: #333; font-size: 14px; font-weight: bold; padding: 5px 0;">{bookingId}</td>
                      </tr>
                      <tr>
                        <td style="color: #666; font-size: 14px; padding: 5px 0;">📅 Start Date:</td>
                        <td style="color: #333; font-size: 14px; font-weight: bold; padding: 5px 0;">{startDate}</td>
                      </tr>
                      <tr>
                        <td style="color: #666; font-size: 14px; padding: 5px 0;">📅 End Date:</td>
                        <td style="color: #333; font-size: 14px; font-weight: bold; padding: 5px 0;">{endDate}</td>
                      </tr>
                      <tr>
                        <td style="color: #666; font-size: 14px; padding: 5px 0;">⏱️ Duration:</td>
                        <td style="color: #333; font-size: 14px; font-weight: bold; padding: 5px 0;">{days} day(s)</td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Changes Summary (Items Added) -->
          <!-- Note: Cloud function will dynamically generate this section based on actual changes -->
          <tr>
            <td style="padding: 0 30px 20px 30px;">
              <div style="background-color: #d4edda; border-left: 4px solid #28a745; border-radius: 8px; padding: 20px; margin-bottom: 15px;">
                <h3 style="margin: 0 0 10px 0; color: #28a745; font-size: 16px;">➕ Items Added</h3>
                <p style="margin: 0; font-size: 14px; color: #333; line-height: 1.6;">
                  {addedItemsList}
                </p>
              </div>
            </td>
          </tr>

          <!-- Changes Summary (Items Removed) -->
          <tr>
            <td style="padding: 0 30px 20px 30px;">
              <div style="background-color: #f8d7da; border-left: 4px solid #dc3545; border-radius: 8px; padding: 20px;">
                <h3 style="margin: 0 0 10px 0; color: #dc3545; font-size: 16px;">➖ Items Removed</h3>
                <p style="margin: 0; font-size: 14px; color: #333; line-height: 1.6;">
                  {removedItemsList}
                </p>
              </div>
            </td>
          </tr>

          <!-- Payment Summary -->
          <tr>
            <td style="padding: 0 30px 20px 30px;">
              <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #fff3cd; border-radius: 8px; border-left: 4px solid #ffc107;">
                <tr>
                  <td style="padding: 20px;">
                    <h3 style="margin: 0 0 15px 0; color: #333; font-size: 16px;">💳 Payment Summary</h3>
                    <table width="100%" cellpadding="5" cellspacing="0">
                      <tr>
                        <td style="color: #333; font-size: 14px;">Additional Charges:</td>
                        <td align="right" style="color: #333; font-size: 14px; font-weight: bold;">+\${additionsTotal}</td>
                      </tr>
                      <tr>
                        <td style="color: #333; font-size: 14px;">Refund Issued:</td>
                        <td align="right" style="color: #333; font-size: 14px; font-weight: bold;">-\${refundTotal}</td>
                      </tr>
                      <tr style="border-top: 2px solid #ffc107;">
                        <td style="color: #333; font-size: 16px; font-weight: bold; padding-top: 10px;">Net Amount:</td>
                        <td align="right" style="color: #ffc107; font-size: 18px; font-weight: bold; padding-top: 10px;">\${netAmount}</td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Security PIN Box -->
          <tr>
            <td style="padding: 0 30px 20px 30px;">
              <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f0f7ff; border-radius: 8px; border: 3px solid #007AFF;">
                <tr>
                  <td style="padding: 20px; text-align: center;">
                    <p style="margin: 0 0 10px 0; font-size: 14px; color: #666;">🔐 Your Booking PIN:</p>
                    <p style="margin: 0; font-size: 36px; font-weight: bold; color: #007AFF; letter-spacing: 8px;">
                      {bookingPin}
                    </p>
                    <p style="margin: 10px 0 0 0; font-size: 12px; color: #666;">
                      Use this PIN to manage your booking online
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Manage Booking Button -->
          <tr>
            <td style="padding: 0 30px 20px 30px;" align="center">
              <a href="{bookingUrl}" style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; text-decoration: none; padding: 15px 40px; border-radius: 25px; font-size: 16px; font-weight: bold; box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);">
                ✏️ View Your Booking
              </a>
            </td>
          </tr>

          <!-- Updated Bikes & Add-ons List -->
          <tr>
            <td style="padding: 0 30px 20px 30px;">
              <h2 style="margin: 0 0 15px 0; color: #333; font-size: 18px;">🚴‍♂️ Your Updated Booking</h2>
              <div style="background-color: #f8f9fa; border-radius: 8px; padding: 15px;">
                <p style="margin: 0; font-size: 14px; color: #333; line-height: 1.6; white-space: pre-line;">{bicyclesAndAddOns}</p>
              </div>
            </td>
          </tr>

          <!-- Contact Info -->
          <tr>
            <td style="padding: 20px 30px 30px 30px; border-top: 1px solid #e0e0e0;">
              <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f8f9fa; border-radius: 8px; padding: 20px;">
                <tr>
                  <td align="center">
                    <h3 style="margin: 0 0 15px 0; color: #333; font-size: 16px;">📞 Questions or Need Help?</h3>
                    <p style="margin: 5px 0; font-size: 14px; color: #666; line-height: 1.8;">
                      <strong>📱 Phone:</strong> {storePhone}<br>
                      <strong>📧 Email:</strong> {storeEmail}
                    </p>
                    <p style="margin: 15px 0 0 0; font-size: 14px;">
                      <a href="https://bonitabikes.com" style="display: inline-block; color: #667eea; text-decoration: none; font-weight: 600; padding: 10px 20px; border: 2px solid #667eea; border-radius: 8px;">
                        🌐 Visit Our Website
                      </a>
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color: #f8f9fa; padding: 20px 30px; text-align: center;">
              <p style="margin: 0; font-size: 12px; color: #999;">
                © 2024 {storeName}. All rights reserved.<br>
                This update was sent to {customerEmail}
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };

  // ==================== SALES TAX FUNCTIONS ====================

  const loadSalesTax = async () => {
    try {
      setSalesTaxLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "salesTax");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const percent = data.percent?.toString() || "";
        setSalesTaxPercent(percent);
        setOriginalSalesTaxPercent(percent);
      } else {
        // Default to empty if not set
        setSalesTaxPercent("");
        setOriginalSalesTaxPercent("");
      }
    } catch (error) {
      console.error("❌ Error loading sales tax:", error);
      setSalesTaxPercent("");
      setOriginalSalesTaxPercent("");
    } finally {
      setSalesTaxLoading(false);
    }
  };

  const handleSaveSalesTax = async () => {
    const percent = parseFloat(salesTaxPercent);

    if (
      salesTaxPercent.trim() !== "" &&
      (isNaN(percent) || percent < 0 || percent > 100)
    ) {
      alert("Error: " + "Please enter a valid sales tax percentage (0-100)");
      return;
    }

    try {
      setSalesTaxSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "salesTax");

      if (salesTaxPercent.trim() === "") {
        // Clear the setting
        await setDoc(settingsRef, { percent: null }, { merge: true });
        setOriginalSalesTaxPercent("");
        alert("Success: " + "Sales tax cleared");
      } else {
        const dataToSave = {
          percent: percent,
          updatedAt: Timestamp.now(),
        };
        await setDoc(settingsRef, dataToSave, { merge: true });
        setOriginalSalesTaxPercent(salesTaxPercent.trim());
        alert("Success: " + "Sales tax saved successfully");
      }
    } catch (error) {
      console.error("❌ Error saving sales tax:", error);
      alert("Error: " +
        `Failed to save sales tax: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setSalesTaxSaving(false);
    }
  };

  // Load Card Processing Fee
  const loadCardProcessingFee = async () => {
    try {
      setCardProcessingFeeLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "cardProcessingFee");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const percent = data.percent?.toString() || "3";
        setCardProcessingFee(percent);
        setOriginalCardProcessingFee(percent);
        const forPurchases = data.forPurchases || false;
        const forRefunds = data.forRefunds || false;
        setCardProcessingFeeForPurchases(forPurchases);
        setCardProcessingFeeForRefunds(forRefunds);
        setOriginalCardProcessingFeeForPurchases(forPurchases);
        setOriginalCardProcessingFeeForRefunds(forRefunds);
      } else {
        // Default to 3% if not set
        setCardProcessingFee("3");
        setOriginalCardProcessingFee("3");
        setCardProcessingFeeForPurchases(false);
        setCardProcessingFeeForRefunds(false);
        setOriginalCardProcessingFeeForPurchases(false);
        setOriginalCardProcessingFeeForRefunds(false);
      }
    } catch (error) {
      console.error("❌ Error loading card processing fee:", error);
      setCardProcessingFee("3");
      setOriginalCardProcessingFee("3");
      setCardProcessingFeeForPurchases(false);
      setCardProcessingFeeForRefunds(false);
      setOriginalCardProcessingFeeForPurchases(false);
      setOriginalCardProcessingFeeForRefunds(false);
    } finally {
      setCardProcessingFeeLoading(false);
    }
  };
  
  // Load Cancellation Policy Windows
  const loadCancellationWindows = async () => {
    try {
      setCancellationWindowsLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "cancellationPolicyWindows");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setCancellationWindows(data.windows || [
          { hours: 48, percentPenalty: 0, daysPenalty: 0 },
          { hours: 24, percentPenalty: 25, daysPenalty: 1 },
          { hours: 0, percentPenalty: 50, daysPenalty: 2 },
        ]);
        setPenaltyType(data.penaltyType || "percentage");
      } else {
        // Default values
        setCancellationWindows([
          { hours: 48, percentPenalty: 0, daysPenalty: 0 },
          { hours: 24, percentPenalty: 25, daysPenalty: 1 },
          { hours: 0, percentPenalty: 50, daysPenalty: 2 },
        ]);
        setPenaltyType("percentage");
      }
    } catch (error) {
      console.error("❌ Error loading cancellation windows:", error);
    } finally {
      setCancellationWindowsLoading(false);
    }
  };

  // Save Cancellation Policy Windows
  const handleSaveCancellationWindows = async () => {
    try {
      // Validation
      for (let i = 0; i < cancellationWindows.length; i++) {
        const window = cancellationWindows[i];
        if (window.hours < 0 || isNaN(window.hours)) {
          alert(`Window ${i + 1}: Hours must be a valid number >= 0`);
          return;
        }
        if (window.percentPenalty < 0 || window.percentPenalty > 100 || isNaN(window.percentPenalty)) {
          alert(`Window ${i + 1}: Percent penalty must be between 0-100`);
          return;
        }
        if (window.daysPenalty < 0 || isNaN(window.daysPenalty)) {
          alert(`Window ${i + 1}: Days penalty must be >= 0`);
          return;
        }
      }
      
      setCancellationWindowsSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "cancellationPolicyWindows");
      
      const dataToSave = {
        windows: cancellationWindows,
        penaltyType: penaltyType,
        updatedAt: Timestamp.now(),
      };
      
      await setDoc(settingsRef, dataToSave, { merge: true });
      alert("✅ Cancellation policy windows saved successfully!");
    } catch (error) {
      console.error("❌ Error saving cancellation windows:", error);
      alert("Error: Failed to save cancellation windows");
    } finally {
      setCancellationWindowsSaving(false);
    }
  };

  // Save Card Processing Fee
  const handleSaveCardProcessingFee = async () => {
    const percent = parseFloat(cardProcessingFee);

    if (
      cardProcessingFee.trim() !== "" &&
      (isNaN(percent) || percent < 0 || percent >= 5)
    ) {
      alert("Error: " + "Please enter a valid card processing fee (0-4.99%)");
      return;
    }

    try {
      setCardProcessingFeeSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "cardProcessingFee");

      if (cardProcessingFee.trim() === "") {
        // Default to 3% if cleared
        const dataToSave = {
          percent: 3,
          forPurchases: cardProcessingFeeForPurchases,
          forRefunds: cardProcessingFeeForRefunds,
          updatedAt: Timestamp.now(),
        };
        await setDoc(settingsRef, dataToSave, { merge: true });
        setOriginalCardProcessingFee("3");
        setCardProcessingFee("3");
        setOriginalCardProcessingFeeForPurchases(cardProcessingFeeForPurchases);
        setOriginalCardProcessingFeeForRefunds(cardProcessingFeeForRefunds);
        alert("Success: " + "Card processing fee reset to 3%");
      } else {
        const dataToSave = {
          percent: percent,
          forPurchases: cardProcessingFeeForPurchases,
          forRefunds: cardProcessingFeeForRefunds,
          updatedAt: Timestamp.now(),
        };
        await setDoc(settingsRef, dataToSave, { merge: true });
        setOriginalCardProcessingFee(cardProcessingFee.trim());
        setOriginalCardProcessingFeeForPurchases(cardProcessingFeeForPurchases);
        setOriginalCardProcessingFeeForRefunds(cardProcessingFeeForRefunds);
        alert("Success: " + "Card processing fee saved successfully");
      }
    } catch (error) {
      console.error("❌ Error saving card processing fee:", error);
      alert("Error: " +
        `Failed to save card processing fee: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setCardProcessingFeeSaving(false);
    }
  };


  // ==================== DURATION DISCOUNTS FUNCTIONS ====================

  const loadUseDurationDiscounts = async () => {
    try {
      setUseDurationDiscountsLoading(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "useDurationDiscounts");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const enabled = settingsDoc.data().enabled !== undefined ? settingsDoc.data().enabled : false;
        setUseDurationDiscounts(enabled);
      } else {
        setUseDurationDiscounts(false);
      }
    } catch (error) {
      console.error("❌ Error loading useDurationDiscounts:", error);
      setUseDurationDiscounts(false);
    } finally {
      setUseDurationDiscountsLoading(false);
    }
  };

  const handleToggleUseDurationDiscounts = async () => {
    const newValue = !useDurationDiscounts;
    
    try {
      setUseDurationDiscountsSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "useDurationDiscounts");
      
      await setDoc(settingsRef, {
        enabled: newValue,
        updatedAt: Timestamp.now(),
      });
      
      setUseDurationDiscounts(newValue);
    } catch (error) {
      console.error("❌ Error saving useDurationDiscounts:", error);
      alert("Error: " + "Failed to update duration discounts setting");
    } finally {
      setUseDurationDiscountsSaving(false);
    }
  };

  const loadDurationDiscounts = async () => {
    try {
      setLoadingDurationDiscounts(true);
      const settingsPath = getSettingsPath();
      const discountsDoc = await getDoc(doc(db, settingsPath, "durationDiscounts"));
      
      if (discountsDoc.exists()) {
        const data = discountsDoc.data();
        const discountsList = data.discounts || [];
        
        // Sort by smallest "fromDays" field first
        const sortedDiscounts = [...discountsList].sort((a, b) => {
          const fromA = a.fromDays !== undefined ? a.fromDays : Infinity;
          const fromB = b.fromDays !== undefined ? b.fromDays : Infinity;
          return fromA - fromB;
        });
        
        setDurationDiscounts(sortedDiscounts);
      } else {
        setDurationDiscounts([]);
      }
    } catch (error) {
      console.error("❌ Error loading duration discounts:", error);
      setDurationDiscounts([]);
    } finally {
      setLoadingDurationDiscounts(false);
    }
  };

  const startEditingDurationDiscount = (discount) => {
    setEditingDurationDiscountId(discount.id);
    setDurationDiscountFormData({
      fromDays: discount.fromDays?.toString() || "",
      toDays: discount.toDays?.toString() || "",
      value: discount.value?.toString() || "",
    });
    setShowDurationDiscountForm(true);
    // Scroll to form after a brief delay to ensure it's rendered
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const cancelEditingDurationDiscount = () => {
    setEditingDurationDiscountId(null);
    setDurationDiscountFormData({ fromDays: "", toDays: "", value: "" });
    setShowDurationDiscountForm(false);
  };

  const handleDurationDiscountSave = async () => {
    const fromDays = parseFloat(durationDiscountFormData.fromDays);
    const toDays = parseFloat(durationDiscountFormData.toDays);
    const value = parseFloat(durationDiscountFormData.value);

    if (isNaN(fromDays) || fromDays < 1) {
      alert("Error: " + "Please enter a valid 'from' days (minimum 1)");
      return;
    }

    if (isNaN(toDays) || toDays < 1) {
      alert("Error: " + "Please enter a valid 'to' days (minimum 1)");
      return;
    }

    if (toDays <= fromDays) {
      alert("Error: " + "'To' days must be greater than 'from' days");
      return;
    }

    if (isNaN(value) || value < 0) {
      alert("Error: " + "Please enter a valid discount percentage");
      return;
    }

    if (value > 100) {
      alert("Error: " + "Percentage discount cannot exceed 100%");
      return;
    }

    try {
      setDurationDiscountSaving(true);
      const discountData = {
        fromDays: fromDays,
        toDays: toDays,
        value: value,
        percent: true,
      };

      const settingsPath = getSettingsPath();
      const discountsDoc = await getDoc(doc(db, settingsPath, "durationDiscounts"));
      let discountsList = [];

      if (discountsDoc.exists()) {
        discountsList = discountsDoc.data().discounts || [];
      }

      if (editingDurationDiscountId) {
        const index = discountsList.findIndex(d => d.id === editingDurationDiscountId);
        if (index !== -1) {
          discountsList[index] = { ...discountData, id: editingDurationDiscountId, updatedAt: Timestamp.now() };
        }
      } else {
        const newId = `duration_${Date.now()}`;
        discountsList.push({ ...discountData, id: newId, createdAt: Timestamp.now() });
      }

      await setDoc(doc(db, settingsPath, "durationDiscounts"), {
        discounts: discountsList,
        updatedAt: Timestamp.now(),
      });

      alert("Success: " + "Duration discount saved successfully");
      await loadDurationDiscounts();
      cancelEditingDurationDiscount();
    } catch (error) {
      console.error("❌ Error saving duration discount:", error);
      alert("Error: " + "Failed to save duration discount");
    } finally {
      setDurationDiscountSaving(false);
    }
  };

  const handleDurationDiscountDelete = async (discountId, discountName) => {
    if (!window.confirm(`Delete duration discount "${discountName}"?`)) {
      return;
    }

    try {
      setDeletingDurationDiscountId(discountId);
      const settingsPath = getSettingsPath();
      const discountsDoc = await getDoc(doc(db, settingsPath, "durationDiscounts"));
      
      if (discountsDoc.exists()) {
        const discountsList = discountsDoc.data().discounts || [];
        const filtered = discountsList.filter(d => d.id !== discountId);
        
        await setDoc(doc(db, settingsPath, "durationDiscounts"), {
          discounts: filtered,
          updatedAt: Timestamp.now(),
        });
        
        await loadDurationDiscounts();
        alert("Success: " + "Duration discount deleted successfully");
      }
    } catch (error) {
      console.error("❌ Error deleting duration discount:", error);
      alert("Error: " + "Failed to delete duration discount");
    } finally {
      setDeletingDurationDiscountId(null);
    }
  };

  const startAddingDurationDiscount = () => {
    setEditingDurationDiscountId(null);
    setDurationDiscountFormData({ fromDays: "", toDays: "", value: "" });
    setShowDurationDiscountForm(true);
    // Scroll to form after a brief delay to ensure it's rendered
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  // ==================== DELIVERY DISCOUNTS FUNCTIONS ====================

  const loadUseDeliveryDiscounts = async () => {
    try {
      setUseDeliveryDiscountsLoading(true);
        const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "useDeliveryDiscounts");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const enabled = settingsDoc.data().enabled !== undefined ? settingsDoc.data().enabled : true;
        setUseDeliveryDiscounts(enabled);
      } else {
        setUseDeliveryDiscounts(true);
      }
    } catch (error) {
      console.error("❌ Error loading useDeliveryDiscounts:", error);
      setUseDeliveryDiscounts(true);
    } finally {
      setUseDeliveryDiscountsLoading(false);
    }
  };

  const handleToggleUseDeliveryDiscounts = async () => {
    const newValue = !useDeliveryDiscounts;
    
    try {
      setUseDeliveryDiscountsSaving(true);
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "useDeliveryDiscounts");
      
      await setDoc(settingsRef, {
        enabled: newValue,
        updatedAt: Timestamp.now(),
      });
      
      setUseDeliveryDiscounts(newValue);
    } catch (error) {
      console.error("❌ Error saving useDeliveryDiscounts:", error);
      alert("Error: " + "Failed to update delivery discounts setting");
    } finally {
      setUseDeliveryDiscountsSaving(false);
    }
  };

  const loadDiscounts = async () => {
    try {
      setLoadingDiscounts(true);
      // Get discounts from settings document
      const settingsPath = getSettingsPath();
      const discountsDoc = await getDoc(doc(db, settingsPath, "deliveryDiscounts"));
      
      if (discountsDoc.exists()) {
        const data = discountsDoc.data();
        const discountsList = data.discounts || [];
        const sortedDiscounts = [...discountsList].sort((a, b) => {
          const fromA = a.from !== undefined ? a.from : Infinity;
          const fromB = b.from !== undefined ? b.from : Infinity;
          return fromA - fromB;
        });
        setDiscounts(sortedDiscounts);
      } else {
        setDiscounts([]);
      }
    } catch (error) {
      console.error("❌ Error loading discounts:", error);
      setDiscounts([]);
    } finally {
      setLoadingDiscounts(false);
    }
  };

  const startEditingDiscount = (discount) => {
    setEditingDiscountId(discount.id);
    // Convert cents to USD for display in form
    const fromDollars = discount.from !== undefined ? centsToDollars(discount.from) : 0;
    const toDollars = discount.to !== undefined ? centsToDollars(discount.to) : 0;
    setDiscountFormData({
      from: fromDollars.toString(),
      to: toDollars.toString(),
      value: discount.value?.toString() || "",
    });
    setShowDiscountForm(true);
    // Scroll to form after a brief delay to ensure it's rendered
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const cancelEditingDiscount = () => {
    setEditingDiscountId(null);
    setDiscountFormData({ from: "", to: "", value: "" });
    setShowDiscountForm(false);
  };

  const handleDiscountSave = async () => {
    const fromDollars = parseFloat(discountFormData.from);
    const toDollars = parseFloat(discountFormData.to);
    const value = parseFloat(discountFormData.value);

    if (isNaN(fromDollars) || fromDollars < 0) {
      alert("Error: " + "Please enter a valid 'from' amount");
      return;
    }

    if (isNaN(toDollars) || toDollars < 0) {
      alert("Error: " + "Please enter a valid 'to' amount");
      return;
    }

    if (toDollars <= fromDollars) {
      alert("Error: " + "'To' amount must be greater than 'from' amount");
      return;
    }

    if (isNaN(value) || value < 0) {
      alert("Error: " + "Please enter a valid discount percentage");
      return;
    }

    if (value > 100) {
      alert("Error: " + "Percentage discount cannot exceed 100%");
      return;
    }

    try {
      setDiscountSaving(true);
      // Convert USD to cents before saving to database
      const discountData = {
        from: dollarsToCents(fromDollars),
        to: dollarsToCents(toDollars),
        value: value,
        percent: true,
      };

      // Load current discounts array
      const settingsPath = getSettingsPath();
      const discountsDoc = await getDoc(doc(db, settingsPath, "deliveryDiscounts"));
      let discountsList = [];
      if (discountsDoc.exists()) {
        discountsList = discountsDoc.data().discounts || [];
      }

      if (editingDiscountId) {
        // Update existing discount in array
        const index = discountsList.findIndex(d => d.id === editingDiscountId);
        if (index !== -1) {
          discountsList[index] = { ...discountsList[index], ...discountData };
        }
        alert("Success: " + "Discount updated successfully");
      } else {
        // Add new discount to array
        const newId = Date.now().toString();
        discountsList.push({ id: newId, ...discountData });
        alert("Success: " + "Discount created successfully");
      }

      // Save updated array back to settings
      await setDoc(doc(db, settingsPath, "deliveryDiscounts"), {
        discounts: discountsList,
        updatedAt: Timestamp.now(),
      }, { merge: true });

      cancelEditingDiscount();
      await loadDiscounts();
    } catch (error) {
      console.error("❌ Error saving discount:", error);
      alert("Error: " +
        `Failed to save discount: ${
          error.message || error.code || "Unknown error"
        }`
      );
    } finally {
      setDiscountSaving(false);
    }
  };

  const handleDiscountDelete = async (discountId, discountName) => {
    if (!window.confirm(`Delete Discount\n\nAre you sure you want to delete "${discountName}"? This action cannot be undone.`)) {
      return;
    }

            try {
              setDeletingDiscountId(discountId);
              
              // Load current discounts array
              const settingsPath = getSettingsPath();
      const discountsDoc = await getDoc(doc(db, settingsPath, "deliveryDiscounts"));
              let discountsList = [];
              if (discountsDoc.exists()) {
                discountsList = discountsDoc.data().discounts || [];
              }
              
              // Remove discount from array
              discountsList = discountsList.filter(d => d.id !== discountId);
              
              // Save updated array
              await setDoc(doc(db, settingsPath, "deliveryDiscounts"), {
                discounts: discountsList,
                updatedAt: Timestamp.now(),
              }, { merge: true });
              
      alert("Success: Discount deleted successfully");
              loadDiscounts();
            } catch (error) {
              console.error("❌ Error deleting discount:", error);
      alert("Error: Failed to delete discount: " + error.message);
            } finally {
              setDeletingDiscountId(null);
            }
  };

  const startAddingDiscount = () => {
    setEditingDiscountId(null);
    setDiscountFormData({ from: "", to: "", value: "" });
    setShowDiscountForm(true);
    // Scroll to form after a brief delay to ensure it's rendered
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  // ==================== SAVE ALL SETTINGS ====================

  const [savingAllSettings, setSavingAllSettings] = useState(false);

  const handleSaveAllSettings = async () => {
    try {
      setSavingAllSettings(true);
      const settingsPath = getSettingsPath();
      
      // Save the entire settings object from Zustand to the database
      if (settings && Object.keys(settings).length > 0) {
        // Save each settings document
        for (const [docName, docData] of Object.entries(settings)) {
          if (docData && typeof docData === 'object') {
            const docRef = doc(db, settingsPath, docName);
            await setDoc(docRef, {
              ...docData,
              updatedAt: Timestamp.now(),
            }, { merge: true });
          }
        }
        
        alert("Success: All settings saved successfully!");
      } else {
        alert("Info: No settings data to save");
      }
    } catch (error) {
      console.error("❌ Error saving all settings:", error);
      alert("Error: Failed to save settings: " + (error.message || "Unknown error"));
    } finally {
      setSavingAllSettings(false);
    }
  };

  // ==================== TOTALS DISCOUNTS FUNCTIONS ====================

  if (
    userLoading ||
    loading ||
    loadingAddOns ||
    salesTaxLoading ||
    loadingDiscounts ||
    startingAddressLoading ||
    mileageRateLoading ||
    loadingFeeLoading ||
    deliveryInfoLoading ||
    pickupInfoLoading ||
    emailTemplateLoading ||
    smsTemplateLoading ||
    storePhoneLoading ||
    storeEmailLoading ||
    deliveryPhoneLoading
  ) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color="#0E7C6B" />
        <Text style={styles.loadingText}>Loading settings...</Text>
      </View>
    );
  }

  if (!user) {
    return (
      <View style={styles.loadingContainer}>
        <View style={styles.loadingContent}>
          {/* Store Logo (if available) */}
          {storeLogo && (
            <Image 
              source={{ uri: storeLogo }} 
              style={styles.loadingLogo}
              resizeMode="contain"
            />
          )}
          
          {/* Bike Icon */}
          <View style={styles.loadingIconContainer}>
            <Text style={styles.loadingIcon}>🚴</Text>
          </View>

          {/* Loading Text */}
          <Text style={styles.loadingTitle}>Bonita Bikes</Text>
          <Text style={styles.loadingSubtitle}>
            Checking authentication...
          </Text>

          {/* Loading Bar */}
          <View style={styles.loadingBarContainer}>
            <View style={styles.loadingBar}>
              <View style={[styles.loadingBarFill, { width: `${loadingProgress}%` }]} />
            </View>
          </View>

          <Text style={styles.loadingHint}>
            Please wait
          </Text>
        </View>
      </View>
    );
  }

  const reviewRequestCurrent = {
    enabled: reviewRequestEnabled,
    url: reviewRequestUrl,
    delayHours: reviewRequestDelayHours,
    reAskSuppressDays: reviewRequestSuppressDays,
    smsTemplate: reviewRequestSmsTemplate,
    emailSubject: reviewRequestEmailSubject,
    emailBody: reviewRequestEmailBody,
  };
  const reviewRequestDirty =
    !!originalReviewRequest &&
    JSON.stringify(reviewRequestCurrent) !== JSON.stringify(originalReviewRequest);
  const reviewRequestActive = !!(originalReviewRequest?.enabled && originalReviewRequest?.url);

  return (
    <View style={styles.container}>
      <AuthHeader
        title={appHeaderTitle || "Product Rentals"} 
        subtitle={storeName ? `by ${storeName}` : ""} 
        isDesktop={isDesktop} 
        isAuthenticated={true} 
      />
      <View style={isDesktop ? styles.desktopWrapper : null}>
      <ScrollView style={styles.content}>
        {/* Bicycles Section */}
      <View style={styles.section}>
        <TouchableOpacity
          style={styles.sectionHeader}
          onPress={() => setShowBicycles(!showBicycles)}
        >
          <Text style={styles.sectionTitle}>{product1Title}</Text>
          <Text style={styles.toggleIcon}>{showBicycles ? "▼" : "▶"}</Text>
        </TouchableOpacity>

        {showBicycles && (
          <>
            {/* Bicycle Form Section - Collapsible */}
            <TouchableOpacity
              style={styles.formSectionHeader}
              onPress={() => setShowBicycleForm(!showBicycleForm)}
            >
              <Text style={[styles.subsectionTitle, { margin: 0 }]}>
                {editingId ? "Edit Bicycle" : "Add New Bicycle"}
              </Text>
              <Text style={styles.toggleIcon}>{showBicycleForm ? "▼" : "▶"}</Text>
            </TouchableOpacity>

            {showBicycleForm && (
            <View style={styles.formSection}>
              <TextInput
                style={styles.input}
                placeholder="Bicycle Name (e.g., Sun Drifter)"
                placeholderTextColor="#AEB8B6"
                value={formData.name}
                onChangeText={(text) =>
                  setFormData({ ...formData, name: text })
                }
              />
              <TextInput
                style={styles.input}
                placeholder="Price per day in dollars (e.g., 20.00)"
                placeholderTextColor="#AEB8B6"
                value={formData.price}
                onChangeText={(text) =>
                  setFormData({ ...formData, price: text })
                }
                keyboardType="numeric"
              />
              {/* Retail Price Checkbox and Field */}
              <View style={styles.checkboxContainer}>
                <TouchableOpacity
                  style={[styles.checkbox, formData.hasRetailPrice && styles.checkboxChecked]}
                  onPress={() =>
                    setFormData({ ...formData, hasRetailPrice: !formData.hasRetailPrice })
                  }
                >
                  {formData.hasRetailPrice && (
                    <Text style={styles.checkboxCheckmark}>✓</Text>
                  )}
                </TouchableOpacity>
                <Text style={styles.checkboxLabel}>
                  Product has retail price
                </Text>
              </View>
              {formData.hasRetailPrice && (
                <TextInput
                  style={styles.input}
                  placeholder="Retail Price (e.g., 299.99)"
                  placeholderTextColor="#AEB8B6"
                  value={formData.retailPriceCents}
                  onChangeText={(text) =>
                    setFormData({ ...formData, retailPriceCents: text })
                  }
                  keyboardType="numeric"
                />
              )}
              {/* Physical Item Checkbox */}
              <View style={styles.checkboxContainer}>
                <TouchableOpacity
                  style={[styles.checkbox, formData.physicalItem && styles.checkboxChecked]}
                  onPress={() =>
                    setFormData({ ...formData, physicalItem: !formData.physicalItem })
                  }
                >
                  {formData.physicalItem && (
                    <Text style={styles.checkboxCheckmark}>✓</Text>
                  )}
                </TouchableOpacity>
                <Text style={styles.checkboxLabel}>
                  Physical item (requires inventory tracking)
                </Text>
              </View>
              <TextInput
                style={styles.input}
                placeholder="Quantity available (e.g., 6)"
                placeholderTextColor="#AEB8B6"
                value={formData.quantity}
                onChangeText={(text) =>
                  setFormData({ ...formData, quantity: text })
                }
                keyboardType="numeric"
              />
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Description (shown in tooltip when customer clicks info icon)"
                placeholderTextColor="#AEB8B6"
                value={formData.description}
                onChangeText={(text) =>
                  setFormData({ ...formData, description: text })
                }
                multiline
                numberOfLines={3}
              />
              <TextInput
                style={styles.input}
                placeholder="Included Items (e.g., Lock, Basket, Bell)"
                placeholderTextColor="#AEB8B6"
                value={formData.includedItems}
                onChangeText={(text) =>
                  setFormData({ ...formData, includedItems: text })
                }
              />
              {/* Image Upload */}
              <View style={styles.imageUploadSection}>
                <Text style={styles.imageUploadLabel}>Product Image:</Text>
                {formData.imageUrl ? (
                  <View style={styles.imagePreviewContainer}>
                    <TouchableOpacity
                      onPress={() => {
                        setSelectedImageUrl(formData.imageUrl);
                        setImageModalVisible(true);
                      }}
                    >
                      <img
                        src={formData.imageUrl}
                        alt="Product preview"
                        style={styles.imagePreview}
                      />
                    </TouchableOpacity>
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={styles.removeImageButton}
                      onPress={() => setFormData({ ...formData, imageUrl: "" })}
                    >
                      <Text style={styles.removeImageText}>Remove</Text>
                    </TouchableOpacity>
                  </View>
                ) : null}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBikeImageUpload}
                  style={{ display: "none" }}
                  ref={bikeImageInputRef}
                />
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.uploadButton}
                  onPress={() => bikeImageInputRef.current?.click()}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? (
                    <ActivityIndicator color="#FFFFFF" size="small" />
                  ) : (
                    <Text style={styles.uploadButtonText}>
                      {formData.imageUrl ? "Change Image" : "Upload Image"}
                    </Text>
                  )}
                </TouchableOpacity>
              </View>
              <View style={styles.formButtons}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save</Text>
                  )}
                </TouchableOpacity>
                {editingId && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.cancelButton]}
                    onPress={cancelEditing}
                  >
                    <Text style={styles.buttonText}>Cancel</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
            )}

            {/* Bicycles List */}
            <View style={styles.listSection}>
              <View style={styles.listHeader}>
                <Text style={styles.subsectionTitle}>
                  {product1Title} ({bicycles.length})
                </Text>
                {!editingId && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.addButton]}
                    onPress={startAdding}
                  >
                    <Text style={styles.buttonText}>+ Add New</Text>
                  </TouchableOpacity>
                )}
              </View>
              {bicycles.length === 0 ? (
                <Text style={styles.emptyText}>
                  No {product1Title.toLowerCase()} found. Add your first {product1Title.toLowerCase().slice(0, -1)} above.
                </Text>
              ) : (
                bicycles.map((bike, index) => (
                  <View key={bike.id} style={styles.bikeCard}>
                    {/* Image - 40% width */}
                    <View style={styles.bikeImageSection}>
                      {bike.imageUrl ? (
                        <TouchableOpacity
                          onPress={() => {
                            setSelectedImageUrl(bike.imageUrl);
                            setImageModalVisible(true);
                          }}
                        >
                          <img
                            src={bike.imageUrl}
                            alt={bike.name || "Bicycle"}
                            style={styles.bikeThumbnail}
                          />
                        </TouchableOpacity>
                      ) : (
                        <View style={styles.bikeThumbnailPlaceholder}>
                          <Text style={styles.bikeThumbnailPlaceholderText}>
                            📷
                          </Text>
                        </View>
                      )}
                    </View>

                    {/* Details - 60% width */}
                    <View style={styles.bikeDetailsSection}>
                      <Text style={styles.bikeName}>
                        {bike.name || "Unnamed"}
                      </Text>
                      <Text style={styles.bikeDetails}>
                        ${(() => {
                          // centsToDollars returns a STRING, so calling .toFixed on it
                          // threw for any non-whole-dollar price. Coerce first.
                          const dollars = Number(centsToDollars(bike.price || 0));
                          return dollars % 1 === 0 ? dollars : dollars.toFixed(2);
                        })()}/day • Quantity: {bike.quantity || 0}
                      </Text>
                      {/* Physical Item Checkbox */}
                      <View style={styles.physicalItemRow}>
                        <TouchableOpacity
                          style={[styles.physicalItemCheckbox, bike.physicalItem && styles.physicalItemCheckboxChecked]}
                          onPress={async () => {
                            try {
                              const newPhysicalItemValue = !bike.physicalItem;
                              console.log("🔄 Toggling physicalItem for bike:", bike.id, "current:", bike.physicalItem, "new:", newPhysicalItemValue);
                              const products1Path = getCollectionPath("products1");
                              
                              // Update database
                              await updateDocument(products1Path, bike.id, {
                                physicalItem: newPhysicalItemValue,
                              });
                              
                              // Update Zustand store immediately
                              const setProducts1 = useProducts.getState().setProducts1;
                              const currentProducts1 = useProducts.getState().products1;
                              const updatedProducts1 = currentProducts1.map(p => 
                                p.id === bike.id 
                                  ? { ...p, physicalItem: newPhysicalItemValue, updatedAt: new Date() }
                                  : p
                              );
                              setProducts1(updatedProducts1);
                              
                              console.log("✅ Physical item updated successfully in DB and store");
                              loadBicycles();
                            } catch (error) {
                              console.error("❌ Error toggling physical item:", error);
                              alert("Error: Failed to update physical item setting");
                            }
                          }}
                        >
                          {bike.physicalItem && (
                            <Text style={styles.physicalItemCheckmark}>✓</Text>
                          )}
                        </TouchableOpacity>
                        <Text style={styles.physicalItemLabel}>Physical Item</Text>
                      </View>
                      <View style={styles.bikeActions}>
                        {/* Move Up Button */}
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={[styles.iconButton, index === 0 && styles.iconButtonDisabled]}
                          onPress={() => moveBicycleUp(index)}
                          disabled={index === 0}
                        >
                          <Text style={[styles.iconButtonText, index === 0 && styles.iconButtonTextDisabled]}>
                            ⬆️
                          </Text>
                        </TouchableOpacity>
                        
                        {/* Move Down Button */}
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={[styles.iconButton, index === bicycles.length - 1 && styles.iconButtonDisabled]}
                          onPress={() => moveBicycleDown(index)}
                          disabled={index === bicycles.length - 1}
                        >
                          <Text style={[styles.iconButtonText, index === bicycles.length - 1 && styles.iconButtonTextDisabled]}>
                            ⬇️
                          </Text>
                        </TouchableOpacity>
                        
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={styles.iconButton}
                          onPress={() => startEditing(bike)}
                          disabled={deletingId === bike.id}
                        >
                          <Text style={styles.iconButtonText}>✏️</Text>
                        </TouchableOpacity>
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={styles.iconButton}
                          onPress={() => handleDelete(bike.id, bike.name)}
                          disabled={deletingId === bike.id || saving}
                        >
                          {deletingId === bike.id ? (
                            <ActivityIndicator color="#C4453D" size="small" />
                          ) : (
                            <Text style={styles.iconButtonText}>🗑️</Text>
                          )}
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>
                ))
              )}
            </View>
          </>
        )}
      </View>

      {/* Add-Ons Section */}
      <View style={styles.section}>
        <TouchableOpacity
          style={styles.sectionHeader}
          onPress={() => setShowAddOns(!showAddOns)}
        >
          <Text style={styles.sectionTitle}>{product2Title}</Text>
          <Text style={styles.toggleIcon}>{showAddOns ? "▼" : "▶"}</Text>
        </TouchableOpacity>

        {showAddOns && (
          <>
            {/* Add-On Form Section - Collapsible */}
            <TouchableOpacity
              style={styles.formSectionHeader}
              onPress={() => setShowAddOnForm(!showAddOnForm)}
            >
              <Text style={[styles.subsectionTitle, { margin: 0 }]}>
                {editingAddOnId ? "Edit Add-On" : "Add New Add-On"}
              </Text>
              <Text style={styles.toggleIcon}>{showAddOnForm ? "▼" : "▶"}</Text>
            </TouchableOpacity>

            {showAddOnForm && (
            <View style={styles.formSection}>
              <TextInput
                style={styles.input}
                placeholder="Add-On Name (e.g., Helmet - Large/XL)"
                placeholderTextColor="#AEB8B6"
                value={addOnFormData.name}
                onChangeText={(text) =>
                  setAddOnFormData({ ...addOnFormData, name: text })
                }
              />
              <TextInput
                style={styles.input}
                placeholder="Price (e.g., 10)"
                placeholderTextColor="#AEB8B6"
                value={addOnFormData.price}
                onChangeText={(text) =>
                  setAddOnFormData({ ...addOnFormData, price: text })
                }
                keyboardType="numeric"
              />
              {/* Retail Price Checkbox and Field */}
              <View style={styles.checkboxContainer}>
                <TouchableOpacity
                  style={[styles.checkbox, addOnFormData.hasRetailPrice && styles.checkboxChecked]}
                  onPress={() =>
                    setAddOnFormData({ ...addOnFormData, hasRetailPrice: !addOnFormData.hasRetailPrice })
                  }
                >
                  {addOnFormData.hasRetailPrice && (
                    <Text style={styles.checkboxCheckmark}>✓</Text>
                  )}
                </TouchableOpacity>
                <Text style={styles.checkboxLabel}>
                  Product has retail price
                </Text>
              </View>
              {addOnFormData.hasRetailPrice && (
                <TextInput
                  style={styles.input}
                  placeholder="Retail Price (e.g., 49.99)"
                  placeholderTextColor="#AEB8B6"
                  value={addOnFormData.retailPriceCents}
                  onChangeText={(text) =>
                    setAddOnFormData({ ...addOnFormData, retailPriceCents: text })
                  }
                  keyboardType="numeric"
                />
              )}
              {/* Physical Item Checkbox */}
              <View style={styles.checkboxContainer}>
                <TouchableOpacity
                  style={[styles.checkbox, addOnFormData.physicalItem && styles.checkboxChecked]}
                  onPress={() =>
                    setAddOnFormData({ ...addOnFormData, physicalItem: !addOnFormData.physicalItem })
                  }
                >
                  {addOnFormData.physicalItem && (
                    <Text style={styles.checkboxCheckmark}>✓</Text>
                  )}
                </TouchableOpacity>
                <Text style={styles.checkboxLabel}>
                  Physical item (requires inventory tracking)
                </Text>
              </View>
              <TextInput
                style={styles.input}
                placeholder="Inventory (optional, e.g., 10)"
                placeholderTextColor="#AEB8B6"
                value={addOnFormData.inventory}
                onChangeText={(text) =>
                  setAddOnFormData({ ...addOnFormData, inventory: text })
                }
                keyboardType="numeric"
              />
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Description (shown in tooltip when customer clicks info icon)"
                placeholderTextColor="#AEB8B6"
                value={addOnFormData.description}
                onChangeText={(text) =>
                  setAddOnFormData({ ...addOnFormData, description: text })
                }
                multiline
                numberOfLines={3}
              />
              {/* Image Upload */}
              <View style={styles.imageUploadSection}>
                <Text style={styles.imageUploadLabel}>Product Image:</Text>
                {addOnFormData.imageUrl ? (
                  <View style={styles.imagePreviewContainer}>
                    <TouchableOpacity
                      onPress={() => {
                        setSelectedImageUrl(addOnFormData.imageUrl);
                        setImageModalVisible(true);
                      }}
                    >
                      <img
                        src={addOnFormData.imageUrl}
                        alt="Product preview"
                        style={styles.imagePreview}
                      />
                    </TouchableOpacity>
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={styles.removeImageButton}
                      onPress={() =>
                        setAddOnFormData({ ...addOnFormData, imageUrl: "" })
                      }
                    >
                      <Text style={styles.removeImageText}>Remove</Text>
                    </TouchableOpacity>
                  </View>
                ) : null}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAddOnImageUpload}
                  style={{ display: "none" }}
                  ref={addOnImageInputRef}
                />
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.uploadButton}
                  onPress={() => addOnImageInputRef.current?.click()}
                  disabled={uploadingAddOnImage}
                >
                  {uploadingAddOnImage ? (
                    <ActivityIndicator color="#FFFFFF" size="small" />
                  ) : (
                    <Text style={styles.uploadButtonText}>
                      {addOnFormData.imageUrl ? "Change Image" : "Upload Image"}
                    </Text>
                  )}
                </TouchableOpacity>
              </View>
              <View style={styles.optionSelector}>
                <Text style={styles.optionLabel}>Option Type:</Text>
                <View style={styles.optionButtons}>
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[
                      styles.optionButton,
                      addOnFormData.option === "per-booking" &&
                        styles.optionButtonActive,
                    ]}
                    onPress={() =>
                      setAddOnFormData({
                        ...addOnFormData,
                        option: "per-booking",
                      })
                    }
                  >
                    <Text
                      style={[
                        styles.optionButtonText,
                        addOnFormData.option === "per-booking" &&
                          styles.optionButtonTextActive,
                      ]}
                    >
                      Per-Booking
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[
                      styles.optionButton,
                      addOnFormData.option === "user-selection" &&
                        styles.optionButtonActive,
                    ]}
                    onPress={() =>
                      setAddOnFormData({
                        ...addOnFormData,
                        option: "user-selection",
                      })
                    }
                  >
                    <Text
                      style={[
                        styles.optionButtonText,
                        addOnFormData.option === "user-selection" &&
                          styles.optionButtonTextActive,
                      ]}
                    >
                      User-Selection
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[
                      styles.optionButton,
                      addOnFormData.option === "per-bike" &&
                        styles.optionButtonActive,
                    ]}
                    onPress={() =>
                      setAddOnFormData({
                        ...addOnFormData,
                        option: "per-bike",
                      })
                    }
                  >
                    <Text
                      style={[
                        styles.optionButtonText,
                        addOnFormData.option === "per-bike" &&
                          styles.optionButtonTextActive,
                      ]}
                    >
                      Per-Bike
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
              <View style={styles.formButtons}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleAddOnSave}
                  disabled={addOnSaving}
                >
                  {addOnSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save</Text>
                  )}
                </TouchableOpacity>
                {editingAddOnId && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.cancelButton]}
                    onPress={cancelEditingAddOn}
                  >
                    <Text style={styles.buttonText}>Cancel</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
            )}

            {/* Add-Ons List */}
            <View style={styles.listSection}>
              <View style={styles.listHeader}>
                <Text style={styles.subsectionTitle}>
                  {product2Title} ({addOns.length})
                </Text>
                {!editingAddOnId && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.addButton]}
                    onPress={startAddingAddOn}
                  >
                    <Text style={styles.buttonText}>+ Add New</Text>
                  </TouchableOpacity>
                )}
              </View>
              {addOns.length === 0 ? (
                <Text style={styles.emptyText}>
                  No {product2Title.toLowerCase()} found. Add your first {product2Title.toLowerCase().slice(0, -1)} above.
                </Text>
              ) : (
                addOns.map((addOn, index) => (
                  <View key={addOn.id} style={styles.bikeCard}>
                    {/* Image - 40% width */}
                    <View style={styles.bikeImageSection}>
                      {addOn.imageUrl ? (
                        <TouchableOpacity
                          onPress={() => {
                            setSelectedImageUrl(addOn.imageUrl);
                            setImageModalVisible(true);
                          }}
                        >
                          <img
                            src={addOn.imageUrl}
                            alt={addOn.name || "Add-On"}
                            style={styles.bikeThumbnail}
                          />
                        </TouchableOpacity>
                      ) : (
                        <View style={styles.bikeThumbnailPlaceholder}>
                          <Text style={styles.bikeThumbnailPlaceholderText}>
                            📷
                          </Text>
                        </View>
                      )}
                    </View>

                    {/* Details - 60% width */}
                    <View style={styles.bikeDetailsSection}>
                      <Text style={styles.bikeName}>
                        {addOn.name || "Unnamed"}
                      </Text>
                      <Text style={styles.bikeDetails}>
                        ${(() => {
                          const priceInDollars = centsToDollars(addOn.price || 0);
                          return priceInDollars % 1 === 0 ? Math.floor(priceInDollars) : priceInDollars.toFixed(2);
                        })()} • Option: {addOn.option || "N/A"}
                        {addOn.inventory !== undefined &&
                        addOn.inventory !== null
                          ? ` • Inventory: ${addOn.inventory}`
                          : ""}
                      </Text>
                      {/* Physical Item Checkbox */}
                      <View style={styles.physicalItemRow}>
                        <TouchableOpacity
                          style={[styles.physicalItemCheckbox, addOn.physicalItem && styles.physicalItemCheckboxChecked]}
                          onPress={async () => {
                            try {
                              const newPhysicalItemValue = !addOn.physicalItem;
                              console.log("🔄 Toggling physicalItem for add-on:", addOn.id, "current:", addOn.physicalItem, "new:", newPhysicalItemValue);
                              const products2Path = getCollectionPath("products2");
                              
                              // Update database
                              await updateDocument(products2Path, addOn.id, {
                                physicalItem: newPhysicalItemValue,
                              });
                              
                              // Update Zustand store immediately
                              const setProducts2 = useProducts.getState().setProducts2;
                              const currentProducts2 = useProducts.getState().products2;
                              const updatedProducts2 = currentProducts2.map(p => 
                                p.id === addOn.id 
                                  ? { ...p, physicalItem: newPhysicalItemValue, updatedAt: new Date() }
                                  : p
                              );
                              setProducts2(updatedProducts2);
                              
                              console.log("✅ Physical item updated successfully in DB and store");
                              loadAddOns();
                            } catch (error) {
                              console.error("❌ Error toggling physical item:", error);
                              alert("Error: Failed to update physical item setting");
                            }
                          }}
                        >
                          {addOn.physicalItem && (
                            <Text style={styles.physicalItemCheckmark}>✓</Text>
                          )}
                        </TouchableOpacity>
                        <Text style={styles.physicalItemLabel}>Physical Item</Text>
                      </View>
                      <View style={styles.bikeActions}>
                        {/* Move Up Button */}
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={[styles.iconButton, index === 0 && styles.iconButtonDisabled]}
                          onPress={() => moveAddOnUp(index)}
                          disabled={index === 0}
                        >
                          <Text style={[styles.iconButtonText, index === 0 && styles.iconButtonTextDisabled]}>
                            ⬆️
                          </Text>
                        </TouchableOpacity>
                        
                        {/* Move Down Button */}
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={[styles.iconButton, index === addOns.length - 1 && styles.iconButtonDisabled]}
                          onPress={() => moveAddOnDown(index)}
                          disabled={index === addOns.length - 1}
                        >
                          <Text style={[styles.iconButtonText, index === addOns.length - 1 && styles.iconButtonTextDisabled]}>
                            ⬇️
                          </Text>
                        </TouchableOpacity>
                        
                        {/* Edit Button */}
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={styles.iconButton}
                          onPress={() => startEditingAddOn(addOn)}
                          disabled={deletingAddOnId === addOn.id}
                        >
                          <Text style={styles.iconButtonText}>✏️</Text>
                        </TouchableOpacity>
                        
                        {/* Delete Button */}
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          style={styles.iconButton}
                          onPress={() =>
                            handleAddOnDelete(addOn.id, addOn.name)
                          }
                          disabled={deletingAddOnId === addOn.id || addOnSaving}
                        >
                          {deletingAddOnId === addOn.id ? (
                            <ActivityIndicator color="#C4453D" size="small" />
                          ) : (
                            <Text style={styles.iconButtonText}>🗑️</Text>
                          )}
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>
                ))
              )}
            </View>
          </>
        )}
      </View>

      {/* Settings Section */}
      <View style={styles.section}>
        <TouchableOpacity
          style={styles.sectionHeader}
          onPress={() => setShowSettings(!showSettings)}
        >
          <Text style={styles.sectionTitle}>⚙️ Settings</Text>
          <Text style={styles.toggleIcon}>{showSettings ? "▼" : "▶"}</Text>
        </TouchableOpacity>

        {showSettings && (
          <>
            {/* ========== SHOP INFORMATION ========== */}
            <Text style={[styles.subsectionTitle, { marginTop: 10, marginBottom: 15, fontSize: 18, color: "#0E7C6B" }]}>
              🏪 Shop Information
            </Text>

            {/* Store Display Name Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Store Display Name</Text>
              <Text style={styles.settingDescription}>
                Your store name (displayed on customer booking pages).
              </Text>
              <TextInput
                style={styles.input}
                placeholder="Store Display Name (e.g., Bonita Bikes)"
                placeholderTextColor="#AEB8B6"
                value={storeName}
                onChangeText={(text) => setStoreName(text)}
              />
              {storeName.trim() !== originalStoreName && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveStoreName}
                  disabled={storeNameSaving}
                >
                  {storeNameSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Store Name</Text>
                  )}
                </TouchableOpacity>
              )}
              {storeName !== "" && (
                <Text style={styles.currentSetting}>Current: {storeName}</Text>
              )}
            </View>

            {/* Legal Store Name Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Legal Store Name (waiver)</Text>
              <Text style={styles.settingDescription}>
                Official legal business name (used in rental waivers and legal documents).
              </Text>
              <TextInput
                style={styles.input}
                placeholder="Legal Store Name (e.g., Bonita Bikes LLC)"
                placeholderTextColor="#AEB8B6"
                value={legalStoreName}
                onChangeText={(text) => setLegalStoreName(text)}
              />
              {legalStoreName.trim() !== originalLegalStoreName && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveLegalStoreName}
                  disabled={legalStoreNameSaving}
                >
                  {legalStoreNameSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Legal Store Name</Text>
                  )}
                </TouchableOpacity>
              )}
              {legalStoreName !== "" && (
                <Text style={styles.currentSetting}>Current: {legalStoreName}</Text>
              )}
            </View>

            {/* Legal Store Address Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Legal Store Address (waiver)</Text>
              <Text style={styles.settingDescription}>
                Official legal business address (used in rental waivers and legal documents).
              </Text>
              <TextInput
                style={styles.input}
                placeholder="Legal Store Address (e.g., 9102 Bonita Beach Rd SE, Bonita Springs, FL 34135)"
                placeholderTextColor="#AEB8B6"
                value={legalStoreAddress}
                onChangeText={(text) => setLegalStoreAddress(text)}
              />
              {legalStoreAddress.trim() !== originalLegalStoreAddress && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveLegalStoreAddress}
                  disabled={legalStoreAddressSaving}
                >
                  {legalStoreAddressSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Legal Address</Text>
                  )}
                </TouchableOpacity>
              )}
              {legalStoreAddress !== "" && (
                <Text style={styles.currentSetting}>Current: {legalStoreAddress}</Text>
              )}
            </View>

            {/* Cancellation Policy Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Cancellation Policy</Text>
              <Text style={styles.settingDescription}>
                Your cancellation policy (displayed on customer booking pages when they request to cancel).
              </Text>
              <TextInput
                style={[styles.input, { minHeight: 80 }]}
                placeholder="Cancellation Policy"
                placeholderTextColor="#AEB8B6"
                value={cancellationPolicy}
                onChangeText={(text) => setCancellationPolicy(text)}
                multiline
                numberOfLines={4}
              />
              {cancellationPolicy.trim() !== "" && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveCancellationPolicy}
                  disabled={cancellationPolicySaving}
                >
                  {cancellationPolicySaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Cancellation Policy</Text>
                  )}
                </TouchableOpacity>
              )}
            </View>

            {/* App Header Title Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>App Header Title</Text>
              <Text style={styles.settingDescription}>
                Main title shown in the app header (default: "Product Rentals").
              </Text>
              <TextInput
                style={styles.input}
                placeholder="App Header Title (e.g., Bicycle Rentals, Kayak Rentals)"
                placeholderTextColor="#AEB8B6"
                value={appHeaderTitle}
                onChangeText={(text) => setAppHeaderTitle(text)}
              />
              {appHeaderTitle.trim() !== originalAppHeaderTitle && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveAppHeaderTitle}
                  disabled={appHeaderTitleSaving}
                >
                  {appHeaderTitleSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save App Header Title</Text>
                  )}
                </TouchableOpacity>
              )}
              {appHeaderTitle !== "" && (
                <Text style={styles.currentSetting}>Current: {appHeaderTitle}</Text>
              )}
            </View>

            {/* Store Logo Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Store Logo</Text>
              <Text style={styles.settingDescription}>
                Upload your store logo (displayed on customer booking pages). Image will be uploaded at original quality without compression.
              </Text>
              
              {storeLogo && (
                <View style={styles.logoPreviewContainer}>
                  <Text style={styles.logoPreviewLabel}>Current Logo:</Text>
                  <View style={styles.logoThumbnailWrapper}>
                    <img
                      src={storeLogo}
                      alt="Store logo preview"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        objectFit: "contain",
                        borderRadius: 6,
                        display: "block",
                      }}
                    />
                  </View>
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, { backgroundColor: "#C4453D", marginTop: 12 }]}
                    onPress={handleRemoveStoreLogo}
                  >
                    <Text style={styles.buttonText}>Remove Logo</Text>
                  </TouchableOpacity>
                </View>
              )}

              <input
                type="file"
                accept="image/*"
                onChange={handleStoreLogoUpload}
                style={{ display: "none" }}
                ref={storeLogoInputRef}
              />
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.button, styles.saveButton]}
                onPress={() => storeLogoInputRef.current?.click()}
                disabled={uploadingStoreLogo}
              >
                {uploadingStoreLogo ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.buttonText}>
                    {storeLogo ? "Change Logo" : "Upload Logo"}
                  </Text>
                )}
              </TouchableOpacity>
              {storeLogo && (
                <Text style={styles.currentSetting}>Logo uploaded ✓</Text>
              )}
            </View>

            {/* Store Phone Number Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Store Phone Number</Text>
              <Text style={styles.settingDescription}>
                Main phone number for the store (used in emails and customer
                communications).
              </Text>
              <TextInput
                style={styles.input}
                placeholder="Store Phone (e.g., 239-555-1234)"
                placeholderTextColor="#AEB8B6"
                value={storePhone}
                onChangeText={(text) => setStorePhone(formatPhoneNumber(text))}
                keyboardType="phone-pad"
              />
              {storePhone.trim() !== originalStorePhone && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveStorePhone}
                  disabled={storePhoneSaving}
                >
                  {storePhoneSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Store Phone</Text>
                  )}
                </TouchableOpacity>
              )}
              {storePhone !== "" && (
                <Text style={styles.currentSetting}>Current: {storePhone}</Text>
              )}
            </View>

            {/* Store Email Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Store Email</Text>
              <Text style={styles.settingDescription}>
                Main email address for the store (used as "from" address in
                emails).
              </Text>
              <TextInput
                style={styles.input}
                placeholder="Store Email (e.g., rentals@bonitabikes.com)"
                placeholderTextColor="#AEB8B6"
                value={storeEmail}
                onChangeText={(text) => setStoreEmail(text)}
                keyboardType="email-address"
                autoCapitalize="none"
              />
              {storeEmail.trim() !== originalStoreEmail && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveStoreEmail}
                  disabled={storeEmailSaving}
                >
                  {storeEmailSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Store Email</Text>
                  )}
                </TouchableOpacity>
              )}
              {storeEmail !== "" && (
                <Text style={styles.currentSetting}>Current: {storeEmail}</Text>
              )}
            </View>

            {/* Store Website Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Store Website</Text>
              <Text style={styles.settingDescription}>
                Your store's website URL (displayed on customer booking pages).
              </Text>
              <TextInput
                style={styles.input}
                placeholder="Store Website (e.g., https://bonitabikes.com)"
                placeholderTextColor="#AEB8B6"
                value={storeWebsite}
                onChangeText={(text) => setStoreWebsite(text)}
                keyboardType="url"
                autoCapitalize="none"
              />
              {storeWebsite.trim() !== originalStoreWebsite && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveStoreWebsite}
                  disabled={storeWebsiteSaving}
                >
                  {storeWebsiteSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Store Website</Text>
                  )}
                </TouchableOpacity>
              )}
              {storeWebsite !== "" && (
                <Text style={styles.currentSetting}>Current: {storeWebsite}</Text>
              )}
            </View>

            {/* Delivery Phone Number Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Text Phone Number</Text>
              <Text style={styles.settingDescription}>
                Phone number for texting booking updates and driver communications
              </Text>
              <TextInput
                style={styles.input}
                placeholder="Text Phone (e.g., 239-555-5678)"
                placeholderTextColor="#AEB8B6"
                value={deliveryPhone}
                onChangeText={(text) => setDeliveryPhone(formatPhoneNumber(text))}
                keyboardType="phone-pad"
              />
              {deliveryPhone.trim() !== originalDeliveryPhone && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveDeliveryPhone}
                  disabled={deliveryPhoneSaving}
                >
                  {deliveryPhoneSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Text Phone</Text>
                  )}
                </TouchableOpacity>
              )}
              {deliveryPhone !== "" && (
                <Text style={styles.currentSetting}>
                  Current: {deliveryPhone}
                </Text>
              )}
            </View>

            {/* Delivery Calling Phone Number Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Delivery Calling Phone Number</Text>
              <Text style={styles.settingDescription}>
                Phone number for delivery driver calling (used in auto-response messages).
              </Text>
              <TextInput
                style={styles.input}
                placeholder="Delivery Calling Phone (e.g., 239-555-5678)"
                placeholderTextColor="#AEB8B6"
                value={deliveryCallingPhone}
                onChangeText={(text) => setDeliveryCallingPhone(formatPhoneNumber(text))}
                keyboardType="phone-pad"
              />
              {deliveryCallingPhone.trim() !== originalDeliveryCallingPhone && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveDeliveryCallingPhone}
                  disabled={deliveryCallingPhoneSaving}
                >
                  {deliveryCallingPhoneSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Delivery Calling Phone</Text>
                  )}
                </TouchableOpacity>
              )}
              {deliveryCallingPhone !== "" && (
                <Text style={styles.currentSetting}>
                  Current: {deliveryCallingPhone}
                </Text>
              )}
            </View>

            {/* Call Messages Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>📞 Call Messages</Text>
              <Text style={styles.settingDescription}>
                Each message can be a typed message (text-to-speech) or an
                uploaded audio file (mp3/wav). Audio overrides text when
                present.
              </Text>
              {CALL_MESSAGES.map((m) => (
                <CallMessageEditor
                  key={m.key}
                  messageKey={m.key}
                  title={m.title}
                  subtitle={m.subtitle}
                  defaultText={m.defaultText}
                />
              ))}
            </View>

            {/* Hours of Operation Sub-Section */}
            <View style={styles.subSection}>
              <TouchableOpacity
                style={styles.hoursHeader}
                onPress={() => setShowHoursOfOperation(!showHoursOfOperation)}
              >
                <Text style={styles.subsectionTitle}>⏰ Hours of Operation</Text>
                <Text style={styles.toggleIcon}>{showHoursOfOperation ? "▼" : "▶"}</Text>
              </TouchableOpacity>
              <Text style={styles.settingDescription}>
                Set your store hours for each day of the week.
              </Text>

              {showHoursOfOperation && (
                <>
                  {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => {
                    const dayName = day.charAt(0).toUpperCase() + day.slice(1);
                    const dayData = hoursOfOperation[day] || { open: false, openTime: "09:00", closeTime: "17:00" };

                    return (
                      <View key={day} style={styles.dayRow}>
                        <View style={styles.dayNameContainer}>
                          <Text style={styles.dayName}>{dayName}</Text>
                          <TouchableOpacity
                            style={[styles.checkbox, dayData.open && styles.checkboxChecked]}
                            onPress={() => updateDayHours(day, "open", !dayData.open)}
                          >
                            {dayData.open && <Text style={styles.checkmark}>✓</Text>}
                          </TouchableOpacity>
                          <Text style={styles.openClosedLabel}>
                            {dayData.open ? "Open" : "Closed"}
                          </Text>
                        </View>

                        {dayData.open && (
                          <View style={styles.timePickers}>
                            <View style={styles.timePickerGroup}>
                              <Text style={styles.timeLabel}>Opens:</Text>
                              <input
                                type="time"
                                value={dayData.openTime}
                                onChange={(e) => updateDayHours(day, "openTime", e.target.value)}
                                style={{
                                  padding: 8,
                                  borderRadius: 5,
                                  borderWidth: 1,
                                  borderColor: "#DDE3E2",
                                  fontSize: 14,
                                }}
                              />
                            </View>
                            <View style={styles.timePickerGroup}>
                              <Text style={styles.timeLabel}>Closes:</Text>
                              <input
                                type="time"
                                value={dayData.closeTime}
                                onChange={(e) => updateDayHours(day, "closeTime", e.target.value)}
                                style={{
                                  padding: 8,
                                  borderRadius: 5,
                                  borderWidth: 1,
                                  borderColor: "#DDE3E2",
                                  fontSize: 14,
                                }}
                              />
                            </View>
                          </View>
                        )}
                      </View>
                    );
                  })}

                  {JSON.stringify(hoursOfOperation) !== JSON.stringify(originalHoursOfOperation) && (
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={[styles.button, styles.saveButton, { marginTop: 16 }]}
                      onPress={handleSaveHoursOfOperation}
                      disabled={hoursSaving}
                    >
                      {hoursSaving ? (
                        <ActivityIndicator color="#FFFFFF" />
                      ) : (
                        <Text style={styles.buttonText}>Save Hours of Operation</Text>
                      )}
                    </TouchableOpacity>
                  )}
                </>
              )}
            </View>

            {/* In-Store Pickup/Dropoff Hours Sub-Section */}
            <View style={[styles.subSection, { marginTop: 20 }]}>
              <TouchableOpacity
                style={styles.hoursHeader}
                onPress={() => setShowPickupHoursOfOperation(!showPickupHoursOfOperation)}
              >
                <Text style={styles.subsectionTitle}>🏪 In-Store Pickup/Dropoff Hours</Text>
                <Text style={styles.toggleIcon}>{showPickupHoursOfOperation ? "▼" : "▶"}</Text>
              </TouchableOpacity>
              <Text style={styles.settingDescription}>
                Set the hours when customers can pick up and drop off their rentals in-store.
              </Text>

              {showPickupHoursOfOperation && (
                <>
                  {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => {
                    const dayName = day.charAt(0).toUpperCase() + day.slice(1);
                    const dayData = pickupHoursOfOperation[day] || { open: false, openTime: "09:00", closeTime: "17:00" };

                    return (
                      <View key={day} style={styles.dayRow}>
                        <View style={styles.dayNameContainer}>
                          <Text style={styles.dayName}>{dayName}</Text>
                          <TouchableOpacity
                            style={[styles.checkbox, dayData.open && styles.checkboxChecked]}
                            onPress={() => updatePickupDayHours(day, "open", !dayData.open)}
                          >
                            {dayData.open && <Text style={styles.checkmark}>✓</Text>}
                          </TouchableOpacity>
                          <Text style={styles.openClosedLabel}>
                            {dayData.open ? "Open" : "Closed"}
                          </Text>
                        </View>

                        {dayData.open && (
                          <View style={styles.timePickers}>
                            <View style={styles.timePickerGroup}>
                              <Text style={styles.timeLabel}>Opens:</Text>
                              <input
                                type="time"
                                value={dayData.openTime}
                                onChange={(e) => updatePickupDayHours(day, "openTime", e.target.value)}
                                style={{
                                  padding: 8,
                                  borderRadius: 5,
                                  borderWidth: 1,
                                  borderColor: "#DDE3E2",
                                  fontSize: 14,
                                }}
                              />
                            </View>
                            <View style={styles.timePickerGroup}>
                              <Text style={styles.timeLabel}>Closes:</Text>
                              <input
                                type="time"
                                value={dayData.closeTime}
                                onChange={(e) => updatePickupDayHours(day, "closeTime", e.target.value)}
                                style={{
                                  padding: 8,
                                  borderRadius: 5,
                                  borderWidth: 1,
                                  borderColor: "#DDE3E2",
                                  fontSize: 14,
                                }}
                              />
                            </View>
                          </View>
                        )}
                      </View>
                    );
                  })}

                  {JSON.stringify(pickupHoursOfOperation) !== JSON.stringify(originalPickupHoursOfOperation) && (
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={[styles.button, styles.saveButton, { marginTop: 16 }]}
                      onPress={handleSavePickupHoursOfOperation}
                      disabled={pickupHoursSaving}
                    >
                      {pickupHoursSaving ? (
                        <ActivityIndicator color="#FFFFFF" />
                      ) : (
                        <Text style={styles.buttonText}>Save Pickup/Dropoff Hours</Text>
                      )}
                    </TouchableOpacity>
                  )}
                </>
              )}
            </View>

            {/* Product Category Titles Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Product Category Titles</Text>
              <Text style={styles.settingDescription}>
                Customize the names of your product categories (shown throughout the booking interface).
              </Text>
              
              <Text style={styles.label}>Product 1 Title (Main Products):</Text>
              <TextInput
                style={styles.input}
                placeholder="Product 1 Title (e.g., Bicycles, Kayaks, Skis)"
                placeholderTextColor="#AEB8B6"
                value={product1Title}
                onChangeText={(text) => setProduct1Title(text)}
              />
              {product1Title.trim() !== originalProduct1Title && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton, { marginBottom: 12 }]}
                  onPress={handleSaveProduct1Title}
                  disabled={product1TitleSaving}
                >
                  {product1TitleSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Product 1 Title</Text>
                  )}
                </TouchableOpacity>
              )}

              <Text style={[styles.label, { marginTop: 12 }]}>Product 2 Title (Add-Ons/Extras):</Text>
              <TextInput
                style={styles.input}
                placeholder="Product 2 Title (e.g., Add-Ons, Accessories, Extras)"
                placeholderTextColor="#AEB8B6"
                value={product2Title}
                onChangeText={(text) => setProduct2Title(text)}
              />
              {product2Title.trim() !== originalProduct2Title && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveProduct2Title}
                  disabled={product2TitleSaving}
                >
                  {product2TitleSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Product 2 Title</Text>
                  )}
                </TouchableOpacity>
              )}

              {(product1Title !== "Bicycles" || product2Title !== "Add-Ons") && (
                <Text style={[styles.currentSetting, { marginTop: 12 }]}>
                  Current: "{product1Title}" and "{product2Title}"
                </Text>
              )}
            </View>

            {/* ========== OPERATIONAL SETTINGS ========== */}
            <Text style={[styles.subsectionTitle, { marginTop: 30, marginBottom: 15, fontSize: 18, color: "#0E7C6B" }]}>
              ⚙️ Operational Settings
            </Text>

            {/* Sales Tax Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Sales Tax</Text>
              <Text style={styles.settingDescription}>
                Set the sales tax percentage to be applied to all bookings.
              </Text>
              <TextInput
                style={styles.input}
                placeholder="Sales Tax Percent (e.g., 7.5)"
                placeholderTextColor="#AEB8B6"
                value={salesTaxPercent}
                onChangeText={(text) => setSalesTaxPercent(text)}
                keyboardType="numeric"
              />
              {salesTaxPercent.trim() !== originalSalesTaxPercent && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveSalesTax}
                  disabled={salesTaxSaving}
                >
                  {salesTaxSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Sales Tax</Text>
                  )}
                </TouchableOpacity>
              )}
              {salesTaxPercent !== "" && (
                <Text style={styles.currentSetting}>
                  Current: {salesTaxPercent}%
                </Text>
              )}
            </View>

            {/* Cadence POS Connection Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Cadence POS Connection</Text>
              <Text style={styles.settingDescription}>
                Show this store's rentals on your Cadence/Warpspeed POS workorder board —
                deliveries and pickups going out each morning, and rentals due back (anything
                still on the board at close wasn't returned). Enter the three values from the
                POS admin's "Rental Link" card, plus the pairing token it generates.
              </Text>
              <TouchableOpacity
                style={{ flexDirection: "row", alignItems: "center", marginBottom: 12 }}
                onPress={() => setCadenceConn({ ...cadenceConn, enabled: !cadenceConn.enabled })}
              >
                <View
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    borderWidth: 2,
                    borderColor: "#0E7C6B",
                    backgroundColor: cadenceConn.enabled ? "#0E7C6B" : "transparent",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 10,
                  }}
                >
                  {cadenceConn.enabled && (
                    <Text style={{ color: "#FFFFFF", fontSize: 14, fontWeight: "bold" }}>✓</Text>
                  )}
                </View>
                <Text style={{ fontSize: 15, color: "#2D3A37" }}>Enable POS board sync</Text>
              </TouchableOpacity>
              {/* Static labels on every field — placeholders vanish once filled
                  (or once the browser autofills), leaving no way to tell which
                  value goes where. autoComplete off everywhere and NO
                  secureTextEntry on the token: a password-styled input made
                  Chrome treat URL+token as a login form and autofill saved
                  credentials into them. */}
              <Text style={cadenceFieldLabelStyle}>POS Tenant ID</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. 1234"
                placeholderTextColor="#AEB8B6"
                value={cadenceConn.tenantID}
                onChangeText={(text) => setCadenceConn({ ...cadenceConn, tenantID: text.trim() })}
                autoComplete="off"
              />
              <Text style={cadenceFieldLabelStyle}>POS Store ID</Text>
              <TextInput
                style={styles.input}
                placeholder="e.g. 999"
                placeholderTextColor="#AEB8B6"
                value={cadenceConn.storeID}
                onChangeText={(text) => setCadenceConn({ ...cadenceConn, storeID: text.trim() })}
                autoComplete="off"
              />
              <Text style={cadenceFieldLabelStyle}>Endpoint URL</Text>
              <TextInput
                style={styles.input}
                placeholder="https://…cloudfunctions.net/rentalInjectionEndpoint"
                placeholderTextColor="#AEB8B6"
                value={cadenceConn.endpointUrl}
                onChangeText={(text) => setCadenceConn({ ...cadenceConn, endpointUrl: text.trim() })}
                autoComplete="off"
              />
              <Text style={cadenceFieldLabelStyle}>Pairing Token</Text>
              <TextInput
                style={styles.input}
                placeholder="Paste the token generated by the POS Rental Link card"
                placeholderTextColor="#AEB8B6"
                value={cadenceConn.pairingToken}
                onChangeText={(text) => setCadenceConn({ ...cadenceConn, pairingToken: text.trim() })}
                autoComplete="off"
              />
              {JSON.stringify(cadenceConn) !== originalCadenceConn && (
                <TouchableOpacity
                  dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveCadenceConnection}
                  disabled={cadenceSaving}
                >
                  {cadenceSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Cadence Connection</Text>
                  )}
                </TouchableOpacity>
              )}
              {cadenceConn.enabled && originalCadenceConn && JSON.stringify(cadenceConn) === originalCadenceConn && (
                <Text style={styles.currentSetting}>
                  Syncing daily at 5:30 AM — rentals resolve off the board automatically at
                  check-out / check-in / cancellation.
                </Text>
              )}
            </View>

            {/* Card Processing Fee Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Credit Card Processing Fee</Text>
              <Text style={styles.settingDescription}>
                Set the credit card processing fee percentage. This fee covers the non-refundable processing costs from payment processors. Default is 3%. Maximum is 4.99%.
              </Text>
              <TextInput
                style={styles.input}
                placeholder="Processing Fee Percent (e.g., 3)"
                placeholderTextColor="#AEB8B6"
                value={cardProcessingFee}
                onChangeText={(text) => {
                  // Only allow numbers under 5
                  const num = parseFloat(text);
                  if (text === "" || (!isNaN(num) && num < 5)) {
                    setCardProcessingFee(text);
                  }
                }}
                keyboardType="numeric"
              />
              
              {/* Checkboxes for Purchases and Refunds */}
              <View style={{ marginTop: 16, marginBottom: 16 }}>
                <TouchableOpacity
                  style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}
                  onPress={() => setCardProcessingFeeForPurchases(!cardProcessingFeeForPurchases)}
                >
                  <View style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    borderWidth: 2,
                    borderColor: cardProcessingFeeForPurchases ? '#0E7C6B' : '#8A9794',
                    backgroundColor: cardProcessingFeeForPurchases ? '#0E7C6B' : '#FFFFFF',
                    marginRight: 8,
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {cardProcessingFeeForPurchases && (
                      <Text style={{ color: '#FFFFFF', fontSize: 14, fontWeight: 'bold' }}>✓</Text>
                    )}
                  </View>
                  <Text style={{ fontSize: 14, color: '#2E3A38' }}>Apply to Purchases</Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={{ flexDirection: 'row', alignItems: 'center' }}
                  onPress={() => setCardProcessingFeeForRefunds(!cardProcessingFeeForRefunds)}
                >
                  <View style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    borderWidth: 2,
                    borderColor: cardProcessingFeeForRefunds ? '#0E7C6B' : '#8A9794',
                    backgroundColor: cardProcessingFeeForRefunds ? '#0E7C6B' : '#FFFFFF',
                    marginRight: 8,
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {cardProcessingFeeForRefunds && (
                      <Text style={{ color: '#FFFFFF', fontSize: 14, fontWeight: 'bold' }}>✓</Text>
                    )}
                  </View>
                  <Text style={{ fontSize: 14, color: '#2E3A38' }}>Apply to Refunds</Text>
                </TouchableOpacity>
              </View>
              
              {(cardProcessingFee.trim() !== originalCardProcessingFee || 
                cardProcessingFeeForPurchases !== originalCardProcessingFeeForPurchases ||
                cardProcessingFeeForRefunds !== originalCardProcessingFeeForRefunds) && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveCardProcessingFee}
                  disabled={cardProcessingFeeSaving}
                >
                  {cardProcessingFeeSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Processing Fee</Text>
                  )}
                </TouchableOpacity>
              )}
              {cardProcessingFee !== "" && (
                <Text style={styles.currentSetting}>
                  Current: {cardProcessingFee}%
                </Text>
              )}
            </View>

            {/* Cancellation Policy Windows Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Cancellation Policy Windows</Text>
              <Text style={styles.settingDescription}>
                Configure the time windows and penalties for cancellations. Choose between percentage-based penalties (% of booking) or days-based penalties (number of rental days forfeited).
              </Text>
              
              {/* Penalty Type Selection */}
              <View style={{ marginBottom: 16 }}>
                <Text style={{ fontSize: 14, fontWeight: "600", color: "#2E3A38", marginBottom: 8 }}>
                  Penalty Calculation Method:
                </Text>
                <View style={{ flexDirection: "row", gap: 16 }}>
                  <TouchableOpacity
                    onPress={() => setPenaltyType("percentage")}
                    style={{ flexDirection: "row", alignItems: "center" }}
                  >
                    <View style={{
                      width: 20,
                      height: 20,
                      borderRadius: 10,
                      borderWidth: 2,
                      borderColor: "#0E7C6B",
                      marginRight: 8,
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: penaltyType === "percentage" ? "#0E7C6B" : "#FFFFFF"
                    }}>
                      {penaltyType === "percentage" && (
                        <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: "#FFFFFF" }} />
                      )}
                    </View>
                    <Text style={{ fontSize: 14, color: "#2E3A38" }}>Percentage (%)</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => setPenaltyType("days")}
                    style={{ flexDirection: "row", alignItems: "center" }}
                  >
                    <View style={{
                      width: 20,
                      height: 20,
                      borderRadius: 10,
                      borderWidth: 2,
                      borderColor: "#0E7C6B",
                      marginRight: 8,
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: penaltyType === "days" ? "#0E7C6B" : "#FFFFFF"
                    }}>
                      {penaltyType === "days" && (
                        <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: "#FFFFFF" }} />
                      )}
                    </View>
                    <Text style={{ fontSize: 14, color: "#2E3A38" }}>Days</Text>
                  </TouchableOpacity>
                </View>
              </View>

              {/* Time Windows Table */}
              <View style={{ borderWidth: 1, borderColor: "#DDE3E2", borderRadius: 6, overflow: "hidden" }}>
                {/* Header Row */}
                <View style={{ flexDirection: "row", backgroundColor: "#F4F6F6", padding: 12, borderBottomWidth: 1, borderBottomColor: "#DDE3E2" }}>
                  <Text style={{ flex: 1, fontWeight: "bold", fontSize: 13, color: "#2E3A38" }}>Time Window (hours)</Text>
                  <Text style={{ flex: 1, fontWeight: "bold", fontSize: 13, color: "#2E3A38", textAlign: "center" }}>
                    % Penalty
                  </Text>
                  <Text style={{ flex: 1, fontWeight: "bold", fontSize: 13, color: "#2E3A38", textAlign: "center" }}>
                    Days Penalty
                  </Text>
                </View>
                
                {/* Data Rows */}
                {cancellationWindows.map((window, index) => (
                  <View key={index} style={{ flexDirection: "row", padding: 12, borderBottomWidth: index < cancellationWindows.length - 1 ? 1 : 0, borderBottomColor: "#DDE3E2", backgroundColor: "#FFFFFF" }}>
                    {/* Hours Input */}
                    <View style={{ flex: 1, paddingRight: 8 }}>
                      <TextInput
                        style={{
                          borderWidth: 1,
                          borderColor: "#DDE3E2",
                          borderRadius: 5,
                          padding: 8,
                          fontSize: 14,
                          color: "#2E3A38",
                          backgroundColor: "#F4F6F6",
                        }}
                        placeholder="Hours"
                        placeholderTextColor="#AEB8B6"
                        value={window.hours.toString()}
                        onChangeText={(text) => {
                          const newWindows = [...cancellationWindows];
                          newWindows[index].hours = parseInt(text) || 0;
                          setCancellationWindows(newWindows);
                        }}
                        keyboardType="numeric"
                      />
                      {index === 0 && <Text style={{ fontSize: 11, color: "#5C6B68", marginTop: 4 }}>48+ hrs</Text>}
                      {index === 1 && <Text style={{ fontSize: 11, color: "#5C6B68", marginTop: 4 }}>24-48 hrs</Text>}
                      {index === 2 && <Text style={{ fontSize: 11, color: "#5C6B68", marginTop: 4 }}>0-24 hrs</Text>}
                    </View>
                    
                    {/* Percent Penalty Input */}
                    <View style={{ flex: 1, paddingHorizontal: 4 }}>
                      <TextInput
                        style={{
                          borderWidth: 1,
                          borderColor: penaltyType === "percentage" ? "#0E7C6B" : "#DDE3E2",
                          borderRadius: 5,
                          padding: 8,
                          fontSize: 14,
                          color: "#2E3A38",
                          backgroundColor: penaltyType === "percentage" ? "#E3F2EF" : "#F4F6F6",
                          textAlign: "center",
                        }}
                        placeholder="0"
                        placeholderTextColor="#AEB8B6"
                        value={window.percentPenalty.toString()}
                        onChangeText={(text) => {
                          const newWindows = [...cancellationWindows];
                          newWindows[index].percentPenalty = parseInt(text) || 0;
                          setCancellationWindows(newWindows);
                        }}
                        keyboardType="numeric"
                      />
                    </View>
                    
                    {/* Days Penalty Input */}
                    <View style={{ flex: 1, paddingLeft: 4 }}>
                      <TextInput
                        style={{
                          borderWidth: 1,
                          borderColor: penaltyType === "days" ? "#0E7C6B" : "#DDE3E2",
                          borderRadius: 5,
                          padding: 8,
                          fontSize: 14,
                          color: "#2E3A38",
                          backgroundColor: penaltyType === "days" ? "#E3F2EF" : "#F4F6F6",
                          textAlign: "center",
                        }}
                        placeholder="0"
                        placeholderTextColor="#AEB8B6"
                        value={window.daysPenalty.toString()}
                        onChangeText={(text) => {
                          const newWindows = [...cancellationWindows];
                          newWindows[index].daysPenalty = parseInt(text) || 0;
                          setCancellationWindows(newWindows);
                        }}
                        keyboardType="numeric"
                      />
                    </View>
                  </View>
                ))}
              </View>
              
              {/* Save Button */}
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.button, styles.saveButton, { marginTop: 16 }]}
                onPress={handleSaveCancellationWindows}
                disabled={cancellationWindowsSaving}
              >
                {cancellationWindowsSaving ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.buttonText}>Save Cancellation Policy</Text>
                )}
              </TouchableOpacity>
              
              {/* Info Text */}
              <Text style={[styles.settingDescription, { marginTop: 12, fontSize: 12, color: "#5C6B68" }]}>
                {penaltyType === "percentage" 
                  ? "Currently using percentage-based penalties. E.g., 25% penalty means customer gets 75% refund."
                  : "Currently using days-based penalties. E.g., 2 days penalty means customer forfeits the value of 2 rental days."}
              </Text>
            </View>

            {/* Starting Address Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Starting Address</Text>
              <Text style={styles.settingDescription}>
                Set the default starting location address for distance
                calculations.
              </Text>
              <TextInput
                style={styles.input}
                placeholder="Enter starting address..."
                placeholderTextColor="#AEB8B6"
                value={startingAddressInput}
                onChangeText={(text) => setStartingAddressInput(text)}
              />
              {startingAddressInput.trim() !== originalStartingAddress && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveStartingAddress}
                  disabled={startingAddressSaving}
                >
                  {startingAddressSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Starting Address</Text>
                  )}
                </TouchableOpacity>
              )}
              {startingAddress !== "" && (
                <Text style={styles.currentSetting}>
                  Current: {startingAddress}
                </Text>
              )}
            </View>

            {/* Mileage Rate Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Mileage Rate</Text>
              <Text style={styles.settingDescription}>
                Set the per-mile rate for delivery and pickup distance
                calculations.
              </Text>
              <TextInput
                style={styles.input}
                placeholder="Mileage Rate (e.g., 2.50)"
                placeholderTextColor="#AEB8B6"
                value={mileageRate}
                onChangeText={(text) => setMileageRate(text)}
                keyboardType="numeric"
              />
              {mileageRate.trim() !== originalMileageRate && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveMileageRate}
                  disabled={mileageRateSaving}
                >
                  {mileageRateSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Mileage Rate</Text>
                  )}
                </TouchableOpacity>
              )}
              {mileageRate !== "" && (
                <Text style={styles.currentSetting}>
                  Current: ${mileageRate}/mile
                </Text>
              )}
            </View>

            {/* Loading Fee Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Loading fee (per trip):</Text>
              <Text style={styles.settingDescription}>
                Set the loading fee applied to each delivery and pickup
                direction.
              </Text>
              <TextInput
                style={styles.input}
                placeholder="Loading fee (e.g., 25.00)"
                placeholderTextColor="#AEB8B6"
                value={loadingFee}
                onChangeText={(text) => setLoadingFee(text)}
                keyboardType="numeric"
              />
              {loadingFee.trim() !== originalLoadingFee && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveLoadingFee}
                  disabled={loadingFeeSaving}
                >
                  {loadingFeeSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Loading Fee</Text>
                  )}
                </TouchableOpacity>
              )}
              {loadingFee !== "" && (
                <Text style={styles.currentSetting}>
                  Current: ${loadingFee}
                </Text>
              )}
            </View>

            {/* ========== CUSTOMER-FACING MESSAGES ========== */}
            <Text style={[styles.subsectionTitle, { marginTop: 30, marginBottom: 15, fontSize: 18, color: "#0E7C6B" }]}>
              💬 Customer-Facing Messages
            </Text>

            {/* Delivery Info Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Delivery Info Message</Text>
              <Text style={styles.settingDescription}>
                This message will appear below the "Home Pickup Needed" and
                "Home Drop-Off Needed" checkboxes when selected.
              </Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Enter delivery instructions (e.g., 'We'll call 30 minutes before arrival')"
                placeholderTextColor="#AEB8B6"
                value={deliveryInfo}
                onChangeText={(text) => setDeliveryInfo(text)}
                multiline
                numberOfLines={4}
              />
              {deliveryInfo.trim() !== originalDeliveryInfo && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveDeliveryInfo}
                  disabled={deliveryInfoSaving}
                >
                  {deliveryInfoSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Delivery Info</Text>
                  )}
                </TouchableOpacity>
              )}
              {deliveryInfo !== "" && (
                <Text style={styles.currentSetting}>
                  Current: {deliveryInfo}
                </Text>
              )}
            </View>

            {/* Pickup Info Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>
                In-Store Pickup Info Message
              </Text>
              <Text style={styles.settingDescription}>
                This message will appear below the "In-Store Pickup" radio
                button when selected.
              </Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Enter pickup instructions (e.g., 'Pick up at 123 Main St between 9am-5pm')"
                placeholderTextColor="#AEB8B6"
                value={pickupInfo}
                onChangeText={(text) => setPickupInfo(text)}
                multiline
                numberOfLines={4}
              />
              {pickupInfo.trim() !== originalPickupInfo && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSavePickupInfo}
                  disabled={pickupInfoSaving}
                >
                  {pickupInfoSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Pickup Info</Text>
                  )}
                </TouchableOpacity>
              )}
              {pickupInfo !== "" && (
                <Text style={styles.currentSetting}>Current: {pickupInfo}</Text>
              )}
            </View>

            {/* Confirmation Heading - Delivery/Pickup */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Confirmation Heading (Delivery/Pickup)</Text>
              <Text style={styles.settingDescription}>
                This heading text will appear at the top of the booking confirmation page for customers who selected delivery or pickup options.
              </Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Enter heading for delivery/pickup bookings (e.g., 'Your rental is confirmed! We'll see you soon.')"
                placeholderTextColor="#AEB8B6"
                value={confirmationHeadingTransportation}
                onChangeText={(text) => setConfirmationHeadingTransportation(text)}
                multiline
                numberOfLines={3}
              />
              {confirmationHeadingTransportation.trim() !== originalConfirmationHeadingTransportation && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveConfirmationHeadingTransportation}
                  disabled={confirmationHeadingTransportationSaving}
                >
                  {confirmationHeadingTransportationSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Delivery/Pickup Heading</Text>
                  )}
                </TouchableOpacity>
              )}
              {confirmationHeadingTransportation !== "" && (
                <Text style={styles.currentSetting}>Current: {confirmationHeadingTransportation}</Text>
              )}
            </View>

            {/* Confirmation Heading - In-Store Pickup */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Confirmation Heading (In-Store Pickup)</Text>
              <Text style={styles.settingDescription}>
                This heading text will appear at the top of the booking confirmation page for customers who selected in-store pickup.
              </Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Enter heading for in-store pickup (e.g., 'Your rental is confirmed! Come pick up at our store.')"
                placeholderTextColor="#AEB8B6"
                value={confirmationHeadingInStorePickup}
                onChangeText={(text) => setConfirmationHeadingInStorePickup(text)}
                multiline
                numberOfLines={3}
              />
              {confirmationHeadingInStorePickup.trim() !== originalConfirmationHeadingInStorePickup && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveConfirmationHeadingInStorePickup}
                  disabled={confirmationHeadingInStorePickupSaving}
                >
                  {confirmationHeadingInStorePickupSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save In-Store Pickup Heading</Text>
                  )}
                </TouchableOpacity>
              )}
              {confirmationHeadingInStorePickup !== "" && (
                <Text style={styles.currentSetting}>Current: {confirmationHeadingInStorePickup}</Text>
              )}
            </View>

            {/* Customer Blurb Sub-Section */}
            <View style={styles.subSection}>
              <Text style={styles.subsectionTitle}>Customer Blurb</Text>
              <Text style={styles.settingDescription}>
                A message or note displayed at the bottom of the customer booking
                view page (e.g., hours of operation, thank you message).
              </Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                placeholder="Enter a message for customers (e.g., 'Thank you for choosing us! Questions? Call us at...')"
                placeholderTextColor="#AEB8B6"
                value={customerBlurb}
                onChangeText={(text) => setCustomerBlurb(text)}
                multiline
                numberOfLines={4}
              />
              {customerBlurb.trim() !== originalCustomerBlurb && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton]}
                  onPress={handleSaveCustomerBlurb}
                  disabled={customerBlurbSaving}
                >
                  {customerBlurbSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save Customer Blurb</Text>
                  )}
                </TouchableOpacity>
              )}
              {customerBlurb !== "" && (
                <Text style={styles.currentSetting}>
                  Current: {customerBlurb}
                </Text>
              )}
            </View>

          </>
        )}
      </View>

      {/* Discounts Section */}
      <View style={styles.section}>
        <TouchableOpacity
          style={styles.sectionHeader}
          onPress={() => setShowDiscounts(!showDiscounts)}
        >
          <Text style={styles.sectionTitle}>Delivery Discounts</Text>
          <Text style={styles.toggleIcon}>{showDiscounts ? "▼" : "▶"}</Text>
        </TouchableOpacity>

        {showDiscounts && (
          <>
            {/* Enable/Disable Delivery Discounts Checkbox */}
            <View style={{ marginBottom: 20, padding: 12, backgroundColor: '#F4F6F6', borderRadius: 6 }}>
                <TouchableOpacity
                style={{ flexDirection: 'row', alignItems: 'center',  }}
                onPress={handleToggleUseDeliveryDiscounts}
                disabled={useDeliveryDiscountsSaving}
              >
                <View style={[
                  styles.checkbox,
                  useDeliveryDiscounts && styles.checkboxChecked
                ]}>
                  {useDeliveryDiscounts && (
                    <Text style={styles.checkmark}>✓</Text>
                  )}
              </View>
                <Text style={{ fontSize: 16, fontWeight: '600', color: '#2E3A38', flex: 1 }}>
                  Enable delivery discounts
              </Text>
                {useDeliveryDiscountsSaving && (
                  <ActivityIndicator color="#0E7C6B" size="small" />
                )}
              </TouchableOpacity>
              <Text style={[styles.settingDescription, { marginTop: 8, marginLeft: 34 }]}>
                When enabled, delivery/pickup costs are discounted based on order amount (product subtotal)
              </Text>
            </View>

            <TouchableOpacity
              style={styles.formSectionHeader}
              onPress={() => setShowDiscountForm(!showDiscountForm)}
            >
              <Text style={[styles.subsectionTitle, { margin: 0 }]}>
              {editingDiscountId ? "Edit Discount" : "Add New Discount"}
            </Text>
              <Text style={styles.toggleIcon}>{showDiscountForm ? "▼" : "▶"}</Text>
            </TouchableOpacity>

            {showDiscountForm && (
            <>
            <Text style={[styles.settingDescription, { fontSize: 12, marginBottom: 10, marginTop: 10 }]}>
              Set order amount range (product subtotal) and percentage discount on delivery/pickup fees
            </Text>
            <View style={{ flexDirection: 'row',  marginBottom: 10 }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 13, color: '#5C6B68', marginBottom: 5 }}>From ($)</Text>
            <TextInput
              style={styles.input}
                  placeholder="0"
              placeholderTextColor="#AEB8B6"
                  value={discountFormData.from}
              onChangeText={(text) =>
                    setDiscountFormData({ ...discountFormData, from: text })
              }
                  keyboardType="numeric"
            />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 13, color: '#5C6B68', marginBottom: 5 }}>To ($)</Text>
            <TextInput
              style={styles.input}
                  placeholder="100"
                  placeholderTextColor="#AEB8B6"
                  value={discountFormData.to}
                  onChangeText={(text) =>
                    setDiscountFormData({ ...discountFormData, to: text })
                  }
                  keyboardType="numeric"
                />
              </View>
            </View>
            <Text style={{ fontSize: 13, color: '#5C6B68', marginBottom: 5 }}>Discount Percentage (%) - Applied to Delivery/Pickup Fees</Text>
            <TextInput
              style={styles.input}
              placeholder="e.g., 10"
              placeholderTextColor="#AEB8B6"
              value={discountFormData.value}
              onChangeText={(text) =>
                setDiscountFormData({ ...discountFormData, value: text })
              }
              keyboardType="numeric"
            />
            <View style={styles.formButtons}>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.button, styles.saveButton]}
                onPress={handleDiscountSave}
                disabled={discountSaving}
              >
                {discountSaving ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.buttonText}>Save</Text>
                )}
              </TouchableOpacity>
              {editingDiscountId && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.cancelButton]}
                  onPress={cancelEditingDiscount}
                >
                  <Text style={styles.buttonText}>Cancel</Text>
                </TouchableOpacity>
              )}
            </View>
            </>
            )}

            {/* Discounts List */}
            <View style={styles.listSection}>
              <View style={styles.listHeader}>
                <Text style={styles.subsectionTitle}>
                  Discounts ({discounts.length})
                </Text>
                {!editingDiscountId && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.addButton]}
                    onPress={startAddingDiscount}
                  >
                    <Text style={styles.buttonText}>+ Add New</Text>
                  </TouchableOpacity>
                )}
              </View>
              {discounts.length === 0 ? (
                <Text style={styles.emptyText}>
                  No discounts found. Add your first discount above.
                </Text>
              ) : (
                discounts.map((discount) => (
                  <View key={discount.id} style={styles.bikeCard}>
                    <View style={styles.bikeInfo}>
                      <Text style={styles.bikeName}>
                        {discount.from !== undefined && discount.to !== undefined
                          ? `$${centsToDollars(discount.from)}-$${centsToDollars(discount.to)} order`
                          : discount.name || "Unnamed"}
                      </Text>
                      <Text style={styles.bikeDetails}>
                        {discount.value || 0}% off delivery/pickup fees
                      </Text>
                    </View>
                    <View style={styles.bikeActions}>
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={styles.iconButton}
                        onPress={() => startEditingDiscount(discount)}
                        disabled={deletingDiscountId === discount.id}
                      >
                        <Text style={styles.iconButtonText}>✏️</Text>
                      </TouchableOpacity>
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={styles.iconButton}
                        onPress={() =>
                          handleDiscountDelete(
                            discount.id, 
                            discount.from !== undefined && discount.to !== undefined
                              ? `$${centsToDollars(discount.from)}-$${centsToDollars(discount.to)} order`
                              : discount.name || "this discount"
                          )
                        }
                        disabled={
                          deletingDiscountId === discount.id || discountSaving
                        }
                      >
                        {deletingDiscountId === discount.id ? (
                          <ActivityIndicator color="#C4453D" size="small" />
                        ) : (
                          <Text style={styles.iconButtonText}>🗑️</Text>
                        )}
                      </TouchableOpacity>
                    </View>
                  </View>
                ))
              )}
            </View>
          </>
        )}
      </View>

      {/* Duration Discounts Section */}
      <View style={styles.section}>
        <TouchableOpacity
          style={styles.sectionHeader}
          onPress={() => setShowDurationDiscounts(!showDurationDiscounts)}
        >
          <Text style={styles.sectionTitle}>Rental Duration Discounts</Text>
          <Text style={styles.toggleIcon}>{showDurationDiscounts ? "▼" : "▶"}</Text>
        </TouchableOpacity>

        {showDurationDiscounts && (
          <>
            {/* Enable/Disable Duration Discounts Checkbox */}
            <View style={{ marginBottom: 20, padding: 12, backgroundColor: '#F4F6F6', borderRadius: 6 }}>
              <TouchableOpacity
                style={{ flexDirection: 'row', alignItems: 'center',  }}
                onPress={handleToggleUseDurationDiscounts}
                disabled={useDurationDiscountsSaving}
              >
                <View style={[
                  styles.checkbox,
                  useDurationDiscounts && styles.checkboxChecked
                ]}>
                  {useDurationDiscounts && (
                    <Text style={styles.checkmark}>✓</Text>
                  )}
                </View>
                <Text style={{ fontSize: 16, fontWeight: '600', color: '#2E3A38', flex: 1 }}>
                  Enable rental duration discounts
            </Text>
                {useDurationDiscountsSaving && (
                  <ActivityIndicator color="#0E7C6B" size="small" />
                )}
              </TouchableOpacity>
              <Text style={[styles.settingDescription, { marginTop: 8, marginLeft: 34 }]}>
                When enabled, product prices are discounted based on rental duration (excludes delivery/pickup fees and sales tax)
              </Text>
            </View>

            <TouchableOpacity
              style={styles.formSectionHeader}
              onPress={() => setShowDurationDiscountForm(!showDurationDiscountForm)}
            >
              <Text style={[styles.subsectionTitle, { margin: 0 }]}>
                {editingDurationDiscountId ? "Edit Duration Discount" : "Add New Duration Discount"}
              </Text>
              <Text style={styles.toggleIcon}>{showDurationDiscountForm ? "▼" : "▶"}</Text>
            </TouchableOpacity>

            {showDurationDiscountForm && (
            <>
            <Text style={[styles.settingDescription, { fontSize: 12, marginBottom: 10, marginTop: 10 }]}>
              Set rental duration range (in days) and percentage discount on product price only
            </Text>
            <View style={{ flexDirection: 'row',  marginBottom: 10 }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 13, color: '#5C6B68', marginBottom: 5 }}>From (days)</Text>
              <TextInput
                  style={styles.input}
                  placeholder="7"
                placeholderTextColor="#AEB8B6"
                  value={durationDiscountFormData.fromDays}
                  onChangeText={(text) => {
                    // Only allow numeric input
                    const numericText = text.replace(/[^0-9]/g, '');
                    setDurationDiscountFormData({ ...durationDiscountFormData, fromDays: numericText });
                  }}
                  keyboardType="numeric"
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 13, color: '#5C6B68', marginBottom: 5 }}>To (days)</Text>
              <TextInput
                  style={styles.input}
                  placeholder="10"
                placeholderTextColor="#AEB8B6"
                  value={durationDiscountFormData.toDays}
                  onChangeText={(text) => {
                    // Only allow numeric input
                    const numericText = text.replace(/[^0-9]/g, '');
                    setDurationDiscountFormData({ ...durationDiscountFormData, toDays: numericText });
                  }}
                  keyboardType="numeric"
                />
              </View>
            </View>
            <Text style={{ fontSize: 13, color: '#5C6B68', marginBottom: 5 }}>Discount Percentage (%)</Text>
            <TextInput
              style={styles.input}
              placeholder="e.g., 10"
              placeholderTextColor="#AEB8B6"
              value={durationDiscountFormData.value}
              onChangeText={(text) => {
                // Only allow numeric input (including decimals)
                const numericText = text.replace(/[^0-9.]/g, '');
                // Prevent multiple decimal points
                const parts = numericText.split('.');
                const formattedText = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericText;
                setDurationDiscountFormData({ ...durationDiscountFormData, value: formattedText });
              }}
              keyboardType="numeric"
            />
            <View style={styles.formButtons}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.button, styles.saveButton, (!durationDiscountFormData.value || isNaN(parseFloat(durationDiscountFormData.value)) || parseFloat(durationDiscountFormData.value) <= 1) ? styles.buttonDisabled : null]}
                onPress={handleDurationDiscountSave}
                disabled={durationDiscountSaving || !durationDiscountFormData.value || isNaN(parseFloat(durationDiscountFormData.value)) || parseFloat(durationDiscountFormData.value) <= 1}
              >
                {durationDiscountSaving ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                  <Text style={styles.buttonText}>Save</Text>
                  )}
                </TouchableOpacity>
              {editingDurationDiscountId && (
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.cancelButton]}
                  onPress={cancelEditingDurationDiscount}
                >
                  <Text style={styles.buttonText}>Cancel</Text>
                </TouchableOpacity>
              )}
            </View>
            </>
            )}

            {/* Duration Discounts List */}
            <View style={styles.listSection}>
              <View style={styles.listHeader}>
                <Text style={styles.listHeaderText}>
                  Duration Discounts ({durationDiscounts.length})
                </Text>
                {!editingDurationDiscountId && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.addButton]}
                    onPress={startAddingDurationDiscount}
                  >
                    <Text style={styles.buttonText}>+ Add New</Text>
                </TouchableOpacity>
                )}
            </View>

              {durationDiscounts.length === 0 ? (
                <Text style={styles.emptyText}>
                  No duration discounts found. Add your first discount above.
              </Text>
              ) : (
                durationDiscounts.map((discount) => (
                  <View key={discount.id} style={styles.bikeCard}>
                    <View style={styles.bikeInfo}>
                      <Text style={styles.bikeName}>
                        {discount.fromDays !== undefined && discount.toDays !== undefined
                          ? `${discount.fromDays}-${discount.toDays} days`
                          : "Unnamed"}
              </Text>
                      <Text style={styles.bikeDetails}>
                        {discount.value || 0}% off product price
              </Text>
                    </View>
                    <View style={styles.bikeActions}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={styles.iconButton}
                        onPress={() => startEditingDurationDiscount(discount)}
                        disabled={deletingDurationDiscountId === discount.id}
                      >
                        <Text style={styles.iconButtonText}>✏️</Text>
                </TouchableOpacity>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={styles.iconButton}
                        onPress={() =>
                          handleDurationDiscountDelete(
                            discount.id, 
                            discount.fromDays !== undefined && discount.toDays !== undefined
                              ? `${discount.fromDays}-${discount.toDays} days`
                              : "this discount"
                          )
                        }
                        disabled={
                          deletingDurationDiscountId === discount.id || durationDiscountSaving
                        }
                      >
                        {deletingDurationDiscountId === discount.id ? (
                          <ActivityIndicator color="#C4453D" size="small" />
                        ) : (
                          <Text style={styles.iconButtonText}>🗑️</Text>
                  )}
                </TouchableOpacity>
              </View>
                  </View>
                ))
              )}
            </View>
          </>
        )}
            </View>

      {/* Email Templates Section */}
      <View style={styles.section}>
        <TouchableOpacity
          style={styles.sectionHeader}
          onPress={() => setShowEmailTemplates(!showEmailTemplates)}
        >
          <Text style={styles.sectionTitle}>📧 Email Templates</Text>
          <Text style={styles.toggleIcon}>
            {showEmailTemplates ? "▼" : "▶"}
          </Text>
        </TouchableOpacity>

        {showEmailTemplates && (
          <>
            {/* Migration Button */}
            <View style={styles.subSection}>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.button, { backgroundColor: "#B4761B", marginBottom: 20 }]}
                onPress={migrateEmailTemplates}
              >
                <Text style={styles.buttonText}>
                  🔄 Migrate All Email Templates
                </Text>
              </TouchableOpacity>
              <Text style={[styles.settingDescription, { marginTop: -10, marginBottom: 20, fontStyle: "italic", color: "#5C6B68" }]}>
                Adds Main Title field to all templates, adds refund fields to refund templates, and updates button text
              </Text>

                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, { backgroundColor: "#C4453D", marginBottom: 20 }]}
                    onPress={removeStripeReceiptTemplates}
                  >
                    <Text style={styles.buttonText}>
                      🗑️ Remove Stripe Receipt Templates
                    </Text>
                  </TouchableOpacity>
                  <Text style={[styles.settingDescription, { marginTop: -10, marginBottom: 20, fontStyle: "italic", color: "#5C6B68" }]}>
                    Permanently deletes stripeReceiptEmailFields and stripeReceiptSmsTemplate from database (hardcoded templates are now used)
                  </Text>
            </View>

            {/* Universal Booking Confirmation Template */}
            <View style={styles.subSection}>
              <Text style={[styles.subsectionTitle, { fontWeight: "bold", fontStyle: "italic", fontSize: 18, color: "#0E7C6B" }]}>
                📧 Booking Confirmation
              </Text>
              <Text style={styles.settingDescription}>
                Sent for all bookings. Handles delivery, home pickup, and in-store pickup variations automatically.
              </Text>

              <EmailTemplateEditor
                templateType="universal"
                initialData={emailUniversalFields}
                onSave={handleSaveEmailUniversalTemplate}
                saving={emailTemplateSaving}
              />
            </View>


            {/* Thank You Email Template (sent after booking completion) */}
            <View style={styles.subSection}>
              <Text style={[styles.subsectionTitle, { fontWeight: "bold", fontStyle: "italic", fontSize: 18, color: "#0E7C6B" }]}>
                📧 Thank You Message
              </Text>
              <Text style={styles.settingDescription}>
                Sent after a booking is completed. Edit the customizable text sections below - layout and variables are automatically managed. The button will link to the view booking page.
              </Text>

              <EmailTemplateEditor
                templateType="thankYou"
                initialData={emailThankYouFields}
                onSave={handleSaveThankYouEmailTemplate}
                saving={emailTemplateSaving}
              />
            </View>

            {/* Cancellation Email Template */}
            <View style={styles.subSection}>
              <Text style={[styles.subsectionTitle, { fontWeight: "bold", fontStyle: "italic", fontSize: 18, color: "#0E7C6B" }]}>
                📧 Cancellation Confirmation
              </Text>
              <Text style={styles.settingDescription}>
                Sent when a customer cancels their booking.
              </Text>
              <EmailTemplateEditor
                templateType="cancellationConfirmation"
                initialData={emailCancellationConfirmationFields}
                onSave={handleSaveCancellationEmailTemplate}
                saving={emailTemplateSaving}
              />
            </View>

            {/* Refund Only Email Template */}
            <View style={styles.subSection}>
              <Text style={[styles.subsectionTitle, { fontWeight: "bold", fontStyle: "italic", fontSize: 18, color: "#0E7C6B" }]}>
                📧 Refund Confirmation
              </Text>
              <Text style={styles.settingDescription}>
                Sent when admin issues a refund without editing the booking (from Edit Booking page).
              </Text>
              <EmailTemplateEditor
                templateType="refundOnly"
                initialData={emailRefundOnlyFields}
                onSave={handleSaveRefundOnlyEmailTemplate}
                saving={emailTemplateSaving}
              />
            </View>

            {/* Booking Edit Email Template */}
            <View style={styles.subSection}>
              <Text style={[styles.subsectionTitle, { fontWeight: "bold", fontStyle: "italic", fontSize: 18, color: "#0E7C6B" }]}>
                📧 Booking Edit Confirmation
              </Text>
              <Text style={styles.settingDescription}>
                Sent when a booking is edited with item additions or removals. Shows itemized changes, payment charges, or refunds issued. (Legacy name: "Partial Refund Templates")
              </Text>
              <EmailTemplateEditor
                templateType="bookingEdit"
                initialData={emailBookingEditFields}
                onSave={handleSavePartialRefundEmailTemplate}
                saving={emailTemplateSaving}
              />
            </View>

            {/* Cancellation Request Email Template */}
            <View style={styles.subSection}>
              <Text style={[styles.subsectionTitle, { fontWeight: "bold", fontStyle: "italic", fontSize: 18, color: "#0E7C6B" }]}>
                📧 Cancellation Request Confirmation
              </Text>
              <Text style={styles.settingDescription}>
                Sent when a customer or admin requests to cancel a booking. Customer must click the link and enter their PIN to complete cancellation.
              </Text>
              <EmailTemplateEditor
                templateType="cancellationRequest"
                initialData={emailCancellationRequestFields}
                onSave={handleSaveCancellationRequestTemplate}
                saving={emailTemplateSaving}
              />
            </View>

            {/* Equipment Penalty Email Template */}
            <View style={styles.subSection}>
              <Text style={[styles.subsectionTitle, { fontWeight: "bold", fontStyle: "italic", fontSize: 18, color: "#0E7C6B" }]}>
                📧 Equipment Penalty Charge
              </Text>
              <Text style={styles.settingDescription}>
                Sent after charging customer for damaged or missing equipment during check-in. The button will link to the check-in page where photos and penalty details are available.
              </Text>
              <EmailTemplateEditor
                templateType="penalty"
                initialData={emailPenaltyFields}
                onSave={handleSavePenaltyEmailTemplate}
                saving={emailTemplateSaving}
              />
            </View>

          </>
        )}
      </View>

      {/* SMS Templates Section */}
      <View style={styles.section}>
        <TouchableOpacity
          style={styles.sectionHeader}
          onPress={() => setShowSmsTemplates(!showSmsTemplates)}
        >
          <Text style={styles.sectionTitle}>📱 Text Message Templates</Text>
          <Text style={styles.toggleIcon}>
            {showSmsTemplates ? "▼" : "▶"}
          </Text>
        </TouchableOpacity>

        {showSmsTemplates && (
          <>
            <View style={styles.subSection}>
              <Text style={styles.settingDescription}>
                Customize the SMS messages sent to customers at booking and
                during their rental period.
              </Text>

              {/* CONFIRMATION MESSAGES */}
              <Text
                style={[
                  styles.subsectionTitle,
                  { marginTop: 25, fontSize: 16, color: "#1F8A5B" },
                ]}
              >
                ✅ Booking Confirmation Messages
              </Text>

              {/* Universal SMS Confirmation */}
              <View style={{ marginTop: 15 }}>
                <Text style={styles.templateLabel}>
                  Booking Confirmation:
                </Text>
                <Text
                  style={[
                    styles.settingDescription,
                    { fontSize: 12, marginBottom: 5 },
                  ]}
                >
                  Sent for ALL new bookings.
                      Variables: {"{"}customerName{"}"}, {"{"}storeName{"}"}, {"{"}bookingUrl{"}"}, {"{"}bookingPin{"}"}
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { minHeight: 100 }]}
                  placeholder="Great news! Your bike rental has been confirmed. {bookingUrl}"
                  placeholderTextColor="#AEB8B6"
                  value={smsUniversalTemplate}
                  onChangeText={setSmsUniversalTemplate}
                  multiline
                  numberOfLines={4}
                />
                {smsUniversalTemplate !== originalSmsUniversalTemplate && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.saveButton, { marginTop: 10 }]}
                    onPress={handleSaveSmsUniversalTemplate}
                    disabled={smsTemplateSaving}
                  >
                    {smsTemplateSaving ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.buttonText}>Save</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>

              {/* REMINDER MESSAGES */}
              <Text
                style={[
                  styles.subsectionTitle,
                  { marginTop: 35, fontSize: 16, color: "#B4761B" },
                ]}
              >
                ⏰ Automated Reminder Messages
              </Text>
              <Text style={[styles.settingDescription, { marginBottom: 15 }]}>
                These are sent automatically by the scheduled service at 8 AM
                daily.
              </Text>

              {/* Day-Before Delivery Reminder */}
              <View style={{ marginTop: 15 }}>
                <Text style={styles.templateLabel}>
                  Day-Before Delivery Reminder:
                </Text>
                <Text
                  style={[
                    styles.settingDescription,
                    { fontSize: 12, marginBottom: 5 },
                  ]}
                >
                  Sent on day before start date for delivery bookings.
                  Variables: {"{"}customerName{"}"}, {"{"}storeName{"}"}, {"{"}bookingUrl{"}"}
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { minHeight: 100 }]}
                  placeholder="🚴 Hi {customerName}! Reminder: Your bike delivery is TOMORROW 11 AM-3 PM. We'll text when we're on the way! {bookingUrl}"
                  placeholderTextColor="#AEB8B6"
                  value={smsDayBeforeDeliveryTemplate}
                  onChangeText={setSmsDayBeforeDeliveryTemplate}
                  multiline
                  numberOfLines={4}
                />
                {smsDayBeforeDeliveryTemplate !== originalSmsDayBeforeDeliveryTemplate && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.saveButton, { marginTop: 10 }]}
                    onPress={handleSaveSmsDayBeforeDeliveryTemplate}
                    disabled={smsTemplateSaving}
                  >
                    {smsTemplateSaving ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.buttonText}>Save</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>

              {/* Day-of Delivery Reminder */}
              <View style={{ marginTop: 20 }}>
                <Text style={styles.templateLabel}>
                  Day-of Delivery Reminder:
                </Text>
                <Text
                  style={[
                    styles.settingDescription,
                    { fontSize: 12, marginBottom: 5 },
                  ]}
                >
                  Sent on start date morning for delivery bookings. Variables:{" "}
                  {"{"}customerName{"}"}, {"{"}storeName{"}"}, {"{"}deliveryPhone{"}"}, {"{"}
                  bookingUrl{"}"}
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { minHeight: 100 }]}
                  placeholder="🚴 Hi {customerName}! Your bike delivery is TODAY 11 AM-3 PM. Text {deliveryPhone} with questions. {bookingUrl}"
                  placeholderTextColor="#AEB8B6"
                  value={smsDayOfDeliveryTemplate}
                  onChangeText={setSmsDayOfDeliveryTemplate}
                  multiline
                  numberOfLines={4}
                />
                {smsDayOfDeliveryTemplate !== originalSmsDayOfDeliveryTemplate && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.saveButton, { marginTop: 10 }]}
                    onPress={handleSaveSmsDayOfDeliveryTemplate}
                    disabled={smsTemplateSaving}
                  >
                    {smsTemplateSaving ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.buttonText}>Save</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>

              {/* Day-before Home Pickup Reminder */}
              <View style={{ marginTop: 20 }}>
                <Text style={styles.templateLabel}>
                  Day-Before Home Pickup Reminder:
                </Text>
                <Text
                  style={[
                    styles.settingDescription,
                    { fontSize: 12, marginBottom: 5 },
                  ]}
                >
                  Sent on day before end date for home pickup bookings.
                  Variables: {"{"}customerName{"}"}, {"{"}storeName{"}"}, {"{"}storePhone{"}"}, {"{"}
                  bookingUrl{"}"}
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { minHeight: 100 }]}
                  placeholder="🚴 Hi {customerName}! Reminder: We'll pick up bikes TOMORROW. Have them ready! Call {storePhone}. {bookingUrl}"
                  placeholderTextColor="#AEB8B6"
                  value={smsDayBeforePickupTemplate}
                  onChangeText={setSmsDayBeforePickupTemplate}
                  multiline
                  numberOfLines={4}
                />
                {smsDayBeforePickupTemplate !== originalSmsDayBeforePickupTemplate && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.saveButton, { marginTop: 10 }]}
                    onPress={handleSaveSmsDayBeforePickupTemplate}
                    disabled={smsTemplateSaving}
                  >
                    {smsTemplateSaving ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.buttonText}>Save</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>

              {/* Day-of Home Pickup Reminder */}
              <View style={{ marginTop: 20 }}>
                <Text style={styles.templateLabel}>
                  Day-of Home Pickup Reminder:
                </Text>
                <Text
                  style={[
                    styles.settingDescription,
                    { fontSize: 12, marginBottom: 5 },
                  ]}
                >
                  Sent on end date morning for home pickup bookings. Variables:{" "}
                  {"{"}customerName{"}"}, {"{"}storeName{"}"}, {"{"}deliveryPhone{"}"}, {"{"}
                  bookingUrl{"}"}
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { minHeight: 100 }]}
                  placeholder="🚴 Hi {customerName}! We're picking up bikes TODAY 11 AM-3 PM. Have them ready! Text {deliveryPhone}. Thanks!"
                  placeholderTextColor="#AEB8B6"
                  value={smsDayOfPickupTemplate}
                  onChangeText={setSmsDayOfPickupTemplate}
                  multiline
                  numberOfLines={4}
                />
                {smsDayOfPickupTemplate !== originalSmsDayOfPickupTemplate && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.saveButton, { marginTop: 10 }]}
                    onPress={handleSaveSmsDayOfPickupTemplate}
                    disabled={smsTemplateSaving}
                  >
                    {smsTemplateSaving ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.buttonText}>Save</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>

              {/* Day-of In-Store Start Reminder */}
              <View style={{ marginTop: 20 }}>
                <Text style={styles.templateLabel}>
                  Day-of In-Store Pickup Reminder (START):
                </Text>
                <Text
                  style={[
                    styles.settingDescription,
                    { fontSize: 12, marginBottom: 5 },
                  ]}
                >
                  Sent on start date morning for in-store bookings (reminder to
                  pick up bikes). Variables: {"{"}customerName{"}"}, {"{"}storeName{"}"}, {"{"}
                  storePhone{"}"}
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { minHeight: 100 }]}
                  placeholder="🚴 Hi {customerName}! Your bike rental starts TODAY! Pick up bikes at our shop. Hours: M-F 11-6, Sat-Sun 11-4. {storePhone}"
                  placeholderTextColor="#AEB8B6"
                  value={smsDayOfInStoreStartTemplate}
                  onChangeText={setSmsDayOfInStoreStartTemplate}
                  multiline
                  numberOfLines={4}
                />
                {smsDayOfInStoreStartTemplate !== originalSmsDayOfInStoreStartTemplate && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.saveButton, { marginTop: 10 }]}
                    onPress={handleSaveSmsDayOfInStoreStartTemplate}
                    disabled={smsTemplateSaving}
                  >
                    {smsTemplateSaving ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.buttonText}>Save</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>

              {/* Day-of In-Store Return Reminder */}
              <View style={{ marginTop: 20 }}>
                <Text style={styles.templateLabel}>
                  Day-of In-Store Return Reminder (END):
                </Text>
                <Text
                  style={[
                    styles.settingDescription,
                    { fontSize: 12, marginBottom: 5 },
                  ]}
                >
                  Sent on end date morning for in-store bookings (reminder to
                  return bikes). Variables: {"{"}customerName{"}"}, {"{"}storeName{"}"}, {"{"}
                  storePhone{"}"}
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { minHeight: 100 }]}
                  placeholder="🚴 Hi {customerName}! Reminder: Today is your LAST DAY. Please return bikes to shop by closing. M-F 11-6, Sat-Sun 11-4. {storePhone}"
                  placeholderTextColor="#AEB8B6"
                  value={smsDayOfInStoreReturnTemplate}
                  onChangeText={setSmsDayOfInStoreReturnTemplate}
                  multiline
                  numberOfLines={4}
                />
                {smsDayOfInStoreReturnTemplate !== originalSmsDayOfInStoreReturnTemplate && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.saveButton, { marginTop: 10 }]}
                    onPress={handleSaveSmsDayOfInStoreReturnTemplate}
                    disabled={smsTemplateSaving}
                  >
                    {smsTemplateSaving ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.buttonText}>Save</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>

              {/* THANK YOU MESSAGE */}
              <Text
                style={[
                  styles.subsectionTitle,
                  { marginTop: 35, fontSize: 16, color: "#1F8A5B" },
                ]}
              >
                🙏 Thank You Message
              </Text>
              <Text style={[styles.settingDescription, { marginBottom: 15 }]}>
                Sent after a booking is completed. Includes a link to view the booking.
              </Text>

              {/* Thank You SMS Template */}
              <View style={{ marginTop: 15 }}>
                <Text style={styles.templateLabel}>
                  Thank You SMS Message:
                </Text>
                <Text
                  style={[
                    styles.settingDescription,
                    { fontSize: 12, marginBottom: 5 },
                  ]}
                >
                  Sent after booking completion. Variables: {"{"}customerName{"}"}, {"{"}storeName{"}"}, {"{"}bookingUrl{"}"}
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { minHeight: 100 }]}
                  placeholder="🙏 Thank you for choosing {storeName}! We hope you had a great experience. View your booking: {bookingUrl}"
                  placeholderTextColor="#AEB8B6"
                  value={thankYouSmsTemplate}
                  onChangeText={setThankYouSmsTemplate}
                  multiline
                  numberOfLines={4}
                />
                {thankYouSmsTemplate !== originalThankYouSmsTemplate && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.saveButton, { marginTop: 10 }]}
                    onPress={handleSaveThankYouSmsTemplate}
                    disabled={thankYouSmsSaving}
                  >
                    {thankYouSmsSaving ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.buttonText}>Save</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>

              {/* REVIEW REQUEST */}
              <Text
                style={[
                  styles.subsectionTitle,
                  { marginTop: 35, fontSize: 16, color: "#B8860B" },
                ]}
              >
                ⭐ Review Requests
              </Text>
              <Text style={[styles.settingDescription, { marginBottom: 15 }]}>
                After a completed check-in, ask the customer for a review. If they agree, a
                review-page link is texted and/or emailed a few hours later.
              </Text>

              <View style={{ marginTop: 15 }}>
                <View style={styles.reviewRequestToggleRow}>
                  <View style={styles.reviewRequestToggleLeft}>
                    <Text style={styles.templateLabel}>Enable Review Requests</Text>
                    <View
                      style={[
                        styles.reviewRequestStatusPill,
                        reviewRequestActive
                          ? styles.reviewRequestStatusPillActive
                          : styles.reviewRequestStatusPillOff,
                      ]}
                    >
                      <Text
                        style={
                          reviewRequestActive
                            ? styles.reviewRequestStatusPillTextActive
                            : styles.reviewRequestStatusPillTextOff
                        }
                      >
                        {reviewRequestActive ? "ACTIVE" : "OFF"}
                      </Text>
                    </View>
                  </View>
                  <Switch
                    value={reviewRequestEnabled}
                    onValueChange={setReviewRequestEnabled}
                    trackColor={{ false: "#DDE3E2", true: "#B8860B" }}
                    thumbColor="#FFFFFF"
                  />
                </View>
                {reviewRequestEnabled && !reviewRequestUrl.trim() && (
                  <Text style={styles.reviewRequestWarning}>
                    Enabled, but no review link is set — customers won't be asked until one is saved below.
                  </Text>
                )}

                <Text style={styles.templateLabel}>Review Page Link</Text>
                <View style={styles.reviewRequestUrlRow}>
                  <TextInput
                    style={[styles.input, { flex: 1, marginBottom: 0 }]}
                    placeholder="g.page/r/your-store/review"
                    placeholderTextColor="#AEB8B6"
                    value={reviewRequestUrl}
                    onChangeText={(text) => {
                      setReviewRequestUrl(text);
                      if (reviewRequestUrlError) setReviewRequestUrlError("");
                    }}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[
                      styles.reviewRequestTestButton,
                      !originalReviewRequest?.url && styles.buttonDisabled,
                    ]}
                    onPress={() => window.open(originalReviewRequest.url, "_blank")}
                    disabled={!originalReviewRequest?.url}
                  >
                    <Text style={styles.buttonText}>Test</Text>
                  </TouchableOpacity>
                </View>
                {!!reviewRequestUrlError && (
                  <Text style={styles.reviewRequestFieldError}>{reviewRequestUrlError}</Text>
                )}

                <Text style={styles.templateLabel}>Delay (hours)</Text>
                <Text style={[styles.settingDescription, { fontSize: 12, marginBottom: 5 }]}>
                  How long after check-in completion to send. 1–72 hours.
                </Text>
                <TextInput
                  style={[styles.input, { width: 120 }]}
                  keyboardType="numeric"
                  value={reviewRequestDelayHours}
                  onChangeText={setReviewRequestDelayHours}
                />

                <Text style={styles.templateLabel}>Re-Ask Suppression (days)</Text>
                <Text style={[styles.settingDescription, { fontSize: 12, marginBottom: 5 }]}>
                  {Number(reviewRequestSuppressDays) === 0
                    ? "Set to 0 — customers are asked on every completed check-in."
                    : "Don't ask the same customer again for this many days after they say yes. 0–365."}
                </Text>
                <TextInput
                  style={[styles.input, { width: 120 }]}
                  keyboardType="numeric"
                  value={reviewRequestSuppressDays}
                  onChangeText={setReviewRequestSuppressDays}
                />

                <Text style={styles.templateLabel}>Review Request SMS:</Text>
                <Text style={[styles.settingDescription, { fontSize: 12, marginBottom: 5 }]}>
                  Variables: {"{"}firstName{"}"}, {"{"}lastName{"}"}, {"{"}storeName{"}"}, {"{"}reviewLink{"}"}
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { minHeight: 100 }]}
                  placeholder={REVIEW_REQUEST_DEFAULT_SMS}
                  placeholderTextColor="#AEB8B6"
                  value={reviewRequestSmsTemplate}
                  onChangeText={setReviewRequestSmsTemplate}
                  multiline
                  numberOfLines={4}
                />

                <Text style={styles.templateLabel}>Review Request Email Subject:</Text>
                <TextInput
                  style={styles.input}
                  placeholder={REVIEW_REQUEST_DEFAULT_EMAIL_SUBJECT}
                  placeholderTextColor="#AEB8B6"
                  value={reviewRequestEmailSubject}
                  onChangeText={setReviewRequestEmailSubject}
                />

                <Text style={styles.templateLabel}>Review Request Email Body:</Text>
                <Text style={[styles.settingDescription, { fontSize: 12, marginBottom: 5 }]}>
                  Variables: {"{"}firstName{"}"}, {"{"}lastName{"}"}, {"{"}storeName{"}"}, {"{"}reviewLink{"}"}
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { minHeight: 100 }]}
                  placeholder={REVIEW_REQUEST_DEFAULT_EMAIL_BODY}
                  placeholderTextColor="#AEB8B6"
                  value={reviewRequestEmailBody}
                  onChangeText={setReviewRequestEmailBody}
                  multiline
                  numberOfLines={4}
                />

                <View style={styles.reviewRequestLockedRow}>
                  <Text>🔒</Text>
                  <Text style={styles.reviewRequestLockedText}>
                    "{REVIEW_REQUEST_OPTOUT_SUFFIX}" — added to every review request text automatically. Cannot be edited.
                  </Text>
                </View>

                {reviewRequestDirty && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.saveButton, { marginTop: 10 }]}
                    onPress={handleSaveReviewRequestSettings}
                    disabled={reviewRequestSaving}
                  >
                    {reviewRequestSaving ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.buttonText}>Save</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>

              {/* PAYMENT LINK SMS TEMPLATE */}
              <Text
                style={[
                  styles.subsectionTitle,
                  { marginTop: 35, fontSize: 16, color: "#0E7C6B" },
                ]}
              >
                💳 Payment Link Messages
              </Text>
              <Text style={[styles.settingDescription, { marginBottom: 15 }]}>
                Sent when admin sends a payment link to customer for manual bookings.
              </Text>

              {/* Payment Link SMS Template */}
              <View style={{ marginTop: 15 }}>
                <Text style={styles.templateLabel}>
                  Payment Link SMS:
                </Text>
                <Text
                  style={[
                    styles.settingDescription,
                    { fontSize: 12, marginBottom: 5 },
                  ]}
                >
                  Sent when admin sends payment link. Variables: {"{"}customerName{"}"}, {"{"}storeName{"}"}, {"{"}paymentUrl{"}"}
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { minHeight: 100 }]}
                  placeholder="💳 Hi {customerName}! Please complete payment for your {storeName} booking: {paymentUrl}"
                  placeholderTextColor="#AEB8B6"
                  value={paymentLinkSmsTemplate}
                  onChangeText={setPaymentLinkSmsTemplate}
                  multiline
                  numberOfLines={4}
                />
                {paymentLinkSmsTemplate !== originalPaymentLinkSmsTemplate && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.saveButton, { marginTop: 10 }]}
                    onPress={handleSavePaymentLinkSmsTemplate}
                    disabled={paymentLinkSmsSaving}
                  >
                    {paymentLinkSmsSaving ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.buttonText}>Save</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>

              {/* Equipment Penalty SMS Template */}
              <View style={{ marginTop: 15 }}>
                <Text style={styles.templateLabel}>
                  Equipment Penalty SMS:
                </Text>
                <Text
                  style={[
                    styles.settingDescription,
                    { fontSize: 12, marginBottom: 5 },
                  ]}
                >
                  Sent when charging customer for damaged/missing equipment. Variables: {"{"}customerName{"}"}, {"{"}penaltyAmount{"}"}, {"{"}checkInUrl{"}"}, {"{"}receiptUrl{"}"}
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { minHeight: 100 }]}
                  placeholder="Hi {customerName}! We charged {penaltyAmount} for equipment penalties. Photos & details available here: {checkInUrl} Receipt: {receiptUrl}"
                  placeholderTextColor="#AEB8B6"
                  value={penaltySmsTemplate}
                  onChangeText={setPenaltySmsTemplate}
                  multiline
                  numberOfLines={4}
                />
                {penaltySmsTemplate !== originalPenaltySmsTemplate && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.saveButton, { marginTop: 10 }]}
                    onPress={handleSavePenaltySmsTemplate}
                    disabled={penaltySmsSaving}
                  >
                    {penaltySmsSaving ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.buttonText}>Save</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>


              {/* CANCELLATION SMS TEMPLATE */}
              <Text
                style={[
                  styles.subsectionTitle,
                  { marginTop: 35, fontSize: 16, color: "#C4453D" },
                ]}
              >
                🚫 Cancellation Confirmation Messages
              </Text>
              <Text style={[styles.settingDescription, { marginBottom: 15 }]}>
                Sent when a customer cancels their booking.
              </Text>

              {/* Cancellation SMS Template */}
              <View style={{ marginTop: 25 }}>
                <Text style={styles.templateLabel}>
                  Cancellation SMS Confirmation:
                </Text>
                <Text
                    style={[
                    styles.settingDescription,
                    { fontSize: 12, marginBottom: 5 },
                  ]}
                >
                  Sent to customer when booking is cancelled. Variables: {"{"}customerName{"}"}, {"{"}storeName{"}"}, {"{"}refundPercentage{"}"}, {"{"}refundAmount{"}"}, {"{"}bookingUrl{"}"}
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { minHeight: 100 }]}
                  placeholder="🚫 Hi {customerName}! Your {storeName} booking has been cancelled. You'll receive a {refundPercentage}% refund (${refundAmount}) within 5-10 business days. {bookingUrl}"
                  placeholderTextColor="#AEB8B6"
                  value={cancellationSmsTemplate}
                  onChangeText={setCancellationSmsTemplate}
                  multiline
                  numberOfLines={4}
                />
                {cancellationSmsTemplate !== originalCancellationSmsTemplate && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.saveButton, { marginTop: 10 }]}
                    onPress={handleSaveCancellationSmsTemplate}
                    disabled={smsTemplateSaving}
                  >
                    {smsTemplateSaving ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.buttonText}>Save</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>

              {/* REFUND ONLY SMS TEMPLATE */}
              <Text
                style={[
                  styles.subsectionTitle,
                  { marginTop: 35, fontSize: 16, color: "#1F8A5B" },
                ]}
              >
                💰 Refund Only Messages (No Booking Edit)
              </Text>
              <Text style={[styles.settingDescription, { marginBottom: 15 }]}>
                Sent when admin issues a refund without editing the booking (from Edit Booking page).
              </Text>

              {/* Refund Only SMS Template */}
              <View style={{ marginTop: 25 }}>
                <Text style={styles.templateLabel}>
                  Refund Only SMS:
                </Text>
                <Text
                  style={[
                    styles.settingDescription,
                    { fontSize: 12, marginBottom: 5 },
                  ]}
                >
                  Sent when admin issues a refund. Variables: {"{"}storeName{"}"}, {"{"}refundAmount{"}"}, {"{"}bookingId{"}"}, {"{"}refundReason{"}"}, {"{"}storePhone{"}"}
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { minHeight: 100 }]}
                  placeholder="💰 {storeName}: A refund of ${refundAmount} has been issued for booking {bookingId}. {refundReason}. Questions? Call {storePhone}"
                  placeholderTextColor="#AEB8B6"
                  value={refundOnlySmsTemplate}
                  onChangeText={setRefundOnlySmsTemplate}
                  multiline
                  numberOfLines={4}
                />
                {refundOnlySmsTemplate !== originalRefundOnlySmsTemplate && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.saveButton, { marginTop: 10 }]}
                    onPress={handleSaveRefundOnlySmsTemplate}
                    disabled={refundOnlySmsSaving}
                  >
                    {refundOnlySmsSaving ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.buttonText}>Save</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>

              {/* BOOKING EDIT SMS TEMPLATE */}
              <Text style={[styles.subsectionTitle, { marginTop: 35, fontSize: 16, color: "#B4761B" }]}>
                ✏️💳 Booking Edit Confirmation Messages
              </Text>
              <Text style={[styles.settingDescription, { marginBottom: 15 }]}>
                Sent when a booking is edited with item additions or removals. Shows itemized changes, payment charges, or refunds issued. (Legacy name: "Partial Refund Templates")
              </Text>

              {/* Booking Edit SMS Template */}
              <View style={{ marginTop: 25 }}>
                <Text style={styles.templateLabel}>
                  Booking Edit SMS Confirmation:
                </Text>
                <Text
                    style={[
                    styles.settingDescription,
                    { fontSize: 12, marginBottom: 5 },
                  ]}
                >
                  Sent when booking is edited (additions/removals). Variables: {"{"}customerName{"}"}, {"{"}storeName{"}"}, {"{"}itemsAddedRemoved{"}"}, {"{"}newBalance{"}"}, {"{"}amountPaid{"}"}, {"{"}amountRefunded{"}"}, {"{"}bookingUrl{"}"}
                </Text>
                <TextInput
                  style={[styles.input, styles.textArea, { minHeight: 100 }]}
                  placeholder="✏️ Hi {customerName}! Your {storeName} booking has been updated. {itemsAddedRemoved} New balance: ${newBalance}. View details: {bookingUrl}"
                  placeholderTextColor="#AEB8B6"
                  value={partialRefundSmsTemplate}
                  onChangeText={setPartialRefundSmsTemplate}
                  multiline
                  numberOfLines={4}
                />
                {partialRefundSmsTemplate !== originalPartialRefundSmsTemplate && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.button, styles.saveButton, { marginTop: 10 }]}
                    onPress={handleSavePartialRefundSmsTemplate}
                    disabled={smsTemplateSaving}
                  >
                    {smsTemplateSaving ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.buttonText}>Save</Text>
                    )}
                  </TouchableOpacity>
                )}
                </View>
            </View>
          </>
        )}
      </View>

      {/* Waiver Templates Section */}
      <View style={styles.section}>
        <TouchableOpacity
          style={styles.sectionHeader}
          onPress={() => setShowWaiverTemplates(!showWaiverTemplates)}
        >
          <Text style={styles.sectionTitle}>📝 Waiver Templates</Text>
          <Text style={styles.toggleIcon}>
            {showWaiverTemplates ? "▼" : "▶"}
          </Text>
        </TouchableOpacity>

        {showWaiverTemplates && (
          <>
            {/* New Booking Waiver Template */}
            <View style={styles.subSection}>
              <Text style={[styles.subsectionTitle, { fontWeight: "bold", fontStyle: "italic", fontSize: 18, color: "#0E7C6B" }]}>
                📝 Waiver Template (New Bookings)
              </Text>
              <Text style={styles.settingDescription}>
                ℹ️ Create and customize your liability waiver template for new bookings. This waiver will be used when customers first book equipment rentals.
                Add or remove sections, customize text, and control which fields require customer initials or signatures.
                Use variables like {'{storeName}'}, {'{storeAddress}'}, {'{customerName}'}, and {'{date}'} in your text.
              </Text>
              
              {waiverTemplateLoading ? (
                <View style={{ padding: 20, alignItems: 'center' }}>
                  <ActivityIndicator size="large" color="#0E7C6B" />
                  <Text style={{ marginTop: 10, color: '#5C6B68' }}>Loading waiver template...</Text>
                </View>
              ) : (
                <WaiverTemplateEditor
                  initialTemplate={waiverTemplate}
                  onSave={handleSaveWaiverTemplate}
                  storeName={legalStoreName || "Your Store Name"}
                />
              )}
            </View>

            {/* Transfer Waiver Template */}
            <View style={styles.subSection}>
              <Text style={[styles.subsectionTitle, { fontWeight: "bold", fontStyle: "italic", fontSize: 18, color: "#0E7C6B" }]}>
                🔄 Transfer Waiver Template
              </Text>
              <Text style={styles.settingDescription}>
                ℹ️ Create and customize your transfer waiver template. This waiver is used when the original renter transfers responsibility for equipment to another person.
                Add or remove sections, customize text, and control which fields require initials or signatures.
                Use variables like {'{storeName}'}, {'{storeAddress}'}, {'{customerName}'}, and {'{date}'} in your text.
              </Text>
              
              {transferWaiverTemplateLoading ? (
                <View style={{ padding: 20, alignItems: 'center' }}>
                  <ActivityIndicator size="large" color="#0E7C6B" />
                  <Text style={{ marginTop: 10, color: '#5C6B68' }}>Loading transfer waiver template...</Text>
                </View>
              ) : (
                <WaiverTemplateEditor
                  initialTemplate={transferWaiverTemplate}
                  onSave={handleSaveTransferWaiverTemplate}
                  storeName={legalStoreName || "Your Store Name"}
                />
              )}
            </View>
          </>
        )}
      </View>

      {/* Save All Settings Button */}
      <View style={styles.section}>
        <TouchableOpacity dataSet={{ hover: "lift" }}
          style={[styles.button, styles.saveAllButton, savingAllSettings && styles.buttonDisabled]}
          onPress={handleSaveAllSettings}
          disabled={savingAllSettings}
        >
          {savingAllSettings ? (
            <ActivityIndicator color="#FFFFFF" size="small" />
          ) : (
            <Text style={styles.buttonText}>💾 Save All Settings</Text>
          )}
        </TouchableOpacity>
        <Text style={[styles.settingDescription, { textAlign: 'center', marginTop: 8 }]}>
          This will save all current settings from this page to the database
        </Text>
      </View>

      </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
  },
  desktopWrapper: {
    width: "100%",
    maxWidth: 800,
    flex: 1,
    alignSelf: "center",
  },
  content: {
    flex: 1,
  },
  section: {
    backgroundColor: "#FFFFFF",
    margin: 16,
    padding: 16,
    borderRadius: 6,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  formsRow: {
    flexDirection: "row",
    margin: 16,
  },
  formSection: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    padding: 16,
    borderRadius: 6,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 16,
    color: "#2E3A38",
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  formSectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: '#E3F2EF',
    padding: 16,
    marginTop: 15,
    marginBottom: 0,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E3F2EF',
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  toggleIcon: {
    fontSize: 16,
    color: "#5C6B68",
    fontWeight: "bold",
  },
  subsectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 12,
    color: "#2E3A38",
  },
  listSection: {
    marginTop: 12,
  },
  subSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#EDF1F0",
  },
  input: {
    height: 44,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    paddingHorizontal: 12,
    fontSize: 16,
    marginBottom: 12,
    backgroundColor: "#F4F6F6",
  },
  textArea: {
    height: 80,
    paddingTop: 12,
    paddingBottom: 12,
    textAlignVertical: "top",
  },
  imageUploadSection: {
    marginBottom: 12,
  },
  imageUploadLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 8,
  },
  imagePreviewContainer: {
    marginBottom: 8,
    alignItems: "center",
  },
  logoPreviewContainer: {
    marginBottom: 16,
    alignItems: "center",
    padding: 16,
    backgroundColor: "#F4F6F6",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  logoPreviewLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 12,
  },
  logoThumbnailWrapper: {
    padding: 12,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
  },
  imagePreview: {
    width: 200,
    height: 200,
    borderRadius: 6,
    objectFit: "cover",
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    imageRendering: "high-quality",
  },
  removeImageButton: {
    backgroundColor: "#C4453D",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 5,
  },
  removeImageText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "600",
  },
  uploadButton: {
    backgroundColor: "#0E7C6B",
    padding: 12,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 44,
  },
  uploadButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  formButtons: {
    flexDirection: "row",
  },
  listHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  button: {
    height: 44,
    borderRadius: 6,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
  },
  saveButton: {
    backgroundColor: "#1F8A5B",
    flex: 1,
  },
  saveAllButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 16,
    marginTop: 20,
    marginBottom: 10,
  },
  cancelButton: {
    backgroundColor: "#8A9794",
    flex: 1,
  },
  buttonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.6,
  },
  addButton: {
    backgroundColor: "#0E7C6B",
  },
  buttonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  bikeCard: {
    flexDirection: "row",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  bikeImageSection: {
    width: "40%",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 7,
  },
  bikeDetailsSection: {
    width: "60%",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  bikeThumbnail: {
    width: "100%",
    aspectRatio: 1,
    borderRadius: 8,
    objectFit: "contain",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    imageRendering: "high-quality",
  },
  bikeThumbnailPlaceholder: {
    width: "100%",
    aspectRatio: 1,
    height: 120,
    borderRadius: 8,
    backgroundColor: "#EDF1F0",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    alignItems: "center",
    justifyContent: "center",
  },
  bikeThumbnailPlaceholderText: {
    fontSize: 24,
  },
  bikeName: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 4,
  },
  bikeDetails: {
    fontSize: 14,
    color: "#5C6B68",
    marginBottom: 4,
  },
  bikeId: {
    fontSize: 12,
    color: "#8A9794",
    fontFamily: "monospace",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.95)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  closeButton: {
    position: "absolute",
    top: 40,
    right: 20,
    zIndex: 1000,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    justifyContent: "center",
    alignItems: "center",
  },
  closeButtonText: {
    fontSize: 28,
    color: "#0F1A19",
    fontWeight: "bold",
  },
  fullScreenImage: {
    width: "90%",
    height: "90%",
    objectFit: "contain",
  },
  bikeActions: {
    flexDirection: "row",
  },
  actionButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 5,
  },
  editButton: {
    backgroundColor: "#0E7C6B",
  },
  deleteButton: {
    backgroundColor: "#C4453D",
  },
  actionButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
  iconButton: {
    padding: 8,
    marginHorizontal: 4,
  },
  iconButtonDisabled: {
    opacity: 0.3,
  },
  iconButtonText: {
    fontSize: 24,
  },
  iconButtonTextDisabled: {
    opacity: 0.3,
  },
  physicalItemRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
    marginBottom: 4,
  },
  physicalItemCheckbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: "#DDE3E2",
    borderRadius: 4,
    backgroundColor: "#FFFFFF",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 8,
  },
  physicalItemCheckboxChecked: {
    backgroundColor: "#1F8A5B",
    borderColor: "#1F8A5B",
  },
  physicalItemCheckmark: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "bold",
  },
  physicalItemLabel: {
    fontSize: 13,
    color: "#5C6B68",
    fontWeight: "500",
  },
  templateLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginTop: 12,
    marginBottom: 8,
  },
  templateInput: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 14,
    marginBottom: 12,
    backgroundColor: "#F4F6F6",
    textAlignVertical: "top",
    minHeight: 44,
    width: "100%",
  },
  templateBodyInput: {
    minHeight: 200,
  },
  templatePreview: {
    marginTop: 16,
    padding: 16,
    backgroundColor: "#F4F6F6",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  templatePreviewTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 12,
  },
  templatePreviewSubject: {
    fontSize: 14,
    fontWeight: "600",
    color: "#0E7C6B",
    marginBottom: 8,
  },
  templatePreviewBody: {
    fontSize: 14,
    color: "#2E3A38",
    lineHeight: 20,
    whiteSpace: "pre-wrap",
  },
  emptyText: {
    fontSize: 14,
    color: "#8A9794",
    textAlign: "center",
    padding: 20,
    fontStyle: "italic",
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: "#F6F8F7",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
  },
  loadingContent: {
    alignItems: "center",
    padding: 40,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
    minWidth: 300,
  },
  loadingLogo: {
    width: 150,
    height: 100,
    marginBottom: 20,
  },
  loadingIconContainer: {
    position: "relative",
    marginBottom: 24,
    width: 80,
    height: 80,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingIcon: {
    fontSize: 64,
    position: "absolute",
  },
  loadingSpinnerOverlay: {
    position: "absolute",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  loadingTitle: {
    fontSize: 28,
    fontWeight: "700",
    color: "#0E7C6B",
    marginBottom: 8,
    textAlign: "center",
  },
  loadingSubtitle: {
    fontSize: 16,
    color: "#5C6B68",
    marginBottom: 24,
    textAlign: "center",
  },
  loadingBarContainer: {
    width: "100%",
    marginBottom: 16,
  },
  loadingBar: {
    width: "100%",
    height: 4,
    backgroundColor: "#DDE3E2",
    borderRadius: 2,
    overflow: "hidden",
  },
  loadingBarFill: {
    height: "100%",
    backgroundColor: "#0E7C6B",
    borderRadius: 2,
    animation: "loading-pulse 1.5s ease-in-out infinite",
    width: "70%",
  },
  loadingHint: {
    fontSize: 14,
    color: "#8A9794",
    textAlign: "center",
    fontStyle: "italic",
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: "#5C6B68",
  },
  optionSelector: {
    marginBottom: 12,
  },
  optionLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 8,
  },
  optionButtons: {
    flexDirection: "row",
  },
  optionButton: {
    flex: 1,
    height: 44,
    borderWidth: 2,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F4F6F6",
  },
  optionButtonActive: {
    borderColor: "#0E7C6B",
    backgroundColor: "#E3F2EF",
  },
  optionButtonText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#5C6B68",
  },
  optionButtonTextActive: {
    color: "#0E7C6B",
  },
  settingDescription: {
    fontSize: 14,
    color: "#5C6B68",
    marginBottom: 12,
    fontStyle: "italic",
  },
  currentSetting: {
    fontSize: 14,
    color: "#1F8A5B",
    marginTop: 8,
    fontWeight: "600",
  },
  addressInputContainer: {
    position: "relative",
    marginBottom: 8,
    zIndex: 1000,
  },
  addressInput: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
    backgroundColor: "#FFFFFF",
    marginBottom: 4,
  },
  suggestionsContainer: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderTopWidth: 0,
    maxHeight: 200,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
    zIndex: 10000,
  },
  suggestionsList: {
    maxHeight: 200,
  },
  suggestionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  suggestionItemLast: {
    borderBottomWidth: 0,
  },
  suggestionMainText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 2,
  },
  suggestionSecondaryText: {
    fontSize: 12,
    color: "#5C6B68",
  },
  loadingMapsText: {
    fontSize: 12,
    color: "#5C6B68",
    fontStyle: "italic",
    marginTop: 4,
  },
  hoursHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  dayRow: {
    backgroundColor: "#F4F6F6",
    padding: 12,
    borderRadius: 6,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  dayNameContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  dayName: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
    width: 100,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    borderRadius: 5,
    backgroundColor: "#FFFFFF",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 8,
  },
  checkboxChecked: {
    backgroundColor: "#0E7C6B",
  },
  checkboxContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    marginBottom: 8,
  },
  checkboxLabel: {
    fontSize: 16,
    color: "#2E3A38",
    flex: 1,
  },
  checkboxCheckmark: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "bold",
  },
  checkmark: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "bold",
  },
  openClosedLabel: {
    fontSize: 14,
    color: "#5C6B68",
    fontWeight: "500",
  },
  timePickers: {
    flexDirection: "row",
    marginLeft: 100,
  },
  timePickerGroup: {
    flexDirection: "row",
    alignItems: "center",
  },
  timeLabel: {
    fontSize: 14,
    color: "#5C6B68",
    fontWeight: "500",
  },
  reviewRequestToggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  reviewRequestToggleLeft: {
    flexDirection: "row",
    alignItems: "center",
  },
  reviewRequestStatusPill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginLeft: 10,
  },
  reviewRequestStatusPillActive: {
    backgroundColor: "#E3F2EF",
  },
  reviewRequestStatusPillOff: {
    backgroundColor: "#EDF1F0",
  },
  reviewRequestStatusPillTextActive: {
    color: "#1F8A5B",
    fontSize: 11,
    fontWeight: "700",
  },
  reviewRequestStatusPillTextOff: {
    color: "#8A9794",
    fontSize: 11,
    fontWeight: "700",
  },
  reviewRequestWarning: {
    color: "#C4453D",
    fontSize: 12,
    marginBottom: 12,
  },
  reviewRequestFieldError: {
    color: "#C4453D",
    fontSize: 12,
    marginTop: -8,
    marginBottom: 12,
  },
  reviewRequestUrlRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  reviewRequestTestButton: {
    backgroundColor: "#8A9794",
    paddingHorizontal: 16,
    height: 44,
    borderRadius: 6,
    justifyContent: "center",
    alignItems: "center",
    marginLeft: 8,
    marginBottom: 12,
  },
  reviewRequestLockedRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F4F6F6",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    marginTop: 8,
  },
  reviewRequestLockedText: {
    color: "#5C6B68",
    fontSize: 13,
    marginLeft: 6,
    flex: 1,
  },
});

export default Settings;

// ============================================================================
// CALL MESSAGES — road-call bridge dialer's two settings-configured voice
// messages. Ported from Warpspeed's GreetingsCard/GreetingEditor pattern
// (PhoneSettingsComponent.jsx) into the local Settings.js idiom. Defaults
// must stay aligned with functions/roadVoice.js's DEFAULT_CONNECT_GREETING /
// DEFAULT_NO_CALLBACK_MESSAGE — this is what's shown pre-fill so what's
// displayed here is what plays on an unconfigured message.
// ============================================================================

const CALL_MESSAGES = [
  {
    key: "connectGreeting",
    title: "Connect Greeting",
    subtitle: "Played to an allowed caller right before their call rings your phone.",
    defaultText: "Thanks for calling — connecting you now, one moment.",
  },
  {
    key: "noCallbackMessage",
    title: "No-Callback Message",
    subtitle:
      "Played to callers who haven't been allowed through — explains why the call won't ring in. The call ends after it plays.",
    defaultText:
      "You've reached Bonita Bikes. This number can't take incoming calls — text us at this number and we'll get right back to you.",
  },
];

const CallMessageEditor = ({ messageKey, title, subtitle, defaultText }) => {
  const [message, setMessage] = useState(null); // { type, text, audioURL, audioPath } once loaded
  const [draftText, setDraftText] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settingsPath = getSettingsPath();
        const settingsRef = doc(db, settingsPath, "roadVoiceMessages");
        const snap = await getDoc(settingsRef);
        const data = snap.exists() ? snap.data() || {} : {};
        const loaded = data[messageKey] || { type: "text", text: "", audioURL: "", audioPath: "" };
        if (!cancelled) setMessage(loaded);
      } catch (error) {
        console.error(`❌ Error loading call message "${messageKey}":`, error);
        if (!cancelled) setMessage({ type: "text", text: "", audioURL: "", audioPath: "" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [messageKey]);

  const saveField = async (partial) => {
    try {
      const settingsPath = getSettingsPath();
      const settingsRef = doc(db, settingsPath, "roadVoiceMessages");
      const next = { ...(message || {}), ...partial };
      setMessage(next);
      await setDoc(settingsRef, { [messageKey]: next, updatedAt: Timestamp.now() }, { merge: true });
    } catch (error) {
      console.error(`❌ Error saving call message "${messageKey}":`, error);
      alert("Error: " + `Failed to save message: ${error.message || "Unknown error"}`);
    }
  };

  if (!message) {
    return (
      <View style={styles.subSection}>
        <Text style={styles.subsectionTitle}>{title}</Text>
        <ActivityIndicator color="#0E7C6B" />
      </View>
    );
  }

  const type = message.type || "text";
  const savedText = message.text || "";
  const displayText = draftText !== null ? draftText : savedText;
  const isDirty = draftText !== null && draftText !== savedText;

  const handleSaveText = () => {
    if (!isDirty) return;
    saveField({ text: draftText });
    setDraftText(null);
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const tenantId = getTenantId();
    const storeId = getStoreId();
    if (!tenantId || !storeId) {
      alert("Error: " + "Missing tenant/store context for audio upload");
      event.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fileName = `voice-messages/${tenantId}_${storeId}_${messageKey}_${Date.now()}_${file.name}`;
      const downloadURL = await uploadFile(fileName, file, { contentType: file.type });
      await saveField({ audioURL: downloadURL, audioPath: fileName });
    } catch (error) {
      console.error(`❌ Error uploading call message audio "${messageKey}":`, error);
      alert("Error: " + `Failed to upload audio: ${error.message || "Unknown error"}`);
    }
    setUploading(false);
    event.target.value = "";
  };

  const handleClearAudio = () => {
    saveField({ audioURL: "", audioPath: "" });
  };

  return (
    <View style={styles.subSection}>
      <View style={styles.hoursHeader}>
        <Text style={styles.subsectionTitle}>{title}</Text>
        {type === "text" && (
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.button, styles.saveButton, !isDirty && { opacity: 0.4 }]}
            onPress={handleSaveText}
            disabled={!isDirty}
          >
            <Text style={styles.buttonText}>Save</Text>
          </TouchableOpacity>
        )}
      </View>
      <Text style={styles.settingDescription}>{subtitle}</Text>

      <View style={{ flexDirection: "row", gap: 16, marginTop: 8, marginBottom: 8 }}>
        <TouchableOpacity
          style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
          onPress={() => saveField({ type: "text" })}
        >
          <View style={[styles.checkbox, type === "text" && styles.checkboxChecked]}>
            {type === "text" && <Text style={styles.checkmark}>✓</Text>}
          </View>
          <Text>Text (read aloud)</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
          onPress={() => saveField({ type: "audio" })}
        >
          <View style={[styles.checkbox, type === "audio" && styles.checkboxChecked]}>
            {type === "audio" && <Text style={styles.checkmark}>✓</Text>}
          </View>
          <Text>Audio file</Text>
        </TouchableOpacity>
      </View>

      {type === "text" && (
        <TextInput
          style={[styles.input, styles.textArea]}
          multiline={true}
          numberOfLines={4}
          value={displayText}
          placeholder={defaultText}
          onChangeText={(val) => setDraftText(val)}
        />
      )}

      {type === "audio" && (
        <View>
          <Text style={styles.currentSetting}>
            {uploading
              ? "Uploading…"
              : message.audioURL
              ? message.audioPath || message.audioURL
              : "No audio uploaded yet"}
          </Text>
          <View style={{ flexDirection: "row", gap: 8, marginTop: 8 }}>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.button, styles.saveButton]}
              onPress={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Text style={styles.buttonText}>{message.audioURL ? "Replace" : "Upload"}</Text>
            </TouchableOpacity>
            {message.audioURL && (
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.button, { backgroundColor: "#C0392B" }]}
                onPress={handleClearAudio}
                disabled={uploading}
              >
                <Text style={styles.buttonText}>Remove</Text>
              </TouchableOpacity>
            )}
          </View>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/mpeg,audio/wav,audio/mp3,audio/x-wav"
            style={{ display: "none" }}
            onChange={handleFile}
          />
          {message.audioURL && (
            <audio controls src={message.audioURL} preload="none" style={{ marginTop: 8, width: "100%" }} />
          )}
        </View>
      )}
    </View>
  );
};
